import type { Uebersetzung } from './de'

/**
 * Englisch — ab Werk ausgeliefert (ADR 010, L11★).
 *
 * **Die Typangabe ist die Vollständigkeitsprüfung.** `Uebersetzung` ist der
 * Schlüsselvorrat aus `de.ts`; fehlt hier ein Schlüssel, übersetzt `tsc` das
 * nicht, sondern bricht ab. Das ist wichtiger, als es aussieht: Eine
 * Sprachdatei, die still auf Deutsch zurückfällt, sieht im Betrieb aus wie
 * eine fertige Übersetzung — und ein Auslandsanwender meldet den einzelnen
 * deutschen Satz nicht, er gewöhnt sich daran.
 *
 * Fachbegriffe folgen der englischen Fachsprache des Konzernrechnungswesens,
 * nicht einer Wort-für-Wort-Übertragung: „Gesellschaft" ist *entity*, nicht
 * *company* — das englische Wort deckt den Teilkonzern mit ab, den das deutsche
 * seit dem 2026-09-17 nur noch mit seinem eigenen Band trägt. „Positionsplan"
 * ist eine *reporting structure*, nicht ein *position plan*, und „Rechnungslegung"
 * ein *accounting framework*, nicht ein *accounting standard*: Der Rahmen ist
 * HGB oder IFRS, der Standard ist IAS 7.
 */
export const en: Uebersetzung = {
  'sprache.name': 'English',

  /*
    en-GB, not en-US: The group reports in euro and writes dates the European
    way. What matters here is the digit grouping — 13,514,000.00 instead of
    13.514.000,00.
  */
  'sprache.gebietsschema': 'en-GB',

  /* The window for a mask — the same at every place (R710) */
  'fenster.schliessen': 'Close',
  'fenster.griff': 'Drag to move the window, double-click to enlarge and restore it',
  'fenster.bildschirmfuellend': 'Fill the screen',
  'fenster.alsfenster': 'Show as a window',

  /* What succeeded — the box at the top right (7.2 section 34, R1130–R1132). */
  'erfolg.aufforderung.zeigen': 'Show data imports of this period',
  'erfolg.datenimport.gespeichert': 'Data import saved successfully',
  'erfolg.konto.eingefuegt': 'Account pasted successfully',
  'erfolg.position.eingefuegt': 'Position pasted successfully',
  'erfolg.kontogruppe.gesetzt': 'Account group changed successfully',
  'erfolg.datenimporte.gespeichert': 'Data imports saved successfully',
  'erfolg.datenimporte.aktualisiert': 'Data imports updated successfully',
  'erfolg.datenimport.geloescht': 'Data import deleted successfully',
  'erfolg.datenimport.verschoben': 'Data import moved successfully',
  'erfolg.datenimporte.verschoben': 'Data imports moved successfully',
  'erfolg.package.gespeichert': 'Package saved successfully',
  'erfolg.konzernabschluss.angelegt': 'Group statement created successfully',
  'erfolg.konzernabschluss.gerechnet': 'Group statement calculated and saved successfully',
  'erfolg.handbuch.gespeichert': 'Edition saved successfully',
  'erfolg.handbuch.entfernt': 'Edition removed successfully',
  'erfolg.schritt.angelegt': 'Step created successfully',
  'erfolg.schritt.entfernt': 'Step removed successfully',
  'erfolg.prozess.gespeichert': 'Process saved successfully',
  'erfolg.spaltenzuordnung.gespeichert': 'Column mapping saved successfully',
  'erfolg.stammdaten.angelegt': 'Entry created successfully',
  'erfolg.stammdaten.entfernt': 'Entry removed successfully',
  'erfolg.stammdaten.stillgelegt': 'Entry retired successfully',
  'erfolg.stammdaten.uebernommen': 'Master data taken over successfully',
  'erfolg.tafel.kopiert': 'Copied successfully',
  'erfolg.tafel.eingefuegt': 'Pasted and saved successfully',
  'erfolg.bewegungsart.angelegt': 'Movement type created successfully',
  'erfolg.wert.angelegt': 'Value created successfully',
  'erfolg.beleg.gebucht': 'Document posted successfully',
  'erfolg.beleg.berichtigt': 'Document corrected successfully',
  'erfolg.beleg.aufgeloest': 'Document released successfully',
  'erfolg.beleg.imjournal': 'The report shows it with the next run; the journal already has it.',
  'erfolg.packagevorlage.gespeichert': 'Package template saved successfully',
  'erfolg.packagevorlage.heruntergeladen': 'Package template downloaded successfully',
  'erfolg.zeitraum.gespeichert': 'Span saved successfully',
  'erfolg.kontenzuordnung.stillgelegt': 'Mapping retired successfully',
  'erfolg.kontenzuordnung.inkraft': 'Mapping reinstated',
  'erfolg.kontenzuordnung.berichtigt': 'Mapping corrected',
  'erfolg.konto.angelegt': 'Account created successfully',
  'erfolg.position.angelegt': 'Position created successfully',
  'erfolg.kfrzeile.angelegt': 'Line created successfully',

  /* --- The rail --- */
  'leiste.bezeichnung': 'Areas',
  /* The four blocks of the sidebar (7.2 section 3a, R324). */
  'leiste.gruppe.abschluss': 'The close',
  'leiste.gruppe.auswerten': 'Review and audit',
  'leiste.gruppe.einrichten': 'Setting up and maintaining',
  'leiste.gruppe.verwaltung': 'Administration',
  'leiste.einklappen': 'Collapse',
  'leiste.ausklappen': 'Expand',
  'leiste.einklappen.hilfe': 'Collapse the area rail',
  'leiste.ausklappen.hilfe': 'Expand the area rail',
  'leiste.geplant': 'planned',
  'leiste.menue.fenster': 'Open {name} in a new window',
  'leiste.menue.tab': 'Open {name} in a new tab',
  'leiste.menue.adresse': 'Copy address',
  'leiste.menue.bezeichnung': 'What to do with this entry',
  'marke.zusatz': 'Consolidation',

  /* --- 1 --- */
  'bereich.arbeitsliste.name': 'Work list',
  'bereich.arbeitsliste.zweck': 'What the current close still asks of you.',

  /* --- 2 --- */
  'bereich.abschluss.name': 'Close',
  'bereich.abschluss.zweck': 'Run, check and release the current close.',
  'bereich.abschluss.monitor': 'Group close monitor',
  'bereich.abschluss.konzernhandbuch': 'Group manual',
  'bereich.abschluss.schritte': 'Consolidation steps',
  'bereich.abschluss.ic-abstimmung': 'Intercompany reconciliation',

  /* --- 3 --- */
  'bereich.berichte.name': 'Reports',
  'bereich.berichte.zweck':
    'Statutory report with a fixed structure and ad-hoc analysis — kept apart.',
  'bereich.berichte.bilanz': 'Consolidated balance sheet',
  'bereich.berichte.guv': 'Consolidated income statement',
  'bereich.berichte.kapitalflussrechnung': 'Statement of cash flows (DRS 21)',
  'bereich.berichte.spiegel': 'Fixed asset schedule',
  'bereich.berichte.abschlussbericht': 'Statutory report',
  'bereich.berichte.wertsuche': 'Value search',

  /* --- 4 --- */
  'bereich.datenimport.name': 'Data import',
  'bereich.datenimport.zweck': 'Accept submissions, map them and run the clearing.',
  'bereich.datenimport.quellen': 'Data sources',
  'bereich.datenimport.packagevorlagen': 'Package templates',
  'bereich.datenimport.lieferungen': 'Data imports',
  'bereich.datenimport.importmapping': 'Mappings',
  'bereich.datenimport.klaerungsliste': 'Clearing list',
  'bereich.datenimport.ladeprozesse': 'Load processes and schedules',
  'bereich.datenimport.landing': 'Archive of submitted files',

  /* --- 5 --- */
  'bereich.konzernstruktur.name': 'Group structure',
  'bereich.konzernstruktur.zweck': 'Who belongs to whom, since when and in which group.',
  'bereich.konzernstruktur.struktur': 'Structure chart',
  'bereich.konzernstruktur.beteiligungsregister': 'Shareholding register',
  'bereich.konzernstruktur.kreise': 'Consolidation groups',
  'bereich.konzernstruktur.einbeziehung': 'Inclusion dates',
  'bereich.konzernstruktur.ueberleitung': 'Sub-group to group reconciliation',

  /* --- 6 --- */
  'bereich.stammdaten.name': 'Master data',
  'bereich.stammdaten.zweck': 'The maintained stock every close is computed from.',
  'bereich.stammdaten.einheiten': 'Entities',
  'bereich.stammdaten.kontenrahmen': 'Chart of accounts',
  'bereich.stammdaten.positionsplaene': 'Reporting structures',
  'bereich.stammdaten.ledger': 'Measurement layers',
  'bereich.stammdaten.bewegungsarten': 'Movement types',
  'bereich.stammdaten.analysedimensionen': 'Optional dimensions',
  'bereich.stammdaten.partnercodes': 'Partner codes',
  'partnercode.titel': 'Partner code structure',
  'partnercode.neu': 'Create partner code',
  'partnercode.neuegruppe': 'Create group',
  'partnercode.leer': 'There are no partner codes yet.',
  'partnercode.wurzel': 'top level',
  'partnercode.neu.titel': 'New partner code under {eltern}',
  'partnercode.neu.titel.gruppe': 'New group under {eltern}',
  'partnercode.marke.gruppe': 'group',
  'partnercode.marke.einheit': 'company {name}',
  'partnercode.marke.ohneeinheit': 'no group company',
  'partnercode.aussondern': 'Delete',
  'partnercode.aussondern.frage': 'Delete {name}? What has been used is deactivated; what was never used disappears.',
  'partnercode.menue.neu': 'Create partner code',
  'partnercode.menue.neuegruppe': 'Create group',
  'partnercode.menue.hoch': 'Move up',
  'partnercode.menue.runter': 'Move down',
  'partnercode.menue.einruecken': 'Into the group above',
  'partnercode.menue.ausruecken': 'One level up',
  'partnercode.menue.aussondern': 'Delete',
  'bereich.stammdaten.laender': 'Countries',
  'bereich.stammdaten.waehrungen': 'Currencies',
  'bereich.stammdaten.kurse': 'Exchange rates',
  'bereich.stammdaten.historische-kurse': 'Historical rates',
  'bereich.stammdaten.abstimmkreise': 'Reconciliation groups',

  /* --- 7 --- */

  /* --- 12 Periods (2.19) --- */
  'bereich.perioden.name': 'Periods',
  'bereich.perioden.zweck': 'The span of time in which this installation keeps data.',
  'bereich.perioden.zeitachse': 'Time axis',

  /* --- 8 --- */
  'bereich.nachweis.name': 'Audit trail',
  'bereich.nachweis.zweck': 'What was computed, with what — and that nothing was altered since.',
  'bereich.nachweis.buchungsjournal': 'Posting journal',
  'bereich.nachweis.aenderungsnachweis': 'Change log',
  'bereich.nachweis.laeufe': 'Runs with check code',
  'bereich.nachweis.prueferansicht': 'Auditor view',
  'bereich.nachweis.uebergangene': 'Overridden checks',

  /* --- 9 --- */
  'bereich.anwender.name': 'Users and permissions',
  'bereich.anwender.zweck': 'Who may do what — by entity, measurement layer and period.',
  'bereich.anwender.liste': 'Users',
  'bereich.anwender.rollen': 'Roles',
  'bereich.anwender.rechte': 'Permissions',
  'bereich.anwender.vieraugen': 'Dual-control criteria',

  /* --- 10 --- */
  /* --- 10 Notes --- */
  'bereich.anhang.name': 'Notes',
  'bereich.anhang.zweck':
    'The sixth component of the statements — sentences with figures in them, every figure from the run.',
  'bereich.anhang.anhangsbuch': 'Notes book',
  'bereich.anhang.zulieferungen': 'Submissions',
  'bereich.anhang.anteilsbesitz': 'Shareholdings',
  'bereich.anhang.angabenkatalog': 'Disclosure catalogue',
  'bereich.anhang.vollstaendigkeit': 'Completeness',
  'bereich.anhang.ausgabe': 'Output',

  /* --- The notes book --- */
  'anhang.gliederung': 'Outline',
  'anhang.angabe': 'The disclosure',
  'anhang.herkunft': 'Where the figures come from',
  'anhang.offen': '{offen} of {alle} open',
  'anhang.blockieren': '{zahl} block release',
  'anhang.blockiert.keine': 'Nothing blocks release.',
  'anhang.gruppe.allgemeines': 'General',
  'anhang.gruppe.methoden': 'Accounting policies',
  'anhang.gruppe.bilanz': 'Balance sheet',
  'anhang.gruppe.guv': 'Income statement',
  'anhang.gruppe.anteilsbesitz': 'Shareholdings',
  'anhang.gruppe.sonstiges': 'Other disclosures',
  'anhang.herkunft.gerechnet': 'computed',
  'anhang.herkunft.erhoben': 'collected',
  'anhang.herkunft.erklaert': 'explained',
  'anhang.art.pflicht': 'mandatory',
  'anhang.art.wahlrecht': 'optional',
  'anhang.art.freiwillig': 'voluntary',
  'anhang.zustand.fehlt': 'missing',
  'anhang.zustand.offen': 'open',
  'anhang.zustand.blockiert': 'blocked',
  'anhang.zustand.veraltet': 'out of date',
  'anhang.zustand.fertig': 'done',
  'anhang.fehlanzeige': 'Nil return',
  'anhang.fehlanzeige.erklaerung':
    'Expressly established as not applicable. Counts as completed.',
  'anhang.leer.titel': 'No text has been written for this disclosure yet.',
  'anhang.leer.satz':
    'Empty looks like forgotten. A mandatory disclosure that does not apply is expressly set to a nil return — with a sentence saying so.',
  'anhang.keineangabe': 'Select a disclosure on the left.',
  'anhang.keinlauf':
    'No consolidation has been run for this period yet, so the anchors cannot show their amounts.',
  'anhang.anker.nichtaufloesbar': 'does not resolve',
  'anhang.anker.vorjahr': 'Prior year',
  'anhang.anker.nachweis': 'Audit trail',
  'anhang.anker.melder': 'Reported by',
  'anhang.anker.auszulieferung': 'from submission',
  'anhang.anker.kfr': 'from the cash flow statement — offset within investing activities',
  'anhang.verantwortlich': 'Responsible',
  'anhang.frist': 'Due',
  'anhang.pruefcode': 'Notes check code',
  'anhang.pruefcode.erklaerung':
    'Over text, anchors and catalogue edition — not the check code of the result. The released statements name both.',
  'anhang.katalogausgabe': 'Catalogue edition',
  'anhang.katalog.fundstelle': 'Legal reference',
  'anhang.katalog.bezeichnung': 'Description',
  'anhang.katalog.art': 'Type',
  'anhang.katalog.herkunft': 'Origin',
  'anhang.katalog.gruppe': 'Outline',
  'anhang.katalog.schutzklausel': 'Protective clause under section 286 HGB applicable',
  'anhang.vollstaendigkeit.hart': 'Blocks release',
  'anhang.vollstaendigkeit.weich': 'Can be overridden with a reason',
  'anhang.vollstaendigkeit.leer': 'No open items.',

  /* --- 11 Operations --- */
  'bereich.betrieb.name': 'Operations',
  'bereich.betrieb.zweck': 'How the installation is running — and what it cannot do yet.',
  'bereich.betrieb.systemzustand': 'System status',
  'bereich.betrieb.antwortzeiten': 'Response times',
  'bereich.betrieb.sprachen': 'Languages and house terminology',
  'bereich.betrieb.sicherung': 'Backup',
  'bereich.betrieb.lizenz': 'Licence',
  'bereich.betrieb.kundenwuensche': 'Unfulfilled customer requests',

  /* --- The honest empty screen --- */
  'platzhalter.titel': 'This screen has not been built yet.',
  'platzhalter.herkunft': 'It is decided in {herkunft}.',
  'platzhalter.stand': 'This screen is not built yet.',
  'platzhalter.stand.verb.eins': 'is',
  'platzhalter.stand.verb.mehrere': 'are',
  'platzhalter.fertig': 'Already built:',
  'platzhalter.vorschau': 'What will live here',

  /* --- Languages and house terminology --- */
  'sprachen.gewaehlt': 'Shipped languages',
  'sprachen.beschriftungen.titel': 'House terminology',
  'sprachen.beschriftungen.stand': 'Maintaining the house terms on screen is not built yet.',

  /* --- The application --- */
  'anwendung.titel': 'CoKo — Consolidated financial statements',

  /* --- The quick switch in the header --- */
  'kopf.sprache': 'Language',
  'kopf.zahlen': 'Numbers',
  'kopf.vorzeichen': 'Amounts',
  'vorzeichen.plusminus': 'Plus and minus',
  'vorzeichen.sollhaben': 'Debit and credit',
  'kopf.minusfarbe': 'Negative amounts',
  'zellsumme.summe': 'Sum',
  'zwischensumme.kopf': 'Total',
  'zellsumme.anzahl': '{anzahl} values',
  'minusfarbe.rot': 'red',
  'minusfarbe.schwarz': 'black',

  /* --- Number format --- */
  'zahlenformat.titel': 'Number format',
  'zahlenformat.sprache': 'follow the language',

  /* --- The context bar --- */
  'kontext.kreis': 'Consolidation group',
  'kontext.element': 'Reporting object',
  'kontext.welt.konzernabschluss': 'Group close',
  'kontext.welt.lieferdaten': 'Delivered data',
  'welt.standjetzt': 'as of now',
  'welt.gerechnet': 'calculated on {zeitpunkt}',
  'welt.ohnelauf': 'No group close has been calculated for {monate}.',
  'welt.daten': 'Data has arrived since the takeover ({monate}).',
  'welt.stammdaten': 'Master data has changed since the takeover ({monate}).',
  'welt.beides': 'Data has arrived and master data has changed since the takeover ({monate}).',
  'welt.weg.rechnen': 'Recalculate {name}',
  'welt.weg.oeffnen': 'Open and calculate {name}',
  'welt.weg.anlegen': 'Create a group close',
  'kontext.element.offen':
    'Not yet added: {was}. Add them under Master data → Reporting objects.',
  'kontext.element.offen.gesellschaften.eins': 'one company',
  'kontext.element.offen.gesellschaften.mehrere': '{anzahl} companies',
  'kontext.element.offen.kreise.eins': 'one consolidation group',
  'kontext.element.offen.kreise.mehrere': '{anzahl} consolidation groups',
  'kontext.element.offen.und': ' and ',
  'kontext.kreis.rueckfrage': 'within group',
  'kontext.ledger': 'Measurement layer',
  'kontext.periode': 'Period',
  'kontext.periode.keine': 'no period selected',
  'periode.anfang': 'the start',
  'kontext.zeitraum': 'Time range',
  'kontext.raster': 'Granularity',
  'zeitraum.von': 'Start of the range',
  'zeitraum.bis': 'End of the range',
  'zeitraum.ohnelauf': 'no computed run',
  'zeitraum.neuere': 'Figures up to {bis}',
  'zeitraum.streifen.hilfe':
    'Click selects {abschnitt}, dragging spans a range, shift-click extends it. Arrow keys move the range, shift and arrow stretch it.',
  'zeitraum.abschnitt.monat': 'a month',
  'zeitraum.abschnitt.quartal': 'a quarter',
  'zeitraum.abschnitt.jahr': 'a year',
  'zeitraum.keinlauf.kurz': 'no run',
  'zeitraum.ueberblick.hilfe':
    'The whole time axis. Click or drag to move the section below; the selection is made in the strip.',
  'zeitraum.quartal': 'Q{quartal} {jahr}',
  'kontext.rechnet': 'Running …',
  'kontext.kreis.rueckfall': 'Group',

  /* --- The five states --- */
  'zustand.fehlt': 'missing',
  'zustand.offen': 'open',
  'zustand.blockiert': 'blocked',
  'zustand.veraltet': 'out of date',
  'zustand.fertig': 'done',

  /* --- The states of a close --- */
  'status.offen': 'open',
  'status.in_arbeit': 'in progress',
  'status.freigegeben': 'released',
  'status.testiert': 'audited',

  /* --- Work list --- */
  'arbeitsliste.kopf.leer': 'no action needed. Everything that can be checked has been checked.',
  'arbeitsliste.kopf.eins': '{anzahl} action needed. Sorted by severity of the finding.',
  'arbeitsliste.kopf.mehrere': '{anzahl} actions needed. Sorted by severity of the finding.',
  'ueberwachung.aufforderung': 'Choose what you monitor.',
  'ueberwachung.aufforderung.weg':
    'Under [[Set up monitoring]], choose groups, folders or companies and a period. Then this page shows, per company, what needs doing.',
  'ueberwachung.festlegen': 'Set up monitoring',
  'ueberwachung.festlegen.erlaeuterung':
    'Choose from the report selection tree what the worklist monitors. A group brings all its companies, a folder those that sit under it during the period — including ones added later.',
  'ueberwachung.festlegen.von': 'From',
  'ueberwachung.festlegen.bis': 'To',
  'ueberwachung.festlegen.bestandteil': 'Component',
  'ueberwachung.festlegen.speichern': 'Set',
  'ueberwachung.festlegen.abbrechen': 'Cancel',
  'ueberwachung.art.kreis': 'Group',
  'ueberwachung.art.ordner': 'Folder',
  'ueberwachung.art.gesellschaft': 'Company',
  'ueberwachung.befund.zeitraum': 'Enter a period from and to.',
  'ueberwachung.befund.reihenfolge': 'The period ends before it starts. Swap from and to.',
  'ueberwachung.befund.leer': 'Choose at least one group, folder or company.',
  'ueberwachung.kopf': 'Monitoring: {bestandteile} · {von} to {bis}.',
  'ueberwachung.nochnichts': 'No month of the chosen period has ended yet; checking starts with the first one that has.',
  'ueberwachung.blatt.kopf': '{von} to {bis}, checked up to {geprueft}',
  'ueberwachung.blatt.ohnebefund': '{ohne} of {anzahl} companies without findings.',
  'ueberwachung.blatt.ohnebefund.eins': '{ohne} of {anzahl} company without findings.',
  'ueberwachung.konzerne': 'Groups',
  'ueberwachung.gesellschaften': 'Companies',
  'ueberwachung.gruppe.Lieferung': 'Delivery',
  'ueberwachung.gruppe.Spiegel': 'Schedules',
  'ueberwachung.gruppe.Konzernintern': 'Intercompany',
  'ueberwachung.gruppe.KapitalUndBeteiligungen': 'Capital and investments',
  'ueberwachung.gruppe.Kapitalflussrechnung': 'Cash flow statement',
  'ueberwachung.gruppe.Kurse': 'Rates',
  'ueberwachung.gruppe.Anhang': 'Notes',
  'ueberwachung.gruppe.Konsolidierungsprozess': 'Consolidation process',
  'ueberwachung.liste.schliessen': 'Close',
  'ueberwachung.liste.leer': 'Nothing to do here.',
  'ueberwachung.ausgefallen': 'The monitoring could not be read just now. Reload the page in a moment.',
  'arbeitsliste.ic-differenz':
    'Intercompany difference not settled: {gesellschaft} against {partner} — {gegenstand} differ by {differenz} {waehrung}.',
  'ic-differenz.gegenstand.schuldenkonsolidierung': 'Receivables and payables',
  'ic-differenz.gegenstand.aufwandertragkonsolidierung': 'Expenses and income cumulated since the start of the year',
  'ic-differenz.gegenstand.dividendenkonsolidierung': 'Investment income and distribution',
  'arbeitsliste.ic-differenz.dividende':
    'Intercompany difference not settled: {gesellschaft} against {partner} — cumulated since the start of the year investment income {ertrag} {waehrung}, distribution {ausschuettung} {waehrung} times equity interest {quote}, difference {differenz} {waehrung}.',
  'arbeitsliste.beteiligung-ohne-partner':
    'Investment without the company held: {konto} at {einheit} in {periode} with {betrag} — capital consolidation is skipped for this amount.',
  'arbeitsliste.keinedaten': 'No data exists for {periode}.',
  'arbeitsliste.keinedaten.ohne': 'No data exists for the selected period.',
  'arbeitsliste.veraltet.regelwerk': 'A new edition of the group manual now applies to “{abschluss}”.',
  'arbeitsliste.bilanz': 'The balance sheet does not balance: assets and liabilities do not agree.',
  'arbeitsliste.antwortzeit.verletzt':
    'The response time commitment of {budget} ms is broken: {ueber} of {anzahl} calls exceed it.',
  'arbeitsliste.weg.freigeben': 'Release it in the [[Group close monitor]].',
  'arbeitsliste.weg.bilanz':
    'Open the [[Consolidated balance sheet]] and trace where the difference comes from. Once it balances, this message disappears.',
  'arbeitsliste.weg.kette':
    'Notify operations: the chain of entries is broken. The [[Change log]] shows where.',
  'arbeitsliste.weg.antwortzeiten': 'The individual calls are listed under [[Response times]].',
  'arbeitsliste.weg.keinedaten':
    'Import the companies’ deliveries under [[Data imports]]. As soon as data is there, CoKo calculates the group statement, and this message disappears.',
  'arbeitsliste.weg.neurechnen':
    'Go to [[Group statements]] and recalculate the group statement. This message then disappears.',
  'arbeitsliste.weg.status': 'Every later change is recorded in the [[Change log]].',
  'arbeitsliste.weg.schritte': 'The settings of the process are at the group statement under [[Group statements]].',
  'arbeitsliste.freigabe': '“{abschluss}” can be released for {periode}.',
  'arbeitsliste.status': '“{abschluss}” is {status} for {periode}.',
  'arbeitsliste.schwere.blockiert': 'Blocks the release',
  'arbeitsliste.schwere.veraltet': 'Outdated',
  'arbeitsliste.schwere.offen': 'Notice',
  'arbeitsliste.schwere.fehlt': 'Missing',
  'arbeitsliste.schwere.fertig': 'Done',
  'arbeitsliste.historisch.menge': '{anzahl} movements',
  'arbeitsliste.historisch.menge.eins': 'one movement',
  'arbeitsliste.historisch.was':
    'Historical rates are missing for {bewegungen} of {gesellschaft} in {periode}. Until the table matches the delivery, the group statement does not calculate.',
  'arbeitsliste.historisch.weg.klick':
    'Click “Create”: the lines are created at the proposed rate, and this message disappears. They are listed one by one under [[Historical rates]].',
  'arbeitsliste.historisch.weg.eintragen':
    'Under [[Historical rates]], enter the rate where there is no proposal, or align the lines with the delivery. Then this message disappears.',
  'arbeitsliste.historisch.anlegen': 'Create',
  'arbeitsliste.inaktiv.was': '{gesellschaft} is inactive since {seit} but still in scope {kreis} without an end.',
  'arbeitsliste.inaktiv.weg':
    'Under [[Consolidation groups]], end the membership as of {seit}. Until then, the group statement calculates with what was delivered.',

  /* --- Group close monitor (R1607) --- */
  'monitor.hinweis':
    'Choose group, measurement layer and period above. The run appears by itself as soon as data is there.',
  'monitor.lauf': 'Computed',
  'monitor.waehrung': 'Group currency',
  'monitor.regelwerk': 'Rule set',
  'monitor.ausgabe': 'Version {nummer}',
  'monitor.pruefcode': 'Check code of the result',
  'monitor.hinweise': 'Notes from the run',

  /* --- Release --- */
  'freigabe.moeglich': 'The close can be released.',
  'freigabe.gesperrt': 'Release is blocked.',
  'freigabe.zustand': 'The close is {status}.',
  'freigabe.zeitraum': 'Checked {von} to {bis}.',
  'freigabe.knopf': 'Release the close',
  'freigabe.laeuft': 'Releasing …',

  /* --- Report --- */
  'gliederung.wahl': 'Presentation',
  'spalte.position': 'Line item',
  'spalte.hb2': 'Adjusted local GAAP',
  'spalte.kapko': 'Equity',
  'spalte.schuko': 'Debt',
  'spalte.aeko': 'Income/expense',
  'spalte.zwerge_uv': 'Unrealised profit (current)',
  'spalte.zwerge_av': 'Unrealised profit (non-current)',
  'spalte.geliefert': 'Delivered',
  'spalte.konzern': 'Group',
  'bericht.aufklappen': 'Expand or collapse this line',
  'bericht.menue.einheiten': 'Break down by entity',
  'bericht.menue.konten': 'Break down by account',
  'bericht.menue.alle.einheiten': 'Expand everything by entity',
  'bericht.menue.alle.konten': 'Expand everything by account',
  'bericht.menue.zuklappen': 'Collapse everything',
  /* Die drei Ansichtsgruppen (7.3 Abschnitt 3, R576). */
  'bericht.menue.alles': 'Breakdown across the report',
  'bericht.menue.gliederung': 'Outline levels',
  'bericht.menue.buchen': 'Post …',

  /* --- The outline folds up (7.3 section 13, R444-R445) --- */
  'bericht.gliederung.klappen': 'Expand or collapse this outline item',
  'bericht.menue.gliederung.ebene': 'Show outline down to level {ebene}',
  'bericht.menue.gliederung.ganz': 'Show the whole outline',

  /* --- The breakdown as a path --- */
  'aufriss.titel': 'Break down by',
  'aufriss.konten': 'Accounts',
  'aufriss.einheiten': 'Entities',
  'suche.oeffnen': 'Search this report (Ctrl+F)',
  'suche.titel': 'Search this report',
  'suche.platzhalter': 'Label or amount',
  'suche.leer': 'no match',
  'suche.zaehler': '{nummer} of {anzahl}',
  'suche.gruppen': '{texte} in labels · {werte} in amounts',
  'suche.umgekehrt': 'opposite sign',
  'suche.weiter': 'Next match',
  'suche.zurueck': 'Previous match',
  'suche.schliessen': 'Close search',

  'auswahl.leer': 'No entry matches “{wort}”',

  'auswahl.bitte': 'Please choose',
  'kontofeld.ohneposition': 'No position — appears in no report',
  'kontofeld.guv': 'Income statement',
  // The statistical accounts under their position (2.1 section 3.6, R1733).
  'kontofeld.kfr': 'Cash flow statement',
  'kontofeld.laedt': 'Loading the structure …',
  'kontofeld.gliederung': 'Structure',
  'kontofeld.keinegliederung': 'No structure has been set up — the accounts are listed without a tree.',
  // Creating from the list (7.2 section 47, R1493, R1497).
  'kontofeld.anlegen': 'Create …',
  'kontofeld.ohnekonto': 'no account yet',

  'bereich.berichte.eigenkapitalspiegel': 'Statement of changes in equity',
  'bereich.berichte.kreditspiegel': 'Movement in bank loans',
  'spiegel.spalte.eroeffnung': 'Opening balance',
  'spiegel.vortrag.anfangsbestand': 'Opening balance',
  'spiegel.vortrag.anfangsbestand_abschreibung': 'Accumulated depreciation, opening',
  'spiegel.vortrag.ergebnisvortrag': 'Result of prior months',
  'quelle.spiegel.anfangsbestand': 'Opening balance (check only)',
  'spiegel.spalte.zugang': 'Additions',
  'spiegel.spalte.abgang': 'Disposals',
  'spiegel.spalte.umbuchung': 'Reclassifications',
  'spiegel.spalte.kreis': 'Change in scope',
  'spiegel.spalte.waehrung': 'Currency effect',
  'spiegel.spalte.abschreibung': 'Depreciation for the year',
  'spiegel.spalte.zuschreibung': 'Write-ups',
  'spiegel.spalte.kapitalerhoehung': 'Capital increase',
  'spiegel.spalte.ausschuettung': 'Distribution',
  'spiegel.spalte.jahresergebnis': 'Result for the year',
  'spiegel.spalte.vortrag': 'Result carried forward',
  'spiegel.spalte.aufnahme': 'Drawdown',
  'spiegel.spalte.tilgung': 'Repayment',
  'spiegel.spalte.endbestand': 'Closing balance',
  'spiegel.spalte.default': 'Default — no movement type',
  'spiegel.block.bestand': 'Cost of acquisition or production',
  'spiegel.block.ahk': 'Cost of acquisition or production',
  'spiegel.block.abschreibung': 'Accumulated depreciation',
  'spiegel.block.buchwert': 'Carrying amounts',
  'spiegel.ahk.eroeffnung': 'Opening balance',
  'spiegel.ahk.zugang': 'Additions',
  'spiegel.ahk.abgang': 'Disposals',
  'spiegel.ahk.umbuchung': 'Reclassifications',
  'spiegel.ahk.kreis': 'Change in scope',
  'spiegel.ahk.waehrung': 'Currency effect',
  'spiegel.ahk.endbestand': 'Closing balance',
  'spiegel.abschreibung.eroeffnung': 'Opening balance',
  'spiegel.abschreibung.abschreibung': 'Depreciation for the year',
  'spiegel.abschreibung.zuschreibung': 'Write-ups',
  'spiegel.abschreibung.abgang': 'Disposals',
  'spiegel.abschreibung.umbuchung': 'Reclassifications',
  'spiegel.abschreibung.kreis': 'Change in scope',
  'spiegel.abschreibung.waehrung': 'Currency effect',
  'spiegel.abschreibung.endbestand': 'Closing balance',
  'spiegel.buchwert.endbestand': 'Carrying amount at end',
  'spiegel.buchwert.eroeffnung': 'Carrying amount at start',
  'spiegel.leer': 'No calculated month with fixed assets exists for this financial year yet.',
  'aufriss.marker.konten': 'by account',
  'aufriss.marker.einheiten': 'by entity',
  'aufriss.menue.konten': 'Break down by account',
  'aufriss.menue.einheiten': 'Break down by entity',
  'aufriss.menue.herkunft': 'Open value proof',
  'aufriss.menue.zuklappen': 'Collapse this line',
  'aufriss.menue.zeile': 'Breakdown of this line',
  'aufriss.partner': 'Partners',
  'aufriss.guv': 'Income statement',
  'aufriss.segment': 'Segments',
  'aufriss.menue.partner': 'Break down by partner',
  'aufriss.menue.guv': 'Break down by income statement',
  'aufriss.menue.segment': 'Break down by segment',
  'aufriss.menue.dimension': 'Break down by {achse}',
  'aufriss.marker.partner': 'by partner',
  'aufriss.marker.segment': 'by segment',
  'aufriss.marker.dimension': 'by {achse}',
  'dimension.default': 'Default',
  'aufriss.laedt': 'Loading …',
  'aufriss.fehler': 'This breakdown could not be loaded.',
  'aufriss.leer': 'Nothing on this axis here.',
  'bericht.menueknopf': 'Menu for this line',

  /* --- Display add-ons --- */
  /* --- Anzeigewährung (2.12 Abschnitt 3.21, R993–R996) --- */
  'anzeigewaehrung.titel': 'Display currency',
  'nachweis.art.waehrungsdifferenz': 'Currency difference',

  /* --- Anzeigeoptionen --- */
  'anzeige.titel': 'Display options',
  'anzeige.keine': 'Complete',
  'anzeige.mehrere': '{anzahl} selected',
  'anzeige.null.titel': 'Suppress zero rows',
  'anzeige.zwischen.titel': 'Subtotals in the period view',
  'anzeige.zwischen.quartal': 'Quarter',
  'anzeige.zwischen.halbjahr': 'Half-year',
  'anzeige.zwischen.jahr': 'Year',
  'anzeige.zwischen.zeitraum': 'Selected period',
  'anzeige.zwischen.erlaeuterung':
    'One column after the last period it covers. Monthly values and movements are added up; the cash fund at the beginning and at the end is shown as a balance. Balances and year-to-date values already show the balance in the last column — no subtotal is added there.',
  'anzeige.null.positionen': 'Items',
  'anzeige.null.positionen.erlaeuterung':
    'Hide items without an amount. A group stays as long as one item below it carries an amount.',
  'anzeige.null.konten': 'Accounts',
  'anzeige.null.konten.erlaeuterung': 'Hide accounts without an amount in the breakdown.',
  'anzeige.null.einheiten': 'Companies',
  'anzeige.null.einheiten.erlaeuterung':
    'Hide companies without an amount in the breakdown — and their columns in the group view.',
  'anzeige.null.bewegungsarten': 'Movement types',
  'anzeige.null.bewegungsarten.erlaeuterung':
    'Hide columns without an amount in the schedule. Unchecked, every movement type of the category is shown — including a newly created one nobody has posted to yet.',
  'anzeige.null.achse.erlaeuterung': 'Hide values of this axis without an amount in the breakdown.',

  'zeitwert.bestand.titel': 'Balance/Movement',
  'zeitwert.bestand.kumuliert': 'Balance',
  'zeitwert.bestand.kumuliert.erlaeuterung':
    'The closing balance of each column — the balance sheet as it stands on that date.',
  'zeitwert.bestand.einzeln': 'Movement',
  'zeitwert.bestand.einzeln.erlaeuterung':
    'The change against the previous column instead of the balances. Shows a jump at once instead of leaving it to be read off twelve amounts.',
  'zeitwert.strom.titel': 'Period/Year to date',
  'zeitwert.strom.einzeln.monat': 'Monthly figure',
  'zeitwert.strom.einzeln.quartal': 'Quarterly figure',
  'zeitwert.strom.einzeln.jahr': 'Annual figure',
  'zeitwert.strom.einzeln.erlaeuterung':
    'What arose in each column’s own period.',
  'zeitwert.strom.kumuliert': 'Year to date',
  'zeitwert.strom.kumuliert.erlaeuterung':
    'Each column carries everything from the start of the financial year up to its end — “YTD” in controlling. The count restarts with the year.',
  'bewegung.vorbehalt':
    'Additions to the consolidation group are excluded — an entity that joins brings its balance with it without anything having moved. The currency effect, however, is still inside the movement: the movement type is missing from the recorded result.',

  /* --- Column layout --- */
  'spalten.titel': 'Column view',
  'spalten.zeitraum': 'Period',
  'spalten.konsolidierung': 'Consolidation steps',
  'spalten.schichten': 'Valuation layers',
  'spalten.struktur': 'Group structure',
  'spalten.perioden': 'Periods',
  'spalten.raster.monat': 'Month',
  'spalten.raster.quartal': 'Quarter',
  'spalten.raster.jahr': 'Year',
  'spalten.kurz': 'Short group view',
  'spalten.partner': 'Partners',
  'spalten.gesellschaft': 'Entity',
  'spalten.gesellschaft.alle': 'all entities',
  'spalte.summe': 'Total',
  'spalte.saldowechsel': 'Sign reclass',
  'spalte.konsolidierung': 'Consolidation',
  'spalten.perioden.leer': 'No period in the selected range has been computed for this group and measurement layer.',
  'bericht.leer.bilanz': 'The balance sheet contains no posted line items.',
  'bericht.ohnestelle':
    'Not included — no place in the structure:',
  /* --- A month without a delivery (2.12 section 3.20, R973–R978) --- */
  'lieferstand.fortgeschrieben':
    '{gesellschaft} delivered nothing for {periode}. Showing the balance from {aus}.',
  'lieferstand.ohne': '{gesellschaft} delivered nothing for {periode}. Last delivery: {aus}.',
  'lieferstand.nie': '{gesellschaft} has delivered nothing up to {periode}.',
  'lieferstand.nicht-faellig':
    '{gesellschaft}: delivery for {periode} not due ({rhythmus}). Until the first delivery of the financial year it stands at zero.',
  'bericht.leer.guv': 'The income statement contains no posted line items.',
  'bilanz.differenz': 'The balance sheet does not balance. Difference: {betrag} {waehrung}.',

  /* --- Consolidation steps --- */
  'vorgang.laedt': 'Fetching postings …',
  'vorgang.leer': 'This step produced no postings.',
  'vorgang.spalte.nummer': 'No.',
  'vorgang.spalte.einheit': 'Entity',
  'vorgang.spalte.konto': 'Account',
  'vorgang.spalte.betrag': 'Amount',
  'vorgang.spalte.regel': 'Rule and derivation',

  /* --- Value proof (2.14, R204–R208) --- */
  'nachweis.titel': 'Value proof',
  'nachweis.schliessen': 'Close',
  'nachweis.laedt': 'Compiling the proof …',
  'nachweis.leer': 'No run exists for this financial year — so there is nothing to prove.',
  'nachweis.aufbau.bestand':
    'Opening balance of the financial year, then the movements per period — {von} to {bis}.',
  'nachweis.aufbau.spiegel':
    'The movements of this schedule column per period — {von} to {bis}. The opening value counts once, from the first month.',
  'nachweis.aufbau.erfolg':
    'Income accounts show the postings of the selected period — {bis}. The report states the ' +
    'month, not the year; for the trend, use the period view.',
  'nachweis.spalte.periode': 'Period',
  'nachweis.spalte.was': 'Movement type',
  'nachweis.spalte.betrag': 'Amount',
  'nachweis.spalte.beleg': 'Document',
  'nachweis.spalte.einheit': 'Entity',
  'nachweis.spalte.konto': 'Account',
  'nachweis.spalte.stufe': 'Valuation layer',
  'nachweis.spalte.partner': 'Partner',
  'nachweis.spalte.segment': 'Segment',
  'nachweis.spalte.waehrung': 'Currency',
  'nachweis.spalte.kurs': 'Rate',
  'nachweis.kurs.historisch': 'historical',
  'nachweis.kurs.stichtag': 'closing',
  'nachweis.spalte.betraglokal': 'Amount (local currency)',
  'nachweis.spalte.betragin': 'Amount ({waehrung})',
  'nachweis.spalte.buchungstext': 'Posting text',
  'nachweis.buchungstext.datenimport': 'Data import',

  'nachweis.wahl.titel': 'Dimensions',
  'nachweis.wahl.leer': 'not used anywhere in this proof',
  'nachweis.wahl.alle': 'All',
  'nachweis.wahl.wenige': 'Few',
  'nachweis.wahl.zusammengefasst': 'aggregated',
  'nachweis.spiegelspalte': 'Schedule column',
  'nachweis.saldowechsel.ziel':
    'Reported here, posted to the accounts of {quelle} — the cumulative amount carries the opposite sign.',
  'nachweis.saldowechsel.quelle':
    'Nothing here: the cumulative amount carries the opposite sign and is reported under {ziel}.',
  /* R1429 — Balance reversal on the account, per company (2.7 section 3.7a). */
  'nachweis.saldowechsel.konto.ziel':
    'Reported here: {konten} carries the opposite sign; the account belongs to the line item {quelle}.',
  'nachweis.saldowechsel.konto.quelle':
    '{konten} carries the opposite sign and is reported under {ziel}.',
  /* R1622 — A switched account says so in its name (2.7 section 3.7b). */
  'saldowechsel.zusatz.haben': '(balance switch – credit)',
  'saldowechsel.zusatz.soll': '(balance switch – debit)',
  /* R1624 — When a whole line item switches, a proxy account stands under the target. */
  'saldowechsel.stellvertreter': 'Balance switch from {position}',
  /* R1642, R1643 — An account is cut and pasted in the report (7.3 section 31). */
  'bericht.menue.ausschneiden': 'Cut',
  'bericht.menue.einfuegen': 'Paste: {konto}',
  'einfuegen.titel': 'Paste account',
  'einfuegen.titel.position': 'Paste position',
  'bis.zusatz': '· until {monat}',
  'einfuegen.satz': 'Move {konto} under {position}.',
  'einfuegen.wirkung': 'The move applies',
  'einfuegen.knopf': 'Paste',
  'einfuegen.schliessen': 'Close',
  'einfuegen.gruppe-ohne-geschwister':
    'The account group stays as it was: no other account under {position} has a group it could inherit.',
  /* R1648, R1649 — The account group belongs to the mapping, changed from a month or always (2.1 section 3.3). */
  'kontogruppe.titel': 'Account group',
  'kontogruppe.wirkung': 'The change applies',
  'kontogruppe.uebernehmen': 'Apply',
  'stammdaten.konto.gruppe.meldung':
    'The account group of {konto} is changed only in the "Account group" section of the account window — from a month or always.',
  'stammdaten.konto.gruppe.weg': 'Open the account with right-click → Properties and change the group there.',
  'gliederung.kontengruppe.unbekannt.meldung': 'This account group does not exist.',
  'einfuegen.gruppe-uneins':
    'The account group stays as it was: accounts of different groups sit under {position}. The work list reports the line item.',
  'nachweis.festgelegt': 'Fixed by the cell',
  'nachweis.art.anfangsbestand': 'Opening balance',
  'nachweis.art.bewegung': 'Movement',
  'nachweis.art.veraenderung': 'Change',
  'nachweis.art.endbestand': 'Closing balance',
  'nachweis.zumbeleg': 'to the document',
  'nachweis.beleg.stichtagskurs': 'Adjustment to the closing rate',
  'nachweis.beleg.historischerkurs': 'Adjustment to the historical rate',
  'nachweis.beleg.durchschnittskurs': 'Adjustment to the average rate',
  'nachweis.beleg.gegenbuchung': 'Translation offset',
  'nachweis.rechenweg.stichtag': 'closing rate',
  'nachweis.rechenweg.durchschnitt': 'average rate',
  'nachweis.rechenweg.historisch': 'historical rate',
  'nachweis.rechenweg.eintritt': 'entry rate',
  'nachweis.rechenweg.zeilen': 'converted rows',
  'nachweis.anteil': 'share {betrag}',
  'nachweis.probe.stimmt': 'The proof adds up to the cell you clicked: {betrag} {waehrung}.',
  'nachweis.probe.weichtab':
    'The proof differs: cell {zelle} {waehrung}, proof {summe} {waehrung}, ' +
    'difference {abweichung} {waehrung}.',
  'nachweis.menue': 'Value proof',

  /* --- Posting journal (2.14) --- */
  'journal.lauf': 'Computed',
  'journal.lauf.frueher': 'earlier run',
  'journal.periode': 'Period',
  'journal.periode.alle': 'All periods',
  'journal.grenze': 'Showing the latest {anzahl} documents. Pick a month to see all documents of that month.',
  'journal.belegklammer': 'Document bracket',
  'journal.belegklammer.wert': 'Step',
  'journal.was.fehlt':
    'The document of 2.14 — header, lines, zero sum, gapless number — is decided and not yet ' +
    'built. Until then the consolidation step is the bracket: every posting sits under the step ' +
    'that produced it, with its rule, explanation and derivation. Delivered balances, the ' +
    'carry-forward and the currency translation carry no document today and are therefore absent.',
  'journal.abschnitt.belege': 'Documents of this period',
  'journal.fremdeperiode':
    'The linked document {nummer} belongs to {periode} and is shown here even though it falls ' +
    'outside the selected period.',
  'journal.abschnitt.ohnebeleg': 'Not yet documented: the consolidation postings',
  'journal.keine.belege': 'No document in the journal for this period.',
  'journal.spalte.bewegungsart': 'Movement type',
  'journal.spalte.partner': 'Partner',
  'journal.nullsumme': 'The document balances: its lines sum to zero per currency.',
  'journal.hebt.auf': 'This document reverses document {nummer} in full.',
  'journal.storniert.durch': 'This document was reversed by document {nummer} and no longer applies.',
  'journal.art.lieferung': 'Submission',
  'journal.art.erfassung': 'Manual entry',
  'journal.art.vortrag': 'Carry-forward',
  'journal.art.umrechnung': 'Translation',
  'journal.art.konsolidierung': 'Consolidation',
  'journal.art.storno': 'Reversal',
  'belegtyp.manuell': 'Manual posting',
  'belegtyp.datenimport': 'Data import',
  'belegtyp.excel': 'Excel posting',
  'belegtyp.konsolidierung': 'Consolidation posting',
  'belegtyp.sonstiges': 'Other',

  'journal.spalte.stand': 'Last change',
  'journal.stand.erfasst': 'created',
  'journal.stand.geaendert': 'corrected',

  'journal.suche': 'Account, entity or posting number',
  'journal.suche.platzhalter': 'e.g. 1200 or NL-US',
  'journal.filter.aktiv': 'From the value proof: document {beleg}, account {konto}.',
  'journal.filter.loesen': 'Clear filter',
  'journal.keinlauf': 'No run exists for this selection — so there are no documents.',
  'journal.leer': 'No posting in the journal matches this search.',
  'journal.fremderlauf':
    'This shows the run in which the document was posted, not the one in the context bar. ' +
    'A consolidation posting that appears as an opening balance in the proof was made in ' +
    'December of the previous year.',
  'journal.art.zuordnung': 'Allocation',
  'journal.art.ueberleitung': 'Reconciliation',

  /* --- Manual posting and document folders (2.16) --- */

  'journal.sicht.ordner': 'Document folders',
  'journal.sicht.chronologisch': 'Chronological',
  'journal.sicht.suche': 'Search',
  'suche.journal': 'Search the journal',
  'suche.journal.platzhalter': 'Account, name, partner — or an amount',
  'suche.alsText': 'Searching text: document number, name, explanation, reason, user — and account, movement type and partner of the lines.',
  'suche.alsWert': 'Searching the amount {wert} (± {toleranz}), in both signs.',
  'suche.journal.leer.ordner':
    'No line in this folder — {anzahl} in other folders of this entity. The tree on the left says which.',
  'suche.journal.leer': 'No line in this entity matches that search.',
  'suche.journal.treffer': '{anzahl} lines found.',
  'suche.journal.treffer.einer': 'One line found.',
  'suche.journal.umgekehrt': 'opposite sign',
  'suche.journal.umgekehrt.zahl': '{anzahl} of them with the opposite sign — those are the contra entries.',
  'suche.spalte.ordner': 'Folder',
  'sortierung.hilfe': 'Sort: first click ascending, second descending, third back to the order of the list',
  'journal.gesellschaft': 'Entity',
  'journal.gesellschaft.hinweis': 'The folder sets the entity; every line of the document inherits it.',
  'ordner.titel': 'Folders',
  'ordner.eingang': 'Inbox',
  'ordner.allePerioden': 'all periods',
  'ordner.inUnterordnern': '· {anzahl} in subfolders',
  'ablage.kopiert': 'On the clipboard: {nummer} — {name}',
  'ablage.ausgeschnitten': 'Cut: {nummer} — {name}',
  'ablage.einfuegen.hier': 'Paste here: {ordner} (Ctrl+V)',
  'ablage.verwerfen': 'Clear clipboard',
  'ordner.neu': 'New folder',
  'ordner.neu.name': 'Folder name',
  'ordner.neu.platzhalter': 'e.g. Accruals 2025',
  'ordner.neu.anlegen': 'Create',
  'ordner.umbenennen': 'Rename (F2)',
  'ordner.umbenennen.tun': 'Rename',
  /* --- The folder tree like Explorer (2.16 section 5.5, R980–R985) --- */
  'ordner.menue.neu': 'New folder (Ins)',
  'ordner.menue.umbenennen': 'Rename (F2)',
  'ordner.menue.ausschneiden': 'Cut (Ctrl+X)',
  'ordner.menue.einfuegen': 'Paste (Ctrl+V)',
  'ordner.menue.loeschen': 'Delete (Del)',
  'ordner.fehler.titel': 'That is not possible',
  'ordner.fehler.verstanden': 'OK',
  'ordner.fehler.gesellschaft': 'A document or folder stays with its company.',
  'ordner.fehler.jahresordner':
    'A year folder is named after its financial year. It cannot be renamed, moved or deleted.',
  'ordner.fehler.name': 'There already is a folder “{name}” here.',
  'ordner.fehler.name.weg': 'Please choose a different name — upper and lower case do not count.',
  'ordner.fehler.nichtleer':
    'Only an empty folder can be deleted. “{name}” still holds {belege} documents and {ordner} subfolders.',
  'ordner.fehler.nichtleer.weg':
    'First drag the documents into another folder or back into their year, and delete the subfolders.',
  'ordner.fehler.jahr': 'The folder “{name}” belongs to {jahr} and stays in that financial year.',
  'ordner.fehler.belegjahr':
    'Document {nummer} does not belong to financial year {jahr}. The folder “{ordner}” only takes documents of that year.',
  'ordner.fehler.zyklus': 'A folder cannot be moved into itself.',
  'ordner.loeschen': 'Delete folder',
  'ordner.loeschen.frage': 'Delete folder “{name}” with its {belege} documents?',
  'ordner.loeschen.hinweis':
    'The documents move to the inbox. A folder is storage — deleting it takes no document ' +
    'with it.',
  'ordner.eingang.bleibt': 'The inbox cannot be deleted.',
  'ordner.leer': 'No document in this folder.',
  'ordner.belege': '{anzahl} documents',
  'ordner.beleg': '1 document',

  'ordner.hoeher': 'Move up one row',
  'ordner.tiefer': 'Move down one row',
  'ordner.folge.hinweis':
    'Custom order — rearrange with the arrows. ' +
    'Clicking a column header sorts instead; the custom order stays saved.',

  'beleg.buchen': 'Post',
  'beleg.neu': 'New posting',
  'beleg.name': 'Document name',
  'beleg.ebene': 'Valuation layer',
  'beleg.ebene.waehlen': 'Choose valuation layer …',
  'beleg.name.platzhalter': 'e.g. Reclass construction interest Q2',
  'beleg.erlaeuterung': 'Explanation — what is being posted',
  'beleg.begruendung': 'Reason — why the intervention',
  'beleg.ordner': 'File in',
  'beleg.spalte.konto': 'Account',
  'beleg.spalte.bewegungsart': 'Movement type',
  'beleg.spalte.partner': 'Partner',
  'beleg.spalte.soll': 'Debit',
  'beleg.spalte.haben': 'Credit',
  'beleg.spalte.segment': 'Segment',
  'beleg.spalte.funktionsbereich': 'Function',
  'beleg.spalte.einbeziehungsart': 'Consolidation method',
  'beleg.spalte.transaktionswaehrung': 'Transaction currency',
  'beleg.spalte.nachbuchung': 'Late posting for',
  'beleg.spalte.zeilentext': 'Posting text',
  'beleg.spalten': 'More columns',
  'beleg.spalten.hinweis':
    'Whatever the group keeps as attributes can be posted. Hidden by default — what you tick ' +
    'here stays ticked next time.',
  'beleg.spalte.leer': 'not set up in this group',
  'beleg.konto.waehlen': 'Select account',
  'beleg.partner.pflicht':
    'The partner is mandatory — “no partner” is a valid answer and must be chosen ' +
    'explicitly.',
  'beleg.guv.hinweis': 'Income statement account — posted as Default',
  'beleg.guv.warum':
    'An income statement account has no movement type. 080 Default is stored, as in ' +
    'deliveries, because every journal line carries one.',
  'beleg.konto.ohnezuordnung':
    'This account is not assigned to a position — it appears in no report.',
  'beleg.zeile.hinzu': 'Add line',
  'beleg.zeile.weg': 'Remove line',
  'beleg.summe.soll': 'Total debit',
  'beleg.summe.haben': 'Total credit',
  'beleg.differenz': 'Difference',
  'beleg.gehtauf': 'The document balances.',
  'beleg.gehtnichtauf': 'Debit and credit do not balance — {betrag} missing.',
  'beleg.ausgleichen': 'Balance line {zeile}: {betrag} {seite}',
  'beleg.speichern': 'Post (Ctrl+S)',
  'beleg.abbrechen': 'Cancel (Esc)',
  'beleg.vorbelegt': 'Taken from the report: {was}. All of it editable.',
  'beleg.vorbelegt.spalte':
    'This cell sits in a consolidation column — those hold calculated values. Nothing is ' +
    'prefilled beyond period and measurement layer; the posting goes to statutory accounts II.',
  'beleg.pflicht.fehlt': 'Missing before saving: {felder}.',
  'beleg.pflicht.name': 'name',
  'beleg.pflicht.erlaeuterung': 'explanation',
  'beleg.pflicht.begruendung': 'reason',

  'beleg.berichtigen': 'Correct document {nummer}',
  'beleg.berichtigen.speichern': 'Save correction (Ctrl+S)',
  'beleg.berichtigen.hinweis':
    'Entity, measurement layer, period and document number stay as they are. ' +
    'Name, explanation, reason and the lines change — the previous state ' +
    'remains visible in the change log.',

  'beleg.menue.bearbeiten': 'Edit document',
  'beleg.menue.oeffnen': 'Open document',
  'beleg.menue.umkehren': 'Release in next period',
  'beleg.menue.umkehren.jahresende': 'Release at financial year end',
  'beleg.menue.umkehren.neues-jahr': 'Release at start of new financial year',
  'beleg.menue.protokoll': 'Change log',
  'beleg.menue.loeschen': 'Delete (Del)',
  'beleg.menue.kopieren': 'Copy (Ctrl+C)',
  'beleg.menue.ausschneiden': 'Cut (Ctrl+X)',
  'beleg.menue.einfuegen': 'Paste (Ctrl+V)',

  'beleg.umkehrt': 'Releases document {nummer} in this period.',
  'beleg.umgekehrt': 'Released by document {nummer}.',
  'beleg.umkehren.frage': 'Release document {nummer} in the following period?',
  'beleg.umkehren.hinweis':
    'A separate document with reversed signs is created at once. The original stays valid — ' +
    'in its period it was right.',
  'beleg.umkehren.jahresende.frage': 'Release document {nummer} at the end of the financial year?',
  'beleg.umkehren.jahresende.hinweis':
    'A separate document with reversed signs is created at once in the last month of the financial ' +
    'year. The annual accounts do not show the entry.',
  'beleg.umkehren.neues-jahr.frage': 'Release document {nummer} at the start of the new financial year?',
  'beleg.umkehren.neues-jahr.hinweis':
    'A separate document is created at once in the first month of the new financial year. The ' +
    'annual accounts show the entry; its income statement effect is released from retained earnings brought forward.',

  'beleg.einfuegen.frage': 'What should “{name}” become?',
  'beleg.einfuegen.duplikat': 'Duplicate — with the same amounts',
  'beleg.einfuegen.duplikat.was':
    'A new document with the same lines and amounts, new number. For “the same again, ' +
    'different month”.',
  'beleg.einfuegen.vorlage': 'Template — attributes without amounts',
  'beleg.einfuegen.vorlage.was':
    'The form opens with accounts, movement types, partners and dimensions — the amount ' +
    'columns are empty. Nothing is stored until you post.',

  'loeschen.titel': 'Delete document',
  'loeschen.frage':
    'Document {nummer} “{name}” for {summe} in {zeilen} lines will be deleted. ' +
    'This cannot be undone.',
  'loeschen.laeufe':
    'This document is part of {anzahl} calculated runs. They become outdated and must be ' +
    'recalculated.',
  'loeschen.freigegeben':
    '{anzahl} of those are released. Their figures and check codes stay unchanged — they will ' +
    'carry a note that their basis has changed.',
  'loeschen.umkehrung':
    'It has a release posting {nummer}. That one is deleted too — a release without its ' +
    'subject would be a posting without reference.',
  'loeschen.protokoll':
    'The change log survives: number, timestamp, user and every line with its amount stay ' +
    'readable. The gap in the numbering is thereby explained.',
  'loeschen.tun': 'Delete permanently',
  'loeschen.hindernis.belegart':
    'A document of type “{belegart}” cannot be deleted — what a delivery or a run wrote is ' +
    'the audit trail itself. It gets reversed.',
  'loeschen.hindernis.storniert':
    'This document is reversed by {nummer}. Whoever reversed it chose the other route.',
  'loeschen.hindernis.weg': 'Reverse it instead of deleting it.',

  'protokoll.titel': 'Change log — document {nummer}',
  'protokoll.spalte.zeitpunkt': 'Time',
  'protokoll.spalte.ereignis': 'Event',
  'protokoll.spalte.anwender': 'User',
  'protokoll.spalte.angabe': 'Detail',
  'protokoll.ereignis.angelegt': 'created',
  'protokoll.ereignis.umbenannt': 'renamed',
  'protokoll.ereignis.verschoben': 'moved',
  'protokoll.ereignis.umgekehrt': 'released in following period',
  'protokoll.ereignis.storniert': 'reversed',
  'protokoll.ereignis.geloescht': 'deleted',
  'protokoll.ereignis.geaendert': 'corrected',
  'protokoll.wertnachweis': 'Value record',
  'protokoll.vorher': 'View previous state',
  'protokoll.vorher.name': 'Name',
  'protokoll.vorher.erlaeuterung': 'Explanation',
  'protokoll.vorher.begruendung': 'Reason',
  'protokoll.vorher.titel': 'Document {nummer} — state before the correction of {zeitpunkt}',
  'protokoll.vorher.leer': 'No complete previous state is stored for this entry.',
  'protokoll.vorher.schliessen': 'Back to the change log',
  'protokoll.leer': 'Nothing logged for this document yet.',
  'protokoll.geloescht.titel': 'Explained gaps in the number range',
  'protokoll.geloescht.hinweis':
    'An explained gap is no audit finding; an unexplained one remains one. ' +
    'Here is what stood in it.',
  'protokoll.geloescht.leer': 'No number is missing in this period.',

  /* --- Change log --- */
  'nachweis.eintraege': 'Entries',
  'nachweis.kette': 'Chain',
  'nachweis.unversehrt': 'intact',
  'nachweis.unterbrochen': 'broken',
  'nachweis.bruch': 'First break at',

  /* --- Response times --- */
  'messung.aufrufe': 'Calls',
  'messung.schnellste': 'Fastest',
  'messung.mittel': 'Mean',
  'messung.p95': '95% of calls below',
  'messung.langsamste': 'Slowest',
  'messung.zusage': 'Commitment',
  'messung.erlaeuterung':
    '{ueber} of {anzahl} calls exceed the commitment. The commitment refers to the reference configuration.',

  /* --- The footer: the run's check code · Operations · System status: the two commitments that stood in the footer until 2026-09-11 --- */
  'zustand.nachweis': 'Change log: {meldung}',
  'zustand.antwortzeit':
    'Response time across {anzahl} calls: mean {mittel} ms, 95% of calls below {p95} ms, commitment {budget} ms',
  'zustand.einzelheiten': 'All measurements',

  /* --- Auditor view: the rule report (2.13 section 9) --- */
  'pruefer.einleitung': 'This report answers two questions. First: does the system keep its own ground rules? It measures that on every call, against everything booked in CoKo. Second: what does it do when it consolidates? It works that through on an invented example group — with the very calculation that computes your own consolidated accounts.',

  'pruefer.grundsaetze': 'The ground rules, measured against what is held here',
  'pruefer.grundsaetze.erlaeuterung':
    'There is nothing to tick off here. Every line carries the verdict just computed, and states how many cases it covers — an "all clear" without that number is no answer at all.',

  'pruefer.grundsatz.beleg-geht-auf': 'Every document balances',
  'pruefer.grundsatz.beleg-geht-auf.was': 'Debit equals credit in every document, and separately for each currency. CoKo rejects a document that does not balance at posting time — this measurement checks that it has held without a gap.',
  'pruefer.grundsatz.beleg-geht-auf.gut': 'All {anzahl} documents balance.',
  'pruefer.grundsatz.beleg-geht-auf.schlecht': '{fehler} of {anzahl} documents do not balance.',
  'pruefer.grundsatz.beleg-geht-auf.leer': 'No document has been posted yet.',

  'pruefer.grundsatz.zeile-hat-beleg': 'No posting without a document',
  'pruefer.grundsatz.zeile-hat-beleg.was':
    'Every journal line belongs to a document — including every consolidation entry. A figure that comes from nowhere cannot exist.',
  'pruefer.grundsatz.zeile-hat-beleg.gut': 'All {anzahl} journal lines belong to a document.',
  'pruefer.grundsatz.zeile-hat-beleg.schlecht':
    '{fehler} of {anzahl} journal lines belong to no document.',
  'pruefer.grundsatz.zeile-hat-beleg.leer': 'Nothing has been posted yet.',

  'pruefer.grundsatz.bilanz-geht-auf': 'Assets equal equity and liabilities',
  'pruefer.grundsatz.bilanz-geht-auf.was':
    'In every current set of accounts, assets equal equity and liabilities — each month in the released run, otherwise the latest. Each run is checked against the layout of its own data cut-off: holding yesterday’s figures against today’s layout would break a balance sheet merely because someone has since moved a line.',
  'pruefer.grundsatz.bilanz-geht-auf.gut': 'All {anzahl} current runs balance.',
  'pruefer.grundsatz.bilanz-geht-auf.schlecht':
    '{fehler} of {anzahl} current runs do not balance. The largest deviation is {groesste}.',
  'pruefer.grundsatz.bilanz-geht-auf.leer': 'Nothing has been computed yet.',

  'pruefer.grundsatz.spiegel-geht-auf': 'Every movement schedule reconciles',
  'pruefer.grundsatz.spiegel-geht-auf.was':
    'In every movement schedule, the opening balance plus movements gives the closing balance of the balance sheet — no amount carries a movement type for which the schedule has no column. Measured with the same rule the release applies, on every account that requires a movement type. Each month is measured on its current run — the released one, otherwise the latest — since the start of its financial year: an error that is balanced out by that month no longer counts.',
  'pruefer.grundsatz.spiegel-geht-auf.gut': 'The movement schedules reconcile in all {anzahl} current runs.',
  'pruefer.grundsatz.spiegel-geht-auf.schlecht':
    'In {fehler} of {anzahl} current runs a movement schedule does not reconcile. The largest amount outside the columns: {groesste}.',
  'pruefer.grundsatz.spiegel-geht-auf.leer': 'No run with a schedule account has been computed yet.',

  'pruefer.grundsatz.rest-nicht-zugeordnet': 'No remainder on "unassigned"',
  'pruefer.grundsatz.rest-nicht-zugeordnet.was':
    'On no account that requires a movement type does part of the balance remain without a movement type. Such a remainder is missing from the schedule although it is in the balance sheet. Measured with the same rule the release applies, on the current run of each month since the start of its financial year.',
  'pruefer.grundsatz.rest-nicht-zugeordnet.gut':
    'In all {anzahl} current runs every balance is assigned to a movement type.',
  'pruefer.grundsatz.rest-nicht-zugeordnet.schlecht':
    'In {fehler} of {anzahl} current runs a remainder is "unassigned". The largest: {groesste}.',
  'pruefer.grundsatz.rest-nicht-zugeordnet.leer': 'No run with a schedule account has been computed yet.',

  'pruefer.grundsatz.konto-hat-zuordnung': 'Every account carries exactly one mapping',
  'pruefer.grundsatz.konto-hat-zuordnung.was':
    'Every account feeds exactly one reporting line — none feeds two, none feeds nothing. Statistical accounts are left out: they feed the notes and no balance sheet line, and deliberately carry no mapping.',
  'pruefer.grundsatz.konto-hat-zuordnung.gut': 'All {anzahl} accounts carry exactly one mapping.',
  'pruefer.grundsatz.konto-hat-zuordnung.warn':
    '{fehler} of {anzahl} accounts carry no mapping yet. Their values feed no reporting line — an open task, not a contradiction.',
  'pruefer.grundsatz.konto-hat-zuordnung.schlecht':
    'For {fehler} of {anzahl} accounts two mappings overlap. Which line the account feeds is then undecided.',
  'pruefer.grundsatz.konto-hat-zuordnung.leer': 'No account has been created yet.',

  'pruefer.grundsatz.nachweiskette': 'The audit trail is intact',
  'pruefer.grundsatz.nachweiskette.was':
    'Every entry in the audit trail is bound to the previous one by a checksum. Remove or alter one and everything after it breaks — and the probe says from which number onwards.',
  'pruefer.grundsatz.nachweiskette.gut':
    'The chain over {anzahl} entries has been recomputed end to end.',
  'pruefer.grundsatz.nachweiskette.schlecht':
    'The chain is broken. {anzahl} entries were recomputed.',
  'pruefer.grundsatz.nachweiskette.leer': 'There is no entry in the trail yet.',

  'pruefer.beispiele': 'Where to look: {liste}',
  'pruefer.regeln': 'Behind this: {liste}',

  'pruefer.ungeprueft': 'What is not measured here',
  'pruefer.ungeprueft.erlaeuterung':
    'The specification names further checks that leave no room for judgement. They depend on functionality that does not exist yet, and so they stand here rather than nowhere: a check that never fires looks like one that passed.',
  'pruefer.ungeprueft.ic-erfassungsfehler':
    'An open recording error in the reconciliation between companies',
  'pruefer.ungeprueft.ic-differenz-ungeklaert':
    'A difference between two companies marked as "unresolved"',
  'pruefer.ungeprueft.wechselseitige-beteiligung': 'An unresolved cross-shareholding',
  'pruefer.ungeprueft.ukv-funktionsbereich':
    'A missing functional area under the cost-of-sales method',

  'pruefer.lehrfall': 'The example group',
  'pruefer.lehrfall.erlaeuterung': 'The figures below are not written down. They arise on every call of this page with the very calculation that computes this house’s own accounts — if a rule were broken, the example would no longer come out right. The group itself is invented: it contains no account and no amount from your data and therefore looks the same in every installation.',
  'pruefer.lehrfall.periode': 'Accounts for {periode} · group currency {waehrung}',
  'pruefer.lehrfall.ledger': 'Valuation layer of the example',
  'pruefer.lehrfall.gesellschaften': 'The companies',
  'pruefer.lehrfall.ausgangslage': 'What the companies reported',
  'pruefer.lehrfall.ausgangslage.erlaeuterung':
    'Debit is positive: assets and expenses positive, capital, liabilities and income negative. The sum over all lines of a company is therefore exactly zero — the same probe as applied to a document above. The result for the year is not reported; it arises in the second step from the income and expense accounts.',
  'pruefer.lehrfall.schritte': 'What each type of consolidation makes of it',
  'pruefer.lehrfall.schritte.erlaeuterung':
    'The order is fixed and not a matter of taste: every step builds on the result of the ones before it (the order of consolidation steps is fixed).',
  'pruefer.lehrfall.ohne-buchung': 'This process posted nothing in the example group.',
  'pruefer.lehrfall.summe': '{anzahl} entries',
  'pruefer.lehrfall.summe.eine': 'one entry',
  'pruefer.lehrfall.keine-buchung': 'no entry',

  'pruefer.schritt.umrechnung.regel':
    'Assets and liabilities are translated at the closing rate, equity at the rate of the acquisition date, income and expenses at the average rate. Because three rates meet in one balance sheet, it no longer balances afterwards; the difference is put into equity as a currency translation difference (the translation difference stays separated by source). If a rate is missing, nothing is estimated and the run stops (a missing rate blocks translation).',
  'pruefer.schritt.summierung.regel':
    'The individual accounts are added up, and each company’s result for the year is taken from its income and expense accounts. It is never reported — otherwise it would be there twice.',
  'pruefer.schritt.goodwill.regel':
    'Goodwill is amortised on a straight-line basis. This period’s charge hits the result for the year, everything before it the retained earnings — otherwise the consolidated balance sheet would be out by exactly that amount. Over how many months is set in the rule set and therefore per valuation layer: commercial law requires amortisation, IFRS know only the impairment test.',
  'pruefer.schritt.schuldenkonsolidierung.regel':
    'Receivables and payables between two consolidated companies are both written off in full — not only up to the smaller amount. That is exactly what makes a reconciliation difference visible instead of leaving it as a remainder. What is left goes to the difference account; above the materiality threshold it is reported as well.',
  'pruefer.schritt.aufwandertragkonsolidierung.regel':
    'Intercompany turnover disappears on both sides: the revenue at the supplying company, the expense at the receiving one. This does not change the group result — it merely shortens the income statement by what the group turned over with itself.',
  'pruefer.schritt.zwischenergebniseliminierung-uv.regel':
    'What one company delivered to another and still sits in stock there must not raise the group’s profit. The stock is reduced by the unrealised profit. This is computed from what the companies reported and not from an assumed group margin.',
  'pruefer.schritt.zwischenergebniseliminierung-av.regel':
    'The same for non-current assets — there the unrealised profit does not dissolve with the next sale but over the useful life. Which report belongs to non-current assets is stated by the consolidation process; where none is maintained, every report counts as current assets.',
  'pruefer.schritt.kapitalkonsolidierung.regel':
    'The parent’s carrying amount of the investment is set against the proportionate equity of the subsidiary at the acquisition date. What remains is goodwill. The equity is read from the subsidiary’s balance sheet rather than typed in, and at the rate of that time. At most what was paid above the proportionate carrying equity may be uncovered. The share that does not belong to the parent is shown as non-controlling interests — posted, not merely displayed.',
  'pruefer.schritt.abschluss.regel':
    'Finally the balance sheet is drawn up and checked for balance. If it does not balance, the run says so here — and not the auditor three weeks later.',

  'pruefer.latente-steuern': 'Deferred taxes',
  'pruefer.latente-steuern.regel':
    'Deferred taxes are in the stock of consolidation tools and are not yet computed. This stands here so that the gap does not look like a check that passed.',

  'pruefer.tafel.kurse': 'The rates used',
  'pruefer.tafel.zwischenergebnisse': 'Unrealised profits reported',
  'pruefer.tafel.einbezug': 'Equity at the acquisition date',

  'pruefer.spalte.gesellschaft': 'Company',
  'pruefer.spalte.waehrung': 'Currency',
  'pruefer.spalte.anteil': 'Capital share',
  'pruefer.spalte.kaufpreis': 'Purchase price',
  'pruefer.spalte.erwerb': 'Acquired',
  'pruefer.spalte.konto': 'Account',
  'pruefer.spalte.bewegungsart': 'Movement type',
  'pruefer.spalte.partner': 'Partner',
  'pruefer.spalte.betrag': 'Amount',
  'pruefer.spalte.kursart': 'Rate type',
  'pruefer.spalte.kurs': 'Rate',
  'pruefer.spalte.lieferant': 'Delivered by',
  'pruefer.spalte.bestand': 'Stock',
  'pruefer.spalte.zwischengewinn': 'Of which unrealised',
  'pruefer.spalte.rechnung': 'How the figure arises',

  'pruefer.oberste-mutter': 'ultimate parent',

  /* --- Agenda and printing (7.2 section 19, R810–R812) --- */
  'agenda.titel': 'Contents',
  'agenda.alles-auf': 'Expand all chapters',
  'agenda.alles-zu': 'Collapse all chapters',

  'druck.knopf': 'Print',
  'druck.anlage': 'CoKo · {anlage}',
  'druck.pruefcode': 'Checksum {code}',

  /* --- The workbook for the audit file (2.13 section 9.11) --- */
  'pruefer.pruefcode': 'Checksum of this example',
  'pruefer.pruefcode.was': "The same checksum every consolidation run carries. It appears here, in the footer of every printed page and in the header of every sheet of the workbook. Two installations with the same checksum demonstrably compute alike — and it changes as soon as anything in CoKo's calculation changes. That is its purpose, not a fault.",

  'pruefer.mappe.knopf': 'As an Excel workbook',
  'pruefer.mappe.laeuft': 'Building the workbook …',
  'pruefer.mappe.datei': 'rule-report-{datum}.xlsx',
  'pruefer.mappe.erstellt': 'Created on {datum}',
  'pruefer.mappe.pruefcode': 'Checksum of the example group: {code}',

  'pruefer.mappe.blatt.grundsaetze': 'Ground rules',
  'pruefer.mappe.blatt.ausgangslage': 'Reported figures',
  'pruefer.mappe.blatt.buchungen': 'Entries',

  'pruefer.mappe.titel.grundsaetze': 'Rule report — the ground rules, measured against what is held here',
  'pruefer.mappe.titel.ausgangslage': 'Rule report — what the example group reported',
  'pruefer.mappe.titel.buchungen': 'Rule report — what the types of consolidation make of it',

  'pruefer.mappe.spalte.grundsatz': 'Ground rule',
  'pruefer.mappe.spalte.urteil': 'Verdict',
  'pruefer.mappe.spalte.geprueft': 'Checked',
  'pruefer.mappe.spalte.beanstandet': 'Objections',
  'pruefer.mappe.spalte.fundstellen': 'Where to look',
  'pruefer.mappe.spalte.regeln': 'Rules',
  'pruefer.mappe.spalte.herkunft': 'Source',
  'pruefer.mappe.spalte.art': 'Type of consolidation',
  'pruefer.mappe.spalte.nr': 'No.',
  'pruefer.mappe.spalte.regel': 'Rule',
  'pruefer.mappe.spalte.erlaeuterung': 'Explanation',

  'pruefer.mappe.herkunft.lieferung': 'Reported',
  'pruefer.mappe.herkunft.einbezug': 'Equity at acquisition date',

  'pruefer.urteil.gut': 'all clear',
  'pruefer.urteil.warn': 'to be resolved',
  'pruefer.urteil.schlecht': 'objection',
  'pruefer.urteil.leer': 'nothing to check',
  'pruefer.urteil.ungeprueft': 'not checked by machine',

  'abbruch.mappe-leer.meldung': 'The workbook has no sheet.',
  'abbruch.mappe-leer.weg': 'This is a programming error. Please report which screen it occurred on.',
  'abbruch.mappe-zu-gross.meldung':
    'The workbook would have {zeilen} rows; more than {grenze} are not produced.',
  'abbruch.mappe-zu-gross.weg': 'Narrow the report down and create the workbook again.',

  /* --- Messages: what is · how much it is · what to do --- */
  'meldung.rechenabbruch.titel': 'The close cannot be computed',
  'meldung.stoerung.unerreichbar': 'CoKo cannot be reached right now.',
  'meldung.stoerung.abgebrochen': 'CoKo stopped this step.',
  'meldung.stoerung.abgewiesen': 'CoKo did not accept this step.',
  'meldung.stoerung.unbekannt': 'CoKo could not finish this step.',
  'meldung.stoerung.weg': 'This cannot be fixed on screen. Please notify operations.',
  /* --- Das Störungsfenster (7.2 Abschnitt 30, R1003–R1006) --- */
  'meldung.stoerung.extern': 'A source outside the application did not deliver.',
  'stoerung.titel': 'Something did not happen',
  'stoerung.anzahl': '{anzahl} times',
  'stoerung.weg.allgemein': 'Try again. If it persists, report it to operations — with the time {zeit}.',
  'stoerung.weg.kennung': 'Try again. If it persists, report it to operations.',
  'stoerung.kopieren': 'Copy for operations',
  'stoerung.kopiert': 'copied',
  // A failed request is not empty (7.2 section 39, R1210).
  'abruf.ausgefallen': 'Could not be loaded.',
  'abruf.erneut': 'Try again',
  'rechte.ausgefallen': 'The actions your rights allow could not be loaded — until then, no editing buttons are shown.',
  'stoerung.kopie': 'Time {zeit} · reference {kennung}',
  'stoerung.verstanden': 'Understood',
  'warten.hinweis': 'CoKo is still working — for {sekunden} seconds.',

  /* ======================================================================
     Data import (5.1, R296-R301)
     ====================================================================== */

  'import.quellen.leer': 'No data source has been recorded yet.',

  'import.art.xlsx': 'Excel workbook',
  'import.art.csv': 'CSV file',
  'import.art.sql': 'SQL database',
  'import.art.lucanet': 'LucaNet export',

  'import.zustand.laeuft': 'runs',
  'import.zustand.nichtausfuehrbar': 'recorded, not retrievable',
  'import.zustand.stillgelegt': 'retired',

  /* ---- The data source: file and mapping (5.1 section 3.7, R521–R527) ---- */

  'quelle.datei.titel': 'The file',
  'quelle.datei.beispiel': 'Sample file',
  'quelle.datei.erlaeuterung': 'Read only, never imported — it shows the columns so they can be mapped.',
  'quelle.datei.keine':
    'Load a sample file. Without it no column can be mapped — only a column number guessed.',
  'quelle.datei.gelesen': '{datei}',
  'quelle.datei.vorschau': 'File preview',
  'quelle.datei.keineDateiart':
    'This source is not a file. It is recorded so the directory is complete; nothing is read from it today.',
  'quelle.blatt': 'Worksheet',
  'quelle.kopfzeilen': 'Header rows',
  'quelle.kopfzeilen.erlaeuterung': 'Including the column-title row — it is the last of them.',
  'quelle.fusszeilen': 'Footer rows',
  'quelle.fusszeilen.erlaeuterung': 'What stands at the end and is no delivery, such as the total line.',
  'quelle.periodenherkunft': 'Period of the delivery',
  'quelle.periodenherkunft.keine': 'set when starting',
  'quelle.periodenherkunft.dateiname': 'from the file name',
  'quelle.periodenherkunft.zelle': 'from a fixed cell',
  'quelle.periodenmuster': 'Pattern in the file name',
  'quelle.periodenzelle': 'Cell',
  'quelle.periodenherkunft.erlaeuterung':
    'Without it the period is set when starting the import. Several months at once are then not possible.',
  'quelle.periodenherkunft.dateiname.erlaeuterung':
    'JJJJ is the year, MM the month: SuSa_2025-01.xlsx becomes January 2025.',
  'quelle.periodenherkunft.zelle.erlaeuterung':
    'Counted as in the sheet: B4 is the fourth row. Read are January 2025, 01.2025, 2025-01 and 202501.',

  'quelle.trennzeichen': 'Separator',
  'quelle.dezimal': 'Decimal separator',
  'quelle.faktor': 'Fixed factor',
  'quelle.faktor.erlaeuterung': '1000 if the file delivers in thousands.',
  'quelle.vorzeichen': 'Reverse the sign',
  'quelle.vorzeichen.erlaeuterung': 'For the whole file, if the source system carries credits positive.',
  'quelle.spalte.belegt': 'mapped to {feld}',
  'quelle.spalten.titel': 'The columns of the file',
  'quelle.spalten.ohnedatei': 'Without a sample file no column can be mapped.',
  'quelle.spiegel.bewegungsart': 'Movement type …',
  'quelle.spiegel.hinweis':
    'One movement type per value column. Opening and carrying amount are not posted — they are the check.',
  'quelle.spiegel.probe': 'check only, not posted',

  'quelle.zuordnung.titel': 'The column mapping',
  'quelle.zuordnung.zusatz': '{zugeordnet} of {gesamt} mapped · {nicht} not delivered · {offen} open',
  'quelle.zuordnung.ohnedatei': 'Without a sample file the column mapping can be read, but no column newly placed.',
  'quelle.zuordnung.oeffnen': 'Map columns',
  'quelle.zuordnung.uebernehmen': 'Apply',
  'quelle.zuordnung.abbrechen': 'Cancel',
  'quelle.zuordnung.offen': '{anzahl} still open: {felder}',
  'quelle.zuordnung.vollstaendig': 'Fully mapped',
  'quelle.gruppe.konto': 'Account',
  'quelle.gruppe.betrag': 'Amount',
  'quelle.gruppe.kontierung': 'Coding',
  'quelle.gruppe.waehrung': 'Currency',
  'quelle.gruppe.beleg': 'Document',
  'quelle.gruppe.dimension': 'Optional dimensions',
  'quelle.gruppe.zaehlung': '{zugeordnet} of {gesamt}',

  'quelle.feld.lokalnummer': 'Account number',
  'quelle.feld.ledger': 'Measurement layer',
  'quelle.feld.betrag': 'Amount',
  'quelle.feld.betrag_soll': 'Debit',
  'quelle.feld.betrag_haben': 'Credit',
  'quelle.feld.betrag_sh': 'Debit/credit indicator',
  'quelle.feld.bezeichnung': 'Account description',
  'quelle.feld.bewegungsart': 'Movement type',
  'quelle.feld.ic_partner': 'Partner',
  'quelle.feld.segment': 'Segment',
  'quelle.feld.funktionsbereich': 'Functional area',
  'quelle.feld.waehrung': 'Currency',
  'quelle.feld.transaktionswaehrung': 'Transaction currency',
  'quelle.feld.betrag_tw': 'Amount in transaction currency',
  'quelle.feld.erlaeuterung': 'Posting text',
  'quelle.feld.pflicht': 'mandatory',
  'quelle.feld.offen': 'open',
  'quelle.feld.nichtgeliefert': 'not delivered',
  'quelle.feld.wert': '= {wert}',
  'quelle.feld.beispiel': 'e.g. {wert}',
  'quelle.feld.wertwahl': 'fixed value …',
  'quelle.feld.spaltenwahl': 'pick a column …',
  'quelle.feld.nicht.knopf': 'not delivered',
  'quelle.feld.offen.knopf': 'open again',
  'quelle.feld.loesen': 'release',

  'quelle.satz.blatt': 'sheet “{blatt}”',
  'quelle.satz.kopf': '{kopf} header rows, {fuss} footer rows',
  'quelle.satz.trennzeichen': 'separator “{zeichen}”',
  'quelle.satz.nicht': 'not delivered: {felder}',
  'quelle.satz.sollhaben': 'Amount = {soll} − {haben}',
  'quelle.satz.kennzeichen': 'sign from {spalte}',
  'quelle.satz.kontotrennung': '(number and name, split at the {trennung})',
  'quelle.trennung.leerzeichen': 'first space',
  'quelle.trennung.zeichen': 'first “{zeichen}”',
  'quelle.satz.faktor': 'amounts times {faktor}',
  'quelle.satz.vorzeichen': 'sign reversed',
  'quelle.satz.saldo': 'delivers balances',
  'quelle.satz.verkehrszahl': 'delivers period movements',
  'quelle.betragsart': 'The amount is held',
  'quelle.betragsart.wert': 'in one value column',
  'quelle.betragsart.sollhaben': 'in a debit and a credit column',
  'quelle.betragsart.kennzeichen': 'in one value column with a debit/credit indicator',
  'quelle.betragsart.spiegel': 'in several columns — a movement schedule, one movement type per column',
  'quelle.betragsart.erlaeuterung':
    'Debit minus credit; an empty cell counts as zero. The indicator recognises S · D · Soll · Debit · + and H · C · Haben · Credit · −.',
  'quelle.kontotrennung': 'Number and name in one column',
  'quelle.kontotrennung.aus': 'no — the column holds the number only',
  'quelle.kontotrennung.leerzeichen': 'yes — split at the first space',
  'quelle.kontotrennung.zeichen': 'yes — split at the first “{zeichen}”',
  'quelle.kontotrennung.erlaeuterung': '“1200 Bank” becomes account number 1200 and account description Bank.',

  'quelle.lieferart': 'The figures are',
  'quelle.lieferart.saldo': 'balances — a position; the movement is derived',
  'quelle.betragsart.package': 'Reporting package — an entire workbook in the standard template',
  'quelle.package.titel': 'The reporting package',
  'quelle.package.offen': 'The measurement layer is still missing',
  'quelle.package.fertig': 'Fully set up',
  'quelle.package.vorlage': 'Sheets and columns are set by the package template. To change what is delivered, go to Data import › Package templates.',
  'quelle.package.ebene': 'Every workbook from this data source is posted to this measurement layer.',
  'quelle.lieferart.verkehrszahl': 'period movements — booked as delivered',
  'quelle.lieferart.erlaeuterung':
    'A trial balance delivers positions: balance sheet accounts their balance, P&L accounts the cumulative value since the start of the fiscal year. The import computes the difference to the last position and books it as the period movement.',

  'quelle.speichern': 'Save',
  'quelle.nochnicht': 'Account and amount need a column; the measurement layer needs a column or a fixed value.',

  'quelle.verwendung.titel': 'Usage',
  'quelle.verwendung.zusatz': 'last changed on {zeit}',
  'quelle.verwendung.zusatz.keine': 'last changed on {zeit} · no delivery yet',
  'quelle.verwendung.leer': 'Nothing has been delivered from this source yet.',
  'quelle.liste.offen': '{anzahl} open',

  'zustand.quellen.vollstaendig': 'All {anzahl} data sources have decided where every field comes from.',
  'zustand.quellen.offen':
    '{anzahl} of {gesamt} data sources have open fields — neither mapped nor ticked as “not delivered”. This blocks nothing.',
  'kachel.stand.datenquellen': 'Data sources',
  'kachel.stand.datenquellen.offen': '{anzahl} of {gesamt} with open fields',

  'import.abbildung.befund.meldung': 'The column mapping was not saved: {befund}',
  'import.abbildung.befund.weg': 'Map the field, or choose the fixed value from the stock.',
  'import.abbildung.unlesbar.meldung': 'The column mapping could not be read.',
  'import.abbildung.unlesbar.weg': 'Reload the sheet and save again.',
  'import.quelle.unbekannt.meldung': 'The data source {quelle} does not exist.',
  'import.verkehrszahl.datei.meldung':
    'A data source from Excel or CSV delivers balances, a schedule or a package. Movement figures come only through a direct connection (SQL, Lucanet).',
  'import.verkehrszahl.datei.weg': 'Set the delivery type to balance, or set the source up as a direct connection.',

  'packagevorlage.quelle.vorschlag': 'Reporting package',
  'packagevorlage.frage.quelle': '1. For which data source?',
  'packagevorlage.quelle.neu': 'Design a new data source',
  'packagevorlage.quelle.name': 'Name of the new data source',
  'packagevorlage.frage.gesellschaften': '2. For which entities and which period?',
  'packagevorlage.frage.ebene': '3. At which level do the reporting entities report?',
  'packagevorlage.ebene.lokal': "The entity's own accounts – from its account mapping",
  'packagevorlage.ebene.konzern': 'Group accounts',
  'packagevorlage.ebene.position': 'Line items – few rows, for small reporting entities',
  'packagevorlage.stellvertreter.frage': 'These line items carry several accounts. Which one does CoKo post the reported amount to?',
  'packagevorlage.stellvertreter.offen': 'please choose',
  'packagevorlage.frage.partner': '4. Should the reporting entities split balances by group partner?',
  'packagevorlage.partner.ja': 'Yes – the Intercompany sheet is added',
  'packagevorlage.partner.nein': 'No',
  'packagevorlage.frage.spiegel': '5. Which schedules should be reported?',
  'packagevorlage.spiegel.anlagen': 'Fixed asset schedule',
  'packagevorlage.spiegel.verbindlichkeiten': 'Loan schedule',
  'packagevorlage.spiegel.rueckstellungen': 'Provision schedule',
  'packagevorlage.spiegel.eigenkapital': 'Statement of changes in equity',
  'packagevorlage.frage.zusaetze': '6. What else should the workbook carry?',
  'packagevorlage.zusatz.eroeffnung': 'Balance at 1 January – only for the first package of an entity',
  'packagevorlage.zusatz.segment': 'Segment',
  'packagevorlage.zusatz.funktionsbereich': 'Functional area',
  'packagevorlage.zusatz.kostenstelle': 'Cost center',
  'packagevorlage.faktor.eins': 'Amounts in units',
  'packagevorlage.faktor.tausend': 'Amounts in thousands',
  'packagevorlage.vorschau': 'What the reporting entity receives',
  'packagevorlage.blatt.zeilen': '{blatt}',
  'packagevorlage.erzeugen': 'Generate',
  'packagevorlage.fehler': 'The template was not generated. {grund}',
  'packagevorlage.fehler.speichern': 'The template was generated but could not be saved as {datei}: {grund} Choose another location and press "Generate" again.',
  'packagevorlage.vorschau.fehler': 'The preview could not be retrieved. {grund} What is shown applies to the previous selection.',
  'packagevorlage.grund.abgemeldet': 'Your session has expired. Sign in again and press "Generate" again.',
  'packagevorlage.grund.recht': 'Generating is not open to your sign-in – it requires the "Import" right.',
  'packagevorlage.grund.server': 'The template could not be generated. Check under question 1 whether the data source has already been created before trying again.',
  'packagevorlage.grund.ort': 'The chosen location does not accept the file — choose another.',
  'packagevorlage.schritt.meldung': 'The template was not generated – it failed {schritt}: {grund}',
  'packagevorlage.schritt.weg': 'Fix the cause and press "Generate" again.',
  'packagevorlage.schritt.angelegt': 'The data source "{quelle}" has already been created and is offered under question 1 – choose it next time instead of designing a new one.',
  'packagevorlage.befund.meldung': 'The template was not generated: {befund}',
  'packagevorlage.befund.weg': 'Answer the open questions in the questionnaire.',

  'import.spalte.code': 'Code',
  'import.spalte.name': 'Name',
  'import.spalte.art': 'Type',
  'import.spalte.zustand': 'State',
  'import.spalte.einheit': 'Company',
  'import.spalte.zustaendig': 'Responsible',
  'import.spalte.herkunft': 'Origin',
  'import.spalte.quelle': 'Source',
  'import.spalte.periode': 'Period',
  'import.spalte.gelesen': 'read',
  'import.spalte.wann': 'accepted on',
  'import.spalte.zeile': 'Row',
  'import.spalte.lokalkonto': 'local account',
  'import.spalte.bezeichnung': 'Description',
  'import.spalte.konzernkonto': 'group account',
  'import.spalte.betrag': 'Amount',
  'import.spalte.bewegungsart': 'Movement type',
  'import.spalte.befund': 'Finding',
  'import.spalte.bisher': 'last position',
  'import.spalte.verkehrszahl': 'movement',
  'import.wirkung.frage': 'Deliveries already exist for {perioden}. What should this one affect?',
  'import.wirkung.periode': 'Retroactively: replace {periode}, {folge} is re-derived',
  'import.wirkung.zielperiode': 'Post into',
  'import.wirkung.nachbuchung': 'As a subsequent posting: book the difference in {ziel}, {periode} stays',

  'import.quelle.neu': 'New data source',
  'import.quelle.waehlen': 'Choose a source …',
  'import.quelle.fehler': 'That could not be saved. Does the code already carry a source?',
  'import.herkunft.erlaeuterung':
    'Folder, server, contact person — for people. No credentials: those belong in the operations vault.',
  'import.aktiv.erlaeuterung': 'A retired source stays in the register and is no longer offered.',
  'import.speichern': 'Save',
  'import.schliessen': 'Close',

  'import.schritt.quelle': 'Data source',
  'import.schritt.periode': 'Period',
  'import.periode.kommt.dateiname': 'taken from the file name',
  'import.periode.kommt.zelle': 'taken from cell {zelle}',
  'import.periode.gelesen.dateiname': '{periode} — from the file name',
  'import.periode.gelesen.zelle': '{periode} — from cell {zelle}',
  'import.schritt.datei': 'File',
  'dateiwahl.waehlen': 'Choose file',
  'dateiwahl.waehlen.mehrere': 'Choose files',
  'dateiwahl.anzahl': '{anzahl} files',
  'import.vorschlag.titel.eins': '1 suggestion from the data source',
  'import.vorschlag.titel.mehrere': '{anzahl} suggestions from the data source',
  'import.vorschlag.satz': '{richtung} on {konto} as {bewegungsart}',
  'import.vorschlag.zeilen.eins': '1 line, {betrag}',
  'import.vorschlag.zeilen.mehrere': '{anzahl} lines, {betrag}',
  'import.vorschlag.annehmen': 'Accept',
  'import.vorschlag.ablehnen': 'Decline',
  'import.uebernehmen.vorschlaege': 'First accept or decline every suggestion from the data source.',
  'import.vorschlag.befund.meldung': 'The suggestions were not saved: {befund}',
  'import.vorschlag.befund.weg':
    'Choose group account, direction and movement type from the stock — each account with the same direction only once.',
  'quelle.vorschlaege.titel': 'Movement type suggestions',
  'quelle.vorschlaege.zusatz.keine': 'none',
  'quelle.vorschlaege.zusatz.eins': 'one suggestion',
  'quelle.vorschlaege.zusatz.mehrere': 'several suggestions',
  'quelle.vorschlaege.konto': 'Group account',
  'quelle.vorschlaege.richtung': 'Direction',
  'quelle.vorschlaege.bewegungsart': 'Movement type',
  'quelle.vorschlaege.zunahme': 'Increase',
  'quelle.vorschlaege.abnahme': 'Decrease',
  'quelle.vorschlaege.neu': 'Add suggestion',
  'quelle.vorschlaege.entfernen': 'Remove suggestion',
  'quelle.vorschlaege.unvollstaendig': 'A suggestion needs a group account and a movement type.',
  'quelle.vorschlaege.fest':
    'The movement type is a fixed value at this source. Every line brings one — the suggestions never apply.',
  'import.pruefen': 'Check',
  'import.pruefterade': 'Checking …',
  'import.nichtausfuehrbar.titel': 'This source cannot be read from today ({art}).',

  'import.vorschau.titel': 'What {datei} contains',
  'import.vorschau.gueltig': 'mappable',
  'import.vorschau.fehlerhaft': 'open',
  'import.vorschau.stehtaus': 'pending',
  'import.vorschau.probe': 'Check',
  'import.vorschau.gehtauf': 'balances',
  'import.vorschau.differenz': 'difference {betrag}',
  'import.vorschlag': 'suggestion: {konto}',
  'import.uebernehmen': 'Accept',
  'import.uebernehmen.eins': 'Take over one row',
  'import.sperre.nochnicht': 'Not yet:',
  'import.sperre.konto.eins': 'One row has no group account',
  'import.sperre.konto.mehrere': '{anzahl} rows have no group account',
  'import.sperre.zuordnung.eins': 'One row lacks a mapping',
  'import.sperre.zuordnung.mehrere': '{anzahl} rows lack a mapping',
  'import.sperre.befund.eins': 'One row carries a finding',
  'import.sperre.befund.mehrere': '{anzahl} rows carry a finding',
  'import.sperre.zeilenzeigen': 'Show only these rows',
  'import.sperre.allezeigen': 'Show all rows',
  'import.sperre.schluss': 'An incomplete data set does not enter the system.',
  'import.uebernehmen.differenz':
    'Not yet: debit and credit differ by {betrag}. The balance sheet equation is a precondition, not a finding.',
  'import.laeufe.titel': 'Saved data imports',
  'import.laeufe.leer': 'No data import has been saved yet.',

  /* The saved data imports — what holds, not what happened (R633–R638). */
  'import.neu': 'New data import',
  'import.bereich.hinweis':
    'In this area you can run new data imports, update existing ones and — where the period ' +
    'lock allows it — delete them.',
  'import.filter.kopf': 'Filter data imports by period, company or valuation layer',
  'import.filter.aktiv': 'This column is filtered',

  /* R374 — Every data import carries a name, including the machine-written one. */
  'import.spalte.belegname': 'Name',
  'import.name.erlaeuterung':
    'What you will find this data import under. The suggestion is filled in — change it when ' +
    'several deliveries go into the same period.',
  'import.name.fehlt': 'Enter a name — every data import has one.',
  'import.staende.leer': 'No data import has been saved yet.',
  'import.staende.leer.eingrenzung': 'No saved data import matches this filter.',
  'import.spalte.schicht': 'Valuation layer',
  'bericht.bezug.unbekannt': 'There is no reference called {bezug}.',
  'bericht.bezug.unbekannt.weg': 'Choose a company or a group from the list.',
  'bericht.bezug.kreis': 'A consolidation group is calculated, not read.',
  'bericht.bezug.kreis.weg': 'Use the consolidation run for it; delivered figures sit under a company or a group.',
  'bericht.bezug.leer': 'No company sits under {bezug} in this period.',
  'bericht.bezug.leer.weg': 'Add companies to the group, or choose a single one.',
  'bericht.bezug.fehlt': 'Without a consolidation group or a reference there is nothing to trace.',
  'stammdaten.feld.uebergeordneter_kreis': 'Parent group',
  'bereich.abschluss.konzernabschluesse': 'Group statements',
  'konzernabschluss.spalte.name': 'Group statement',
  'konzernabschluss.spalte.kreis': 'Consolidation group',
  'konzernabschluss.spalte.geschaeftsjahr': 'Financial year',
  'konzernabschluss.spalte.prozesse': 'Processes',
  'konzernabschluss.spalte.gerechnet': 'Computed',
  'konzernabschluss.spalte.zustand': 'Status',
  'konzernabschluss.spalte.aktualisiert': 'Updated',
  'konzernabschluss.zustand.gerechnet': '{anzahl} of twelve months computed',
  'konzernabschluss.zustand.offen': 'Not yet computed',
  'konzernabschluss.nichtgerechnet': 'not yet computed',
  'konzernabschluss.von': 'by {wer}',
  'prozess.zustand.fertig': 'Computed',
  'prozess.zustand.unvollstaendig': 'Did not run in every computed month',
  'prozess.zustand.offen': 'CoKo does not compute this process yet',
  'konzernabschluss.freigegeben': '{anzahl} released',
  'konzernabschluss.leer': 'No group statement yet. Without one only the precomputation runs — and it does not know your processes.',
  'konzernabschluss.neu': 'Create group statement',
  'konzernabschluss.anlegen': 'Create',
  'konzernabschluss.abbrechen': 'Cancel',
  'konzernabschluss.feld.name': 'Name',
  'konzernabschluss.feld.name.hinweis': 'How the auditor recognises it, for instance “Group statement 2026”.',
  'konzernabschluss.feld.kreis': 'Consolidation group',
  'konzernabschluss.feld.kreis.leer': 'Choose a group …',
  'konzernabschluss.feld.ledger': 'Valuation layer',
  'konzernabschluss.feld.ledger.leer': 'Choose a layer …',
  'konzernabschluss.feld.ledger.hinweis': 'Which framework is applied. The same group reports separately under German GAAP and IFRS — those are two statements.',
  'konzernabschluss.feld.von': 'First month of the financial year',
  'konzernabschluss.feld.von.hinweis': 'The other eleven follow. Every month is computed; there are no gaps.',
  'konzernabschluss.feld.vorlage': 'Take processes from',
  'konzernabschluss.feld.vorlage.hinweis': 'The processes are copied, not linked. Removing one here later does not change the template.',
  'konzernabschluss.vorlage.werk': 'Factory version',
  'konzernabschluss.rechnen': 'Run',
  'konzernabschluss.rechnen.titel': 'Run group statement',
  'konzernabschluss.rechnen.frage': 'Run {name} now?',
  'konzernabschluss.rechnen.hinweis': 'All twelve months are computed. A month that cannot be computed does not stop the others — it is listed afterwards with its message.',
  'konzernabschluss.ablage.meldung':
    'The group statement is calculated, but the findings for monitoring the worklist could not be stored. Calculate it once more; if it persists, notify operations.',
  'ablage.spiegel': 'Of which without a column in the schedule: {ohne_spalte}.',
  'uebersicht.konzernbestand.nicht-konsolidiert':
    'Group balances not checked: {monat} is not consolidated. Calculate the month in [[Group statements]]; they are checked afterwards.',
  'uebersicht.konzernbestand.ablage-folgt':
    'Group balances for {monat} not yet checked: the findings of the run are being added and will appear here within a minute.',
  'konzernabschluss.rechnet': 'Computing … month {fertig} of {monate}',
  'konzernabschluss.speichert': 'Saving …',
  'konzernabschluss.rechnet.wer': 'started {wann} by {wer}',
  'konzernabschluss.nichtgespeichert': '{name} not saved',
  'konzernabschluss.nichtgespeichert.monate': 'Not every month could be computed.',
  'konzernabschluss.nichtgespeichert.abbruch': 'The computation ended with a message.',
  'konzernabschluss.ansehen': 'View',
  'konzernabschluss.verschwunden': '{name} not computed to the end',
  'konzernabschluss.verschwunden.satz': 'The service was restarted in the meantime; nothing was saved.',
  'konzernabschluss.neurechnen': 'Compute again',
  'konzernabschluss.verweigert': 'Not saved: {anzahl} months carry an error. Either the whole financial year is written or none — otherwise a statement with missing months would stand there looking finished.',
  'konzernabschluss.verweigert.eins': 'Not saved: one month carries an error. Either the whole financial year is written or none — otherwise a statement with a missing month would stand there looking finished.',
  'konzernabschluss.laufhin.schliessen': 'Close',
  'konzernabschluss.fehlt.meldung': 'This group statement does not exist.',
  'konzernabschluss.fehlt.weg': 'Create it before you run it.',
  'konzernabschluss.ohnename.meldung': 'The group statement needs a name.',
  'konzernabschluss.ohnename.weg': 'Enter a name that speaks, for instance “Group statement 2026”. That is how the auditor finds it in the report.',
  'konzernabschluss.namedoppelt.meldung': 'A group statement is already called {name}.',
  'konzernabschluss.namedoppelt.weg': 'Choose another name. Two statements for the same period are allowed — the original and the adjusted one — two equal names are not.',
  'kreis.mitglieder.titel': 'Who belongs to it',
  'kreis.mitglieder.leer': 'No company admitted yet. Without members the group computes nothing.',
  'kreis.mitglied.gesellschaft': 'Company',
  'kreis.mitglied.ab': 'Belongs from',
  'kreis.mitglied.ab.hinweis': 'From this period its figures flow into the group. Not before — not even where they are posted.',
  'kreis.mitglied.bis': 'Until',
  'kreis.mitglied.offen': 'open',
  'kreis.mitglied.art': 'Included as',
  'kreis.mitglied.oberste': 'top company',
  'kreis.mitglied.waehlen': 'Choose a company …',
  'kreis.mitglied.hinzufuegen': 'Add company',
  'kreis.mitglied.aufnehmen': 'Admit',
  'kreis.mitglied.abbrechen': 'Cancel',
  'lieferrhythmus.spalte.ab': 'Applies from',
  'lieferrhythmus.leer': 'No rhythm maintained; “monthly” applies.',
  'lieferrhythmus.aendern': 'Change rhythm',
  'lieferrhythmus.waehlen': 'Choose rhythm …',
  'lieferrhythmus.ab.hinweis': 'From this month the new rhythm applies; the time before stays as it was.',
  'lieferrhythmus.speichern': 'Save',
  'lieferrhythmus.abbrechen': 'Cancel',
  'lieferrhythmus.entfernen': 'Remove',
  'kreis.mitglied.eintrittskurs': 'Entry rate',
  'kreis.eintrittskurs.fehlt': 'missing',
  'kreis.eintrittskurs.nachtragen.kurz': 'not available — release blocked',
  'kreis.eintrittskurs.stichtag.kurz': '{kurs} · closing rate {periode}, agreed with the auditor',
  'kreis.eintrittskurs.waehlen': 'choose',
  'kreis.eintrittskurs.aendern': 'change',
  'kreis.eintrittskurs.speichern': 'Save entry rate',
  'kreis.eintrittskurs.frage':
    '{einheit} reports in {waehrung}, the group in {konzernwaehrung}. The entry uses one rate for the whole opening balance, fixed. The balance brought in is the one at the end of {eingebracht}.',
  'kreis.eintrittskurs.weg.eingetragen': 'Enter the rate',
  'kreis.eintrittskurs.weg.nachtragen':
    'Historical rates are not available to me. Create a task to add them, and block the closing until that is done.',
  'kreis.eintrittskurs.weg.stichtag':
    'After consulting the auditor, the closing rate of the month may be assumed as the historical rate. Please take it from the exchange-rate table.',
  'kreis.eintrittskurs.kurs': '1 {waehrung} = … {konzernwaehrung}',
  'kreis.eintrittskurs.stichtag.wert': 'Take the closing rate {periode}: {kurs}',
  'kreis.eintrittskurs.stichtag.keiner': 'There is no closing rate {waehrung} for {periode} in the rate table.',
  'kreis.eintrittskurs.zentralbank': 'Fetch rates {periode} from the ECB',
  'kreis.eintrittskurs.zentralbank.ergebnis': 'The ECB provides the closing rate {kurs} for {periode}.',
  'kreis.eintrittskurs.zentralbank.nichts': 'The ECB provides no closing rate for {periode}.',
  'kreis.eintrittskurs.zentralbank.uebernehmen': 'Add to the rate table',
  'kreis.eintrittskurs.pflicht.meldung':
    '{einheit} reports in {waehrung}, the group in {konzernwaehrung}: the entry rate is required.',
  'kreis.eintrittskurs.pflicht.weg':
    'Choose one of the three ways: enter the rate, “Historical rates are not available to me” or take the closing rate.',
  'kreis.eintrittskurs.wert.meldung': 'An entered entry rate for {einheit} needs a value above zero.',
  'kreis.eintrittskurs.wert.weg': 'Enter the rate: 1 {waehrung} = … {konzernwaehrung}.',
  'kreis.eintrittskurs.nachtragen.meldung': 'Whoever chooses “Historical rates are not available to me” enters no rate for {einheit}.',
  'kreis.eintrittskurs.nachtragen.weg': 'Leave the rate empty or choose “Enter the rate”.',
  'kreis.eintrittskurs.nichtfremd.meldung': '{einheit} reports in the group currency {konzernwaehrung} and needs no entry rate.',
  'kreis.eintrittskurs.nichtfremd.weg': 'Leave the entry rate empty.',
  'kreis.eintrittskurs.stichtag.anders.meldung':
    'The closing rate for {einheit} comes from the rate table and cannot be changed by hand.',
  'kreis.eintrittskurs.stichtag.anders.weg': 'Choose “Enter the rate” if a different rate is to apply.',
  'kreis.eintrittskurs.stichtag.fehlt.meldung': 'There is no closing rate {waehrung} for {periode} in the rate table.',
  'kreis.eintrittskurs.stichtag.fehlt.weg': 'Fetch the rates {periode} from the ECB or enter them under Exchange rates.',
  'abbruch.eintrittskurs.fehlt.meldung':
    '{einheit} enters the group in a foreign currency, and its entry rate has not been chosen. The group calculates in {konzernwaehrung}.',
  'abbruch.eintrittskurs.fehlt.weg':
    'Choose the entry rate for the member under [[Consolidation groups]]. Then recalculate.',
  'abbruch.historischer-kurs.fehlt.meldung':
    '{einheit}, {periode}: account {konto}, {bewegungsart}, has no line of historical rates — delivered: {differenz} {waehrung}.',
  'abbruch.historischer-kurs.summe.meldung':
    '{einheit}, {periode}: the historical rates on account {konto}, {bewegungsart}, do not match the delivery — delivery minus table: {differenz} {waehrung}.',
  'abbruch.kreiseintritt.ohne-stand.meldung':
    '{einheit} enters the consolidation group in {periode}, but there is no balance for the end of the previous month. CoKo forms the addition from that balance.',
  'abbruch.kreiseintritt.ohne-stand.weg':
    'Import the trial balance {vormonat} of {einheit} under [[Data imports]]. Then recalculate.',
  'abbruch.historischer-kurs.weg':
    'Create or align the lines under [[Historical rates]]. Then recalculate.',
  'historischer-kurs.startwert': 'Opening value',
  'historischer-kurs.sachverhalt.pflicht': 'A line of historical rates needs the name of its transaction.',
  'historischer-kurs.unbekannt': 'This line of historical rates no longer exists. Please reload the page.',
  'historischer-kurs.kurs.pflicht': 'A historical rate is greater than zero.',

  /* --- Historical rates (2.6 section 2.12) --- */
  'historisch.offen.titel': 'Open in {periode}',
  'historisch.offen.erlaeuterung':
    'What the delivery brings this month and the table does not carry yet. Until it matches, the close does not calculate.',
  'historisch.offen.summe': 'Align the lines',
  'historisch.spalte.gesellschaft': 'Company',
  'historisch.spalte.bewertungsebene': 'Valuation level',
  'historisch.spalte.konto': 'Account',
  'historisch.spalte.bewegungsart': 'Movement type',
  'historisch.spalte.geliefert': 'Delivered',
  'historisch.spalte.intabelle': 'In the table',
  'historisch.spalte.kurs': 'Rate',
  'historisch.spalte.monat': 'Month',
  'historisch.spalte.sachverhalt': 'Transaction',
  'historisch.spalte.betrag': 'Amount',
  'historisch.spalte.konzern': 'In group currency',
  'historisch.spalte.herkunft': 'Origin',
  'historisch.vorschlag.stichtag': 'Closing rate of the month',
  'historisch.vorschlag.ergebnisjahr': 'Rate of the result year {jahr}',
  'historisch.vorschlag.eintrittskurs': 'Entry rate of the group membership',
  'historisch.vorschlag.keiner': 'No proposal',
  'historisch.vorschlag.ueberschreiben': 'Enter a different rate',
  'historisch.eingabe.kurs': '1 {waehrung} = … {konzernwaehrung}',
  'historisch.eingabe.mischkurs': 'Blended rate: 1 {waehrung} = … {konzernwaehrung}',
  'historisch.anlegen': 'Create lines',
  'historisch.tabelle.titel': 'Table of the company',
  'historisch.tabelle.leer': 'There is no line in the table for {gesellschaft} yet.',
  'historisch.gesellschaft.waehlen': 'Choose a company',
  'historisch.herkunft.eintrittskurs': 'Entry rate',
  'historisch.herkunft.vorgeschlagen': 'proposed',
  'historisch.herkunft.eingetragen': 'entered',
  'historisch.speichern': 'Save',
  'historisch.abbrechen': 'Cancel',
  'historisch.aendern': 'Change',
  'historisch.teilen': 'Split',
  'historisch.loeschen': 'Delete',
  'kreis.mitglied.entfernen': 'Remove',
  'kreis.zirkel.meldung': 'That would place the group below itself.',
  'kreis.zirkel.weg': 'Choose a group that is not already below this one. Leaving it empty means this is the top group.',
  'werkzeug.datenuebernahme': 'Transfer of data into the group',
  'werkzeug.goodwillfortschreibung': 'Carry forward goodwill',
  'werkzeug.ic-abstimmung': 'Intercompany reconciliation',
  'werkzeug.schuldenkonsolidierung': 'Elimination of intercompany balances',
  'werkzeug.aufwandertragkonsolidierung': 'Elimination of intercompany income and expense',
  'werkzeug.zwischenergebniseliminierung-uv': 'Elimination of unrealised profit — current assets',
  'werkzeug.zwischenergebniseliminierung-av': 'Elimination of unrealised profit — non-current assets',
  'werkzeug.kapitalkonsolidierung': 'Capital consolidation',
  'werkzeug.latente-steuern': 'Deferred taxes on consolidation entries',
  'werkzeug.anteile-nicht-beherrschend': 'Current profit attributable to non-controlling interests',
  'werkzeug.dividendenkonsolidierung': 'Consolidation of intercompany distributions',
  'werkzeug.datenuebernahme.liest': 'Reads the submitted separate financial statements, translates foreign currency into group currency and places the entities side by side.',
  'werkzeug.goodwillfortschreibung.liest': 'Reads the goodwill arising on initial consolidation and amortises it where the measurement layer requires scheduled amortisation.',
  'werkzeug.ic-abstimmung.liest': 'Reads the intercompany receivables and payables of both sides and sets them against each other.',
  'werkzeug.schuldenkonsolidierung.liest': 'Reads intercompany receivables and payables and eliminates them against each other.',
  'werkzeug.aufwandertragkonsolidierung.liest': 'Reads intercompany income and expense and eliminates them against each other.',
  'werkzeug.zwischenergebniseliminierung-uv.liest': 'Reads inventories carrying a partner code and removes the unrealised profit at the supplier’s margin.',
  'werkzeug.zwischenergebniseliminierung-av.liest': 'Reads non-current assets delivered within the group and removes the unrealised profit they contain over their useful life.',
  'werkzeug.kapitalkonsolidierung.liest': 'Reads the parent’s carrying amount of the investment and the subsidiary’s equity at the date of inclusion.',
  'werkzeug.latente-steuern.liest': 'Reads the entries of the other consolidation processes and measures their tax effects.',
  'werkzeug.anteile-nicht-beherrschend.liest':
    'Reads the profit of the subsidiary and reports the share attributable to non-controlling interests separately. Splitting the remaining equity belongs to the initial consolidation and appears in its voucher.',
  'werkzeug.dividendenkonsolidierung.liest':
    'Reads the parent’s investment income and the subsidiary’s distribution, eliminates each with its own amount and books the difference to the difference account; the non-controlling share of the distribution is booked against their equity line.',
  'werkzeug.ausweiskorrektur': 'Group presentation adjustments',
  'werkzeug.ausweiskorrektur.liest': 'Reads line items or accounts the group presents differently from the separate statements and reclassifies their whole value to the target account.',
  'werkzeug.saldierung': 'Offsetting of balances in the group',
  'werkzeug.saldierung.liest': 'Reads two line items that face each other in the group and books the smaller group total to the collective account of the larger one.',
  'regel.umgliederung': 'Reclassification',
  'regel.konzernsaldierung': 'Offset in the group',
  'meldung.ausweiskorrektur.keine': 'No presentation adjustment is maintained for these consolidated statements.',
  'meldung.saldierung.keine': 'No offsetting is maintained for these consolidated statements.',
  'meldung.ausweiskorrektur.entlastung-fremd': 'Account {konto} does not belong to {position}. Relief is booked only within the source — this adjustment was not booked.',
  'meldung.konsolidierungsbeleg.nichtauf':
    'Voucher {nummer} in step “{schritt}” does not balance in {waehrung}: {betrag} is missing. The run was not saved; the previous state still applies.',
  'meldung.konsolidierungsbeleg.nichtauf.weg':
    'This is not caused by your data. Please report it with the voucher number.',
  'meldung.ausweiskorrektur.ohne-konten': 'No account is assigned to line item {position}. There is nothing to repost.',
  'meldung.ausweiskorrektur.ohne-gegenseite': 'The offset on {position} names no opposite side. Which side survives is decided only when the figures are calculated, so both are required.',
  'buchung.umgliederung.heraus': '{betrag} {waehrung} removed from {konto} — the group presents this line item differently.',
  'buchung.umgliederung.hinein': '{betrag} {waehrung} transferred to {konto} — the group-wide presentation of this line item.',
  'buchung.konzernsaldierung.heraus': '{betrag} {waehrung} removed from {konto} — across the group the larger of the two sides survives.',
  'buchung.konzernsaldierung.hinein': '{betrag} {waehrung} transferred to {konto} — the side the group presents.',
  'schritte.spalte.stelle': 'Position',
  'prozess.blatt.einstellungen': 'Settings',
  'prozess.blatt.belege': 'Entries produced',
  'prozess.bucht': 'Posts to',
  'prozess.angabe': 'Item',
  'prozess.ausHandbuch': 'From the group manual',
  'prozess.ausHandbuch.ausgabe': 'These values come from edition {nummer} of the group manual and apply to every consolidated statement.',
  'prozess.ausHandbuch.ohne': 'No edition of the group manual applies to this period.',
  'prozess.ausHandbuch.leer': 'not set',
  'prozess.abweichend': 'This process posts to instead',
  'prozess.abweichend.leer': 'as in the group manual',
  'prozess.abweichend.schalter': 'Post differently from the group manual',
  'prozess.abgrenzung.vorhanden': 'This process works on selected line items. The selection is maintained in the template area.',
  'prozess.ohnevorgang': 'CoKo does not run this process yet. It appears in the sequence because it exists in substance — it produces no entries.',
  'prozess.belege.keine': 'This process posted nothing in {monat}.',
  'prozess.belege.ohne':
    'This month was calculated before consolidation vouchers existed. Recalculate the consolidated statements to see its vouchers here.',
  'prozess.beleg.ausweis': 'Result in the balance sheet',
  'prozess.beleg.ausnahme': 'Counter-entry not yet posted',
  'prozess.belege.suche': 'Search entity or matter …',
  'prozess.belege.ordnung': 'Sorted by',
  'prozess.belege.nachgesellschaft': 'Entity',
  'prozess.belege.nachregel': 'Matter',
  'prozess.belege.nachbetrag': 'Amount, largest first',
  'prozess.belege.zeile': 'one line',
  'prozess.belege.gehtauf': 'balances',
  'prozess.speichern': 'Save',
  'prozess.schliessen': 'Close',
  'prozess.weichtab': 'departs',
  'prozess.einbezogen': 'Line items and accounts included',
  'ic.titel': 'Intercompany reconciliation',
  'ic.zeigtnur':
    'This sheet shows the pairwise differences. Nothing is posted — that is done by debt consolidation and income and expense consolidation.',
  'ic.abschluss': 'Consolidated statement',
  'ic.blatt.schuldenkonsolidierung': 'Differences, debt consolidation',
  'ic.blatt.aufwandertragkonsolidierung': 'Differences, income and expense',
  'ic.sicht.saldo': 'Balance',
  'ic.sicht.ytd': 'Cumulative year to date',
  'ic.sicht.verkehr': 'Movement',
  'ic.spalte.paar': 'Company pair',
  'ic.schwelle': 'Tolerated threshold: {betrag} {waehrung} — from the group manual.',
  'ic.waehrung': 'of which currency difference',
  'ic.eigeneschwelle': 'own threshold {betrag}',
  'ic.eigeneschwelle.titel':
    'This pair has its own threshold from the group manual, not the general one.',
  'ic.laedt': 'Computing the differences …',
  'ic.befund.stimmt': 'matches to the cent',
  'ic.befund.innerhalb': 'within the tolerated threshold',
  'ic.befund.ausserhalb': 'outside the tolerated threshold',
  'ic.leer.ohne-prozess':
    'This consolidated statement does not carry the process. Without it there is nothing to reconcile — add it under consolidation steps.',
  'ic.leer.ohne-positionen':
    'The process carries no included line items. Empty means "all intercompany accounts" there, and "all" cannot be split into two sides — set the line items in the process window.',
  'ic.leer.ohne-konten':
    'No account hangs under the included line items. The assignment lives in account mapping.',
  'ic.leer.ohne-paare':
    'No company posted a counterparty on these line items.',
  'ic.ohnepartner':
    'Without partner — no counterparty, therefore no difference but an open amount',
  'prozess.weichtab.titel': 'This process posts differently from the group manual.',
  'prozess.oeffnen': 'Open process',
  'prozess.korrektur.titel': 'Presentation adjustments',
  'prozess.grundlage.titel': 'What this process works on',
  'prozess.grundlage.zaehlung': '{gesamt} investments in the group · {voll} fully consolidated · {erfasst} with recorded equity at inclusion.',
  'prozess.grundlage.zaehlung.eine': 'One investment in the group, {voll} fully consolidated, {erfasst} with recorded equity at inclusion.',
  'prozess.grundlage.suche': 'Search entity …',
  'prozess.grundlage.alle': 'Show the other {anzahl}',
  'prozess.grundlage.nichts': 'No investment found.',
  'prozess.grundlage.tochter': 'Subsidiary',
  'prozess.grundlage.quote': 'Capital share',
  'prozess.grundlage.erwerb': 'Acquired',
  'prozess.grundlage.quelle': 'Equity at inclusion',
  'prozess.grundlage.quelle.erfasst': 'recorded in the investment register',
  'prozess.grundlage.quelle.geliefert': 'from the delivery of the acquisition month',
  'prozess.grundlage.quelle.staende': 'balances at takeover, recorded in the investment register',
  'prozess.grundlage.quelle.manuell': 'consolidated manually — exception, the process books nothing',
  'prozess.grundlage.von': 'held by {mutter}',
  'prozess.grundlage.ohneerwerb': 'no acquisition date',
  'prozess.grundlage.nichtvoll': 'not calculated here ({art})',
  'prozess.grundlage.hinweis': 'Recording is for exceptions only — when the delivery of the acquisition month does not allow a correct derivation. The values are maintained in the investment register, where they carry their history.',
  'einbeziehungsart.voll': 'Full consolidation',
  'einbeziehungsart.quotal': 'Proportionate consolidation',
  'einbeziehungsart.equity': 'At equity',
  'einbeziehungsart.nicht': 'not included',
  'prozess.korrektur.leer': 'No adjustment maintained yet. Without one the group presents what the entities delivered.',
  'prozess.korrektur.neu': 'Add adjustment',
  'prozess.korrektur.quelle': 'Line item or account whose value moves',
  'prozess.korrektur.entlastung': 'Relieved on',
  'prozess.korrektur.eigene': 'On its own accounts',
  'prozess.korrektur.aufquelle': 'On the account itself',
  'prozess.korrektur.ziel': 'Account it moves to',
  'prozess.saldierung.titel': 'Offsets',
  'prozess.saldierung.leer': 'No offset maintained yet. Without one both sides stand in the group as the entities delivered them.',
  'prozess.saldierung.neu': 'Add offset',
  'prozess.saldierung.seite': 'The two line items',
  'prozess.saldierung.sammelkonto': 'Their collective accounts',
  'prozess.saldierung.quelle': 'First side: the line item',
  'prozess.saldierung.ziel': 'First side: its collective account',
  'prozess.saldierung.hinweis': 'An offset applies only when both sides carry a balance in the group. The smaller group total moves to the collective account of the larger — which is which is decided when the figures are calculated.',
  'schritte.amende': 'calculates last, in this order',
  'prozess.amende.vermerk': 'Calculates after all other processes. Presentation adjustments and offsetting calculate among themselves in the order shown here.',
  'prozess.korrektur.gegenquelle': 'Opposite side: the second line item',
  'prozess.korrektur.gegenziel': 'Opposite side: its collective account',
  'prozess.korrektur.hinweis': 'Reclassifying always moves the whole value. A line item takes every account below it, including one added later; relief is booked on its own accounts or as one sum on an account of the line item.',
  'konzernabschluss.prozesse.laedt': 'Loading the processes …',
  'schritte.spalte.name': 'Step',
  'schritte.leer': 'No step defined yet. Without steps the group statement computes nothing.',
  'schritte.neu': 'Add step',
  'schritte.anlegen': 'Create',
  'schritte.abbrechen': 'Cancel',
  'schritte.speichern': 'Save',
  'schritte.entfernen': 'Remove',
  'schritte.entfernen.titel': 'Remove step',
  'schritte.entfernen.frage': 'Remove the step {name} from the sequence?',
  'schritte.entfernen.hinweis':
    'Earlier statements keep it — they were computed with it. The process still computes afterwards, but with the values from the group manual and without the settings of this step.',
  'schritte.feld.werkzeug': 'Process',
  'schritte.feld.werkzeug.leer': 'Choose a process …',
  'schritte.feld.werkzeug.hinweis':
    'What the step does. The same process may appear several times — for instance elimination of intercompany income and expense twice, once for revenue and cost, once for financial result. The step is its place in the sequence.',
  'schritte.feld.name': 'Name',
  'schritte.feld.name.hinweis': 'What the step is called in your statement. The number is not part of the name — it follows from the position.',
  'schritte.rechnetnicht': 'does not compute yet',
  'schritte.rechnetnicht.erlaeuterung':
    'There is no calculation for this process yet. It is listed so that the sequence is complete.',
  'schritte.befund.doppelt':
    '“{werkzeug}” appears {anzahl} times in the sequence and no step is scoped — it would be eliminated twice. Scope the steps against each other under [[Scope]]; then this message disappears.',
  'schritte.befund.fehlt':
    'The process “{werkzeug}” has not been set up yet. Click [[Add step]] and confirm the standard settings once. This message then disappears.',
  'schritte.befund.ohne-differenzkonto':
    '“{werkzeug}” has no difference account; without it the group statement cannot be calculated. Enter it under [[Scope]], then this message disappears.',
  'schritte.befund.ohne-differenzkonto-waehrung':
    '“{werkzeug}” has no difference account for currency differences; without it the group statement cannot be calculated. Enter it under [[Scope]] — it may be the same as the difference account. Then this message disappears.',
  'schritte.befund.unbekannt':
    'A step names the process {werkzeug}, which does not exist, and is never computed. Click [[Remove]], then this message disappears.',
  'schritte.nurvorlage':
    'Every new group statement starts with these steps. The steps of an existing statement are maintained on it, under [[Group statements]].',
  'schritte.nachweis': 'Evidence',
  'schritte.nachweis.zu': 'Close evidence',
  'schritte.nachweis.titel': 'What {werkzeug} posted',
  'schritte.nachweis.monat': 'Month',
  'schritte.nachweis.buchungen': 'Postings',
  'schritte.nachweis.meldung': 'Message',
  'schritte.nachweis.gesellschaft': 'Company',
  'schritte.nachweis.konto': 'Account',
  'schritte.nachweis.betrag': 'Amount',
  'schritte.nachweis.erlaeuterung': 'Explanation',
  'schritte.nachweis.nielief': 'This process has never run — the group statement was not started.',
  'schritte.fehlt.meldung': 'This step no longer exists.',
  'schritte.fehlt.weg': 'Reload the page — it was probably removed.',
  'schritte.uebersteuerung.fremd.meldung':
    'Process “{werkzeug}” does not read {angabe}.',
  'schritte.korrektur.fremd.meldung': 'Process “{werkzeug}” reads no presentation adjustments.',
  'schritte.korrektur.fremd.weg': 'Only the group presentation adjustments process can reclassify, and only the offsetting process can offset. Add it to this sequence.',
  'schritte.korrektur.ohnegegenseite.meldung': 'The offset on {position} names no opposite side.',
  'schritte.korrektur.ohnegegenseite.weg': 'Which side survives across the group is decided only when the figures are calculated. Enter the second line item and its collective account.',
  'schritte.uebersteuerung.fremd.weg':
    'Only what this process actually uses can be overridden. Everything else stays in the group manual.',
  'schritte.werkzeug.unbekannt': 'The process {werkzeug} does not exist.',
  'schritte.werkzeug.unbekannt.weg':
    'Choose one from the list. The set is fixed — a new process is a subject-matter decision, not a setting.',
  'schritte.unbekannt': 'Step {schritt} does not exist in this sequence.',
  'schritte.unbekannt.weg': 'Reload the page. The step was probably removed elsewhere.',
  'schritte.position.unbekannt': 'These positions do not exist: {positionen}.',
  'schritte.position.unbekannt.weg': 'Choose positions from the statement layout.',
  'schritte.differenzkonto.unbekannt': 'Account {konto} does not exist.',
  'schritte.differenzkonto.unbekannt.weg': 'Choose an existing account. If the field is left empty, the difference account from the rule set is used.',
  'schritte.abgrenzen': 'Scope',
  'schritte.abgrenzen.zu': 'Close scope',
  'schritte.abgrenzung.titel': 'What this process works on',
  'schritte.abgrenzung.positionen': 'Positions',
  'schritte.abgrenzung.positionen.hinweis': 'Empty means all intercompany accounts. A chosen position includes its sub-positions.',
  'schritte.abgrenzung.differenzkonto': 'Difference account',
  'schritte.abgrenzung.differenzkonto.hinweis': 'Where the remaining difference is posted. Empty means the account from the rule set.',
  'schritte.abgrenzung.toleranz': 'Threshold',
  'schritte.abgrenzung.toleranz.hinweis': 'From this amount a difference is reported as material. It is posted either way. Empty means the threshold from the rule set.',
  'schritte.abgrenzung.leer': 'No position chosen — the process works on all intercompany accounts.',
  'schritte.marge': 'Group margin',
  'schritte.marge.definition':
    'The margin is the share of profit in the transfer price. If the holding sells for 100 what cost it 80, the margin is 20 %. Eliminated is the inventory carrying the supplier’s partner code times this margin: 50,000 in stock at 20 % give an unrealised profit of 10,000. If you think in mark-ups, convert: a 25 % mark-up on cost is a 20 % margin.',
  'schritte.marge.positionen': 'Inventories',
  'schritte.marge.positionen.leer': 'No position chosen — without inventories the process eliminates nothing.',
  'schritte.marge.positionen.hinweis': 'The positions holding the inventories. A chosen position includes its sub-positions.',
  'schritte.ertrag.positionen': 'Investment income',
  'schritte.ertrag.positionen.leer': 'No position chosen — without it the process recognises no investment income.',
  'schritte.ertrag.positionen.hinweis': 'The positions holding income from investments. A chosen position includes its sub-positions.',
  'schritte.kapko.beteiligungen': 'Investments',
  'schritte.kapko.beteiligungen.leer': 'No position chosen — without it the group accounts do not calculate.',
  'schritte.kapko.beteiligungen.hinweis': 'The positions holding the shares in the subsidiaries. A chosen position includes its sub-positions.',
  'schritte.kapko.eigenkapital': 'Equity',
  'schritte.kapko.eigenkapital.leer': 'No position chosen — without it the group accounts do not calculate.',
  'schritte.kapko.eigenkapital.hinweis': 'The equity positions offset against the investment — without the non-controlling interests.',
  'schritte.marge.standard': 'Standard margin',
  'schritte.marge.standard.hinweis': 'Applies to every supply relationship without a margin of its own. If it is missing while inventory carries a partner code, the run stops.',
  'schritte.marge.keine': 'none',
  'schritte.marge.paare': 'Margin per supply relationship',
  'schritte.marge.paare.leer': 'No margin of its own — the standard margin applies to all.',
  'schritte.marge.paare.hinweis': 'One line per direction: the margin at which the supplier sells to the recipient. It takes precedence over the standard margin.',
  'schritte.marge.lieferant': 'Supplier',
  'schritte.marge.empfaenger': 'Recipient',
  'schritte.marge.marge': 'Margin in percent',
  'schritte.marge.paar.neu': 'Add supply relationship',
  'schritte.marge.paar.entfernen': 'Remove supply relationship',
  'schritte.marge.quelle.standard': 'Standard margin of the process',
  'schritte.marge.quelle.paar': 'Margin of the supply relationship',
  'schritte.marge.ungueltig.meldung': 'A margin is not between 0 and below 100 percent.',
  'schritte.marge.ungueltig.weg': 'The margin is the share of profit in the transfer price — enter a value from 0 to below 100.',
  'schritte.marge.paar.ungueltig.meldung': 'A supply relationship is incomplete, duplicated or names the same entity twice.',
  'schritte.marge.paar.ungueltig.weg': 'Choose a supplier and a different recipient for each line, and each pair per direction only once.',
  'schritte.abgrenzung.hinzufuegen': 'Add position',
  'schritte.abgrenzung.entfernen': 'Remove',
  'schritte.abgrenzung.nur-eliminierung': 'Scope applies only to processes that eliminate intercompany relationships.',
  'schritte.abgrenzung.positionen.waehlen': 'Choose line items',
  'schritte.abgrenzung.differenzkonto.keins': 'Account from the rulebook',
  'schritte.abgrenzung.differenzkontoWaehrung': 'Difference account for currency differences',
  'schritte.abgrenzung.differenzkontoWaehrung.hinweis':
    'Where the currency part of the difference is posted — what both sides carry as currency difference. Required; may be the same as the difference account.',
  'schritte.abgrenzung.differenzkontoWaehrung.keins': 'not chosen',
  'schritte.abgrenzung.ohnebezug':
    'Line items from balance sheet and P&L are chosen at the group statement: it says which valuation layer and which period apply — and that decides which line items exist.',
  'gliederungswahl.titel.positionen': 'Line items from balance sheet and P&L',
  'gliederungswahl.hinweis.positionen':
    'Several at once. A chosen line item includes its sub-items.',
  'gliederungswahl.bericht.bilanz': 'Balance sheet',
  'gliederungswahl.bericht.guv': 'Profit and loss',
  'gliederungswahl.uebernehmen': 'Apply',
  'gliederungswahl.abbrechen': 'Cancel',
  'gliederungswahl.aufklappen': 'Expand {position}',
  'gliederungswahl.zuklappen': 'Collapse {position}',
  'gliederungswahl.leer': 'No line item here.',
  'handbuch.spalte.ausgabe': 'Edition',
  'handbuch.spalte.name': 'Name',
  'handbuch.spalte.gueltig': 'Valid',
  'handbuch.spalte.abschluesse': 'Statements',
  'handbuch.geltung.offen': 'from {von}',
  'handbuch.geltung.bis': '{von} to {bis}',
  'handbuch.leer': 'No edition yet. Without one no group statement can be computed.',
  'handbuch.neu': 'New edition',
  'handbuch.neu.name': 'Group manual, edition {nummer}',
  'handbuch.gesperrt': 'released',
  'handbuch.speichern': 'Save',
  'handbuch.entfernen': 'Remove',
  'handbuch.entfernen.titel': 'Remove edition',
  'handbuch.entfernen.frage': 'Remove edition {nummer} — {name} — completely?',
  'handbuch.entfernen.hinweis': 'The edition and its rules are deleted, not retired. Statements computed with it have to be computed again.',
  'handbuch.abschnitt.ausgabe': 'The edition',
  'handbuch.zusatz.ausgabe': 'Number {nummer}',
  'handbuch.abschnitt.rechnen': 'Rounding and thresholds',
  'handbuch.zusatz.rechnen': '{stellen} decimal places',
  'handbuch.abschnitt.konten': 'Consolidation accounts',
  'handbuch.feld.name': 'Name',
  'handbuch.feld.name.hinweis': 'What the auditor reads in the report.',
  'handbuch.feld.gueltigVon': 'Valid from',
  'handbuch.feld.gueltigVon.hinweis': 'From this period every group statement follows this edition.',
  'handbuch.feld.gueltigBis': 'Valid until',
  'handbuch.feld.gueltigBis.hinweis': 'Leave empty while the edition still applies.',
  'handbuch.feld.nachkommastellen': 'Decimal places',
  'handbuch.feld.nachkommastellen.hinweis': 'How every computed amount is rounded.',
  'handbuch.feld.icGrenze': 'Intercompany difference threshold',
  'handbuch.abschnitt.icpaare': 'Thresholds for individual company pairs',
  'handbuch.zusatz.icpaare.leer': 'the general threshold applies to all',
  'handbuch.icpaare.leer':
    'No pair has its own threshold — the intercompany difference threshold above applies to all.',
  'handbuch.icpaare.hinweis':
    'A pair threshold takes precedence over the general one and applies in both directions. Two companies in the same currency should match to the cent; two in different currencies always carry the translation-driven share.',
  'handbuch.icpaar.a': 'Company',
  'handbuch.icpaar.b': 'Counterparty',
  'handbuch.icpaar.grenze': 'Threshold',
  'handbuch.icpaar.neu': 'Add pair',
  'handbuch.icpaar.entfernen': 'Remove',
  'handbuch.feld.icGrenze.hinweis': 'Up to this amount a difference between two companies is written off instead of reported.',
  'handbuch.feld.nutzungsdauer': 'Amortise goodwill over (months)',
  'handbuch.feld.nutzungsdauer.hinweis': 'That is {jahre} years of scheduled amortisation.',
  'handbuch.feld.nutzungsdauer.ohne': 'Required: German GAAP requires amortisation over a useful life in months. The edition cannot be saved while this is empty.',
  'handbuch.feld.firmenwertKonto': 'Goodwill',
  'handbuch.feld.firmenwertKonto.hinweis': 'Where the excess of the purchase price over the share of equity is posted.',
  'handbuch.feld.unterschiedsbetragPassivKonto': 'Negative difference on consolidation',
  'handbuch.feld.unterschiedsbetragPassivKonto.hinweis':
    'Where the amount is posted when the share of equity exceeds the purchase price. Under HGB a liabilities-side account, shown after equity; under IFRS an income account. Required even if the case never occurs.',
  'handbuch.feld.firmenwertAbschreibungKonto': 'Goodwill amortisation',
  'handbuch.feld.firmenwertAbschreibungKonto.hinweis': 'The expense of the current period.',
  'handbuch.konto.pflicht.meldung': 'The field “{feld}” is empty. Without this account no group closing is calculated.',
  'handbuch.konto.pflicht.weg': 'Choose an account, then save.',
  'handbuch.feld.gewinnvortragKonto': 'Profit/loss carried forward',
  'handbuch.feld.gewinnvortragKonto.hinweis':
    'Where group postings of earlier financial years go, such as amortisation of earlier periods — it burdened earlier years, not this one.',
  'handbuch.feld.abschreibungFinanzanlagenKonto': 'Write-downs of financial assets',
  'handbuch.feld.abschreibungFinanzanlagenKonto.hinweis':
    'Only needed when a parent writes its investment in a subsidiary down or up: the capital consolidation reverses the write-down there. May stay empty — if it is missing when needed, the run stops.',
  'handbuch.feld.abgangBeteiligungenKonto': 'Result from disposal of investments',
  'handbuch.feld.abgangBeteiligungenKonto.hinweis':
    'Only needed for a partial disposal of a subsidiary: the parent’s gain is adjusted to the group’s gain there. May stay empty — if it is missing when needed, the run stops.',
  'handbuch.feld.nichtBeherrschendKonto': 'Non-controlling interests',
  'handbuch.feld.nichtBeherrschendKonto.hinweis': 'The part of equity that does not belong to the group.',
  'handbuch.feld.icDifferenzKonto': 'Intercompany difference',
  'handbuch.feld.icDifferenzKonto.hinweis': 'Where a difference below the threshold is written off.',
  'handbuch.feld.icDifferenzKontoWaehrung': 'Intercompany difference from currency',
  'handbuch.feld.icDifferenzKontoWaehrung.hinweis':
    'Where the currency part of an intercompany difference is written off. May be the same account as for the intercompany difference.',
  'handbuch.feld.zwischenergebnisKonto': 'Unrealised profit',
  'handbuch.feld.zwischenergebnisKonto.hinweis': 'Where profit from an intragroup delivery is eliminated.',
  'handbuch.abschnitt.vergleich': 'What changed against edition {nummer}',
  'handbuch.vergleich.zusatz': '{anzahl} entries',
  'handbuch.vergleich.zusatz.gleich': 'unchanged',
  'handbuch.vergleich.gleich': 'Both editions make the same decision on every entry.',
  'handbuch.vergleich.angabe': 'Entry',
  'handbuch.vergleich.vorher': 'Edition {nummer}',
  'handbuch.vergleich.nachher': 'Edition {nummer}',
  'handbuch.vergleich.offen': 'open',
  'handbuch.vergleich.keineAbschreibung': 'no scheduled amortisation',
  'handbuch.freigegeben': 'A statement using this edition has been released.',
  'handbuch.freigegeben.meldung': 'Edition {ausgabe} carries the released statement {periode}.',
  'handbuch.freigegeben.weg': 'Create a new edition. What an auditor has seen stays unchanged.',
  'import.package.titel': 'Reporting package',
  'import.package.blaetter': 'Sheets',
  'import.package.eroeffnung': 'Balance 1 Jan',
  'import.package.eroeffnung.gebucht': 'will be posted',
  'import.package.eroeffnung.verprobt': 'checked only',
  'import.package.bereit': 'ready',
  'import.package.befunde': '{anzahl} open',
  'import.package.befund': 'Finding',
  'import.package.nurverprobt': 'checked only',
  'import.package.spalte.blatt': 'Sheet',
  'import.package.spalte.wirkung': 'Purpose',
  'import.package.rolle.eroeffnung': 'Opening balance',
  'import.package.rolle.financials': 'Trial balance',
  'import.package.rolle.intercompany': 'Partner detail',
  'import.package.rolle.spiegel': 'Movement schedule',
  'import.package.proben': '{anzahl} checks do not balance:',
  'import.package.uebernehmen': 'Post entire package',
  'import.package.buchtgerade': 'Posting the workbook …',
  'import.package.gesperrt':
    'A package is posted as a whole or not at all — every check must balance and every line must be mapped.',
  'import.package.bericht.halt':
    'Stopped at {blatt}. The {gebucht} sheets before it remain posted, those after are untouched.',
  'import.stapel.titel': 'Batch',
  'import.stapel.alle': 'all files',
  'import.stapel.dateien': 'Files',
  'import.stapel.monate': 'Months',
  'import.stapel.ersetzt': 'Replaced',
  'import.stapel.bereit': 'ready',
  'import.stapel.befunde': '{anzahl} with findings',
  'import.stapel.spalte.monat': 'Month',
  'import.stapel.spalte.datei': 'File',
  'import.stapel.spalte.summe': 'Difference',
  'import.stapel.spalte.stand': 'State',
  'import.stapel.stand.neu': 'new',
  'import.stapel.stand.ersetzt': 'replaces {name}',
  'import.stapel.stand.gebucht': 'posted',
  'import.stapel.uebernehmen': 'Accept all {anzahl} months',
  'import.stapel.buchtgerade': 'Posting {anzahl} months …',
  'import.stapel.gesperrt': 'Only once every file names its month, balances and is mapped.',
  'import.stapel.bericht.halt':
    'Stopped at {datei}. The {gebucht} months before stay posted, those after are untouched.',
  'import.stapel.hindernis.periodenherkunft':
    'This data source does not say where the period comes from. Until it does, one file per month applies — the origin is set on the source.',
  'import.stapel.hindernis.leer': 'No file was submitted.',
  /* R1407 — Without a readable column mapping the data import reads nothing and names the data source. */
  'import.abbildung.quellefehlt': 'Data source {quelle} no longer exists.',
  'import.abbildung.unlesbar': 'Data source “{quelle}” has no readable column mapping.',
  'import.abbildung.vorlageunlesbar': 'Import template {vorlage} has no readable column mapping.',
  'import.abbildung.ohnequelle': 'No data source is named whose column mapping could be used for reading.',
  /* R1412, R1413, R1418 — An account without a name is rejected. */
  'import.bezeichnung.fehlt.meldung':
    'The delivery is not taken over: {anzahl} rows carry an account number but no name (rows {zeilen}). Without a name it cannot be checked whether a number means the same account as at another company — a wrong mapping would only show in the group P&L.',
  'import.bezeichnung.fehlt.weg':
    'Add the name in the file, or map the column with the name in the column mapping.',
  'import.bezeichnung.nurnummer.meldung':
    'The delivery is not taken over: the column is mapped as “number and name in one column”, but {anzahl} rows contain only a number (rows {zeilen}).',
  'import.bezeichnung.nurnummer.weg':
    'Check the separator on the account number in the column mapping, or add the name in the file.',
  'import.bezeichnung.package.meldung':
    'The package is not taken over: the Intercompany sheet has {anzahl} account numbers that the Financials sheet of the same workbook does not list (rows {zeilen}). Without them the account name is missing, and the number alone does not map.',
  'import.bezeichnung.package.weg':
    'List every account of the Intercompany sheet in the Financials sheet as well, with its name.',
  'import.abbildung.weg':
    'Set up the column mapping on the data source. Without it CoKo reads no file — an assumed layout could read the wrong column as the amount.',
  /* R1409 — A stored setting that is used in the calculation and cannot be read stops the run. */
  'lauf.einstellung.unlesbar.meldung': 'The setting “{einstellung}” of process “{prozess}” cannot be read.',
  'lauf.einstellung.unlesbar.weg':
    'Open the process and save it again. Until then CoKo does not calculate: without the setting, figures would be based on the group manual instead of the process.',
  'lauf.einstellung.abgrenzung': 'Scope',
  'lauf.einstellung.kaufpreisallokation': 'Purchase price allocation',
  'lauf.einstellung.uebersteuerung': 'Overrides of group manual values',
  'lauf.einstellung.ausweiskorrektur': 'Reclassifications and offsets',
  'lauf.uebersteuerung.keinezahl.meldung':
    'The override “{wert}” for “{angabe}” in process “{prozess}” cannot be read as a number.',
  'lauf.uebersteuerung.icDifferenzGrenze': 'Threshold for intercompany differences',
  'lauf.uebersteuerung.goodwillNutzungsdauerMonate': 'Useful life of goodwill in months',
  /* R1394 — There are no factory values for the group closing. */
  'abbruch.handbuch.unlesbar.meldung': 'Edition {ausgabe} of the group manual cannot be read.',
  'abbruch.handbuch.angabefehlt.meldung': 'Edition {ausgabe} of the group manual is missing “{angabe}”.',
  'abbruch.handbuch.kontofehlt.meldung':
    '“{angabe}” in edition {ausgabe} of the group manual names account {konto}, which is not in the chart of accounts.',
  'abbruch.handbuch.weg':
    'Complete the entry in the group manual. Until then CoKo does not calculate: a built-in value could point to an account that means something else in your chart.',
  /* R1396 — The useful life of goodwill is required: months or “No amortisation” (IFRS only). */
  'abbruch.handbuch.keineabschreibung.meldung':
    'Edition {ausgabe} of the group manual says “No amortisation” for goodwill. Under {rechnungslegung} a useful life in months is required.',
  'handbuch.nutzungsdauer.pflicht.meldung': 'The useful life of goodwill is missing.',
  'handbuch.nutzungsdauer.pflicht.weg':
    'Enter the useful life in months. German GAAP requires amortisation; an empty field would otherwise mean “forgotten”, not “no amortisation”.',
  'import.stapel.befund.doppelt':
    'Two files name {periode}. One would replace the other — which one applies, the files do not say.',
  'import.stapel.befund.periode': 'No period recognised — looked for it at {stelle}.',
  'import.stapel.befund.unlesbar': 'The file cannot be read: {grund}',
  'import.stapel.befund.pruefung': '{offen} rows without mapping, difference {summe}.',
  'import.stapel.halt': 'Stopped at {periode}: {grund}',
  'import.stand.aktualisieren': 'Update',
  'import.ordner.alle': 'All data imports',
  'import.ordner.markieren': 'Select',
  'import.ordner.allemarkieren': 'Select all shown',
  'import.ordner.markiert': '{anzahl} selected',
  'import.ordner.ziel': 'Move to',
  'import.ordner.verschieben': 'Move',
  'import.ordner.aufheben': 'Clear selection',
  'import.ordner.mehrere':
    'A folder belongs to exactly one company. To move data imports, select those of one company only.',
  'import.ordner.leer': 'No data import is in this folder.',

  'import.stand.loeschen': 'Delete',
  'import.stand.gilt': 'in force',
  'import.stand.geschlossen': '{kreis} is closed',
  'import.stand.freigegeben': '{kreise} released',
  'import.stand.nachbuchung': 'Late entry for {periode}',
  'import.stand.aktualisierung': 'Update required — {periode} was updated afterwards',
  'monitor.aktualisierung.eins':
    'There is 1 import whose preceding period was updated without updating the following month. This may endanger data consistency. I recommend updating the affected import:',
  'monitor.aktualisierung.mehrere':
    'There are {anzahl} imports whose preceding period was updated without updating the following months. This may endanger data consistency. I recommend updating the affected imports:',
  'import.stand.verlauf': 'History',
  'import.stand.beleg': 'Document',
  'journal.zurueck.datenimporte': 'Back to the data imports',
  'import.stand.laeufe.leer': 'No run is recorded for this state any more.',
  'import.stand.zurueck': 'Back to the list',

  /* R931–R933 — Update reloads the retained file (5.1 section 3.39). */
  'import.aktualisierung.eingelegt':
    'The retained file {datei} is loaded. If you have a newer version, choose it in the File field.',
  'import.aktualisierung.liest': 'Reading the retained files …',
  'import.aktualisierung.alle': 'Update all {anzahl} flagged imports',
  'import.aktualisierung.titel': 'Update all flagged data imports',
  'import.aktualisierung.hinweis':
    'The retained files are read. Posting runs per company and data source in ascending period order; each month’s movement is rebuilt against the balance before it.',
  'import.aktualisierung.uebernehmen': 'Update {anzahl} data imports',
  'import.aktualisierung.buchtgerade': 'Updating {anzahl} data imports …',
  'import.aktualisierung.gesperrt':
    'Only once no row carries a finding. Update a data import with a finding from its row in the list.',
  'import.aktualisierung.weg': 'Update the data import from its row and choose the file there by hand.',
  'import.aktualisierung.befund.ohnedatei': 'No file is retained for this data import.',
  'import.aktualisierung.befund.package': 'A reporting package is updated as a whole workbook.',
  'import.aktualisierung.befund.pruefung': 'The file cannot be posted as it is — {offen} rows without mapping.',
  'import.aktualisierung.bleibt': 'stays, because {kreis} is closed',
  'import.aktualisierung.halt': '{gebucht} data imports updated, then stopped. The rest remains flagged.',

  /* R638 — The warning names what disappears. No "Are you sure?". */
  'import.loeschen.titel': 'Delete this data import?',
  'import.loeschen.was':
    '{einheit}, {periode}, {schicht} — {zeilen} rows totalling {summe}. Document and postings ' +
    'disappear; what is deleted does not return as a predecessor.',
  'import.loeschen.spur':
    'The run and the delivered file stay untouched, and the change log keeps every row with ' +
    'its amount. The trail back to the original file does not break.',
  'import.loeschen.zuordnung':
    'Any assignment made stays behind and then shows entirely as "unassigned". That blocks the ' +
    'release until it is resolved.',
  'import.loeschen.tun': 'Delete permanently',
  'import.loeschen.abbrechen': 'Cancel',
  'import.loeschen.laeuft': 'Deleting …',

  'datenimport.unbekannt.meldung': 'This data import no longer exists (document {beleg}).',
  'datenimport.unbekannt.weg': 'Reload the list — someone else may have deleted it already.',
  'datenimport.hindernis.geschlossen.meldung':
    'The closing for {kreis} in {periode} is closed. Deleting and replacing is only possible in ' +
    'an open period.',
  'datenimport.hindernis.geschlossen.weg':
    'Reopen the period — or send the new file as a late entry into the next open month. It is ' +
    'accepted either way.',
  'datenimport.hindernis.folgeperiode.meldung':
    'A delivery already exists for {periode}. Its opening balance rests on the closing balance ' +
    'of this data import.',
  /* R639 — What gets replaced is what was marked. */
  'import.ersetzt.fremd.meldung':
    'The data import to be replaced does not belong to {einheit} in {periode}.',
  'import.ersetzt.fremd.weg':
    'Reload the list and press Update on the row you mean.',
  'import.neu.hinweis':
    'This data import is added; it replaces nothing. To replace an existing one, go back and ' +
    'press Update on its row.',
  'import.ersetzt.hinweis': 'Being replaced: {bezeichnung}. Everything else in {periode} stays, including a top-up posting.',

  'datenimport.hindernis.folgeperiode.weg':
    'Delete the data import for {periode} first, then this one.',


  /* ---- Account mapping (B8, R138, R303) ---- */

  'mapping.suche.beispiel': 'account, code or value',
  'mapping.suche': 'Search',
  'mapping.leer': 'No account has been mapped yet.',
  /* ---- The company and the workbook (5.1 section 3.49, R1153, R1155) ---- */
  'mapping.gesellschaft': 'Company',
  'mapping.gesellschaft.alle': 'All companies',
  'mapping.gesellschaft.leer': 'The saved deliveries of this company use none of these mappings.',
  'mapping.export': 'As an Excel workbook',
  /* ---- Retiring from a month, in the row menu (5.1 section 3.50, R1160–R1164) ---- */
  'mapping.spalte.gueltig': 'valid',
  'mapping.gueltig.ab': 'from {von}',
  'mapping.gueltig.bis': '{von} to {bis}',
  'mapping.menue.stilllegen': 'Retire …',
  'mapping.menue.inkraft': 'Reinstate',
  'mapping.stilllegen.titel': 'Retire {quelle} → {ziel}',
  'mapping.stilllegen.laedt': 'Looking up who uses it …',
  'mapping.stilllegen.verwender': 'Used by {liste} — it is retired for all.',
  'mapping.stilllegen.niemand': 'Not used by any saved delivery.',
  'mapping.stilllegen.unbekannt': 'Who uses it could not be looked up. It is retired for all companies.',
  'mapping.stilllegen.ab': 'No longer valid from',
  'mapping.stilllegen.bis': 'It stays valid until {monat}.',
  'mapping.stilllegen.ganz': 'It then applies to no month and disappears from the list.',
  /* ---- Correcting, history and the month it takes effect (5.1 section 3.51, R1170–R1175) ---- */
  'mapping.menue.berichtigen': 'Correct …',
  'mapping.menue.verlauf': 'History …',
  'mapping.berichtigen.titel': 'Correct {quelle}',
  'mapping.berichtigen.partner': 'Partner',
  'mapping.berichtigen.ohnepartner': 'no fixed partner',
  'mapping.berichtigen.ab': 'Effective from',
  'mapping.berichtigen.bis': 'Until {monat}, {ziel} still applies.',
  'mapping.berichtigen.ganz': 'The line gets the new target from its start.',
  'mapping.berichtigen.davor': 'The month lies before the start of this line ({von}).',
  'mapping.berichtigen.tun': 'Correct from {monat}',
  'mapping.berichtigen.tun.leer': 'Correct',
  'mapping.verlauf.titel': 'History: {quelle}',
  'mapping.verlauf.laedt': 'Loading …',
  'mapping.verlauf.spalte.wann': 'When',
  'mapping.verlauf.spalte.aenderung': 'Change',
  'mapping.verlauf.tat.angelegt': 'created',
  'mapping.verlauf.tat.berichtigt': 'corrected',
  'mapping.verlauf.tat.stillgelegt': 'retired',
  'mapping.verlauf.tat.inkraft': 'reinstated',
  'mapping.verlauf.tat.aufgehoben': 'for no month any more',
  'mapping.verlauf.tat.geaendert': 'changed',
  'mapping.wirksam.titel': 'From when does the new mapping take effect?',
  'mapping.wirksam.ab': 'Effective from',
  'mapping.wirksam.tun': 'Map from {monat}',
  'mapping.wirksam.tun.leer': 'Map',
  'import.zuordnung.monatfehlt.bis.meldung': '{lokal} was mapped to {ziel} from {von} to {bis}.',
  'import.zuordnung.monatfehlt.ab.meldung': '{lokal} is mapped to {ziel} from {von}.',
  'import.zuordnung.monatfehlt.weg': 'Enter the month from which the new mapping takes effect.',
  'import.zuordnung.spaeter.meldung': 'For {lokal}, a later mapping to {ziel} already applies from {von}.',
  'import.zuordnung.spaeter.weg': 'Correct the later mapping under Data import → Mappings.',
  'mapping.keintreffer': 'No match.',
  /* ---- Creating a new account (R307, R308) ---- */

  'import.konto.zwilling.meldung':
    'The account {nummer} {bezeichnung} already exists in the group chart of accounts.',
  'import.konto.zwilling.weg':
    'Drag the local account onto it instead of creating a second one of the same name. The same number under a different name is permitted.',
  'import.konto.ohnegeschwister.meldung':
    'No account hangs under {position} yet from which the account group could be derived.',
  'import.konto.uneins.meldung':
    'The accounts under {position} belong to different account groups ({gruppen}).',
  'import.konto.gruppe.weg':
    'Choose the account group explicitly — translation method, movement-type requirement and fund membership hang on it.',
  'import.konto.keineposition.meldung': 'The position {position} does not exist.',
  'import.konto.unbekannt.meldung': '{konto} is not an account of the group chart of accounts.',
  'import.konto.nurcoko.meldung': '{konto} is a technical account delivered with CoKo — no local account can be mapped to it.',
  'import.konto.nurcoko.weg': 'Map the local account to an ordinary account.',
  'import.konto.unbekannt.weg':
    'Map onto an existing account — or create a new one by dragging onto a position.',

  'mapping.position.ausgefallen': 'The “Position” column could not be loaded.',
  'mapping.gliederung.leer': 'No structure is recorded for this measurement layer.',
  'mapping.offeneplaene':
    'The new account is not placed in position plan {plaene} yet; it falls under “unassigned” there.',
  'mapping.gruppe.frage': 'No account exists under {position} yet. Which account group should {lokal} get?',
  'mapping.gruppe.wahl': 'Account group …',
  'mapping.gruppe.anlegen': 'Create account and map',
  'mapping.gruppe.abbrechen': 'Cancel',
  'mapping.rechenposition': 'calculated line — its value is a formula, it carries no accounts',
  'mapping.rahmen.titel': 'Group chart of accounts',
  'mapping.hierablegen': 'Drop {lokal} here',
  'mapping.tabelle.titel': 'Accounts',
  'mapping.bewegungsart.titel': 'Movement types',
  'mapping.bewegungsart.leer': 'No movement type code has been mapped yet.',
  'mapping.werte.titel': 'Further values',
  'mapping.werte.leer': 'No other value has been mapped yet.',
  'mapping.spalte.code': 'submitted value',
  'mapping.spalte.feld': 'Dimension',
  'mapping.spalte.ziel': 'Target',
  'mapping.spalte.bewegungsart': 'Movement type',

  'mapping.spalte.lokalnummer': 'local account',
  'mapping.spalte.kontonummer': 'account number',
  'mapping.spalte.konzernkonto': 'group account',
  'mapping.spalte.position': 'Position',
  'mapping.konto.waehlen': 'Choose a group account …',
  'mapping.speichern': 'Save mapping',
  'import.zuordnen': 'Map to: {konto}',
  'import.ordnetzu': 'Mapping …',

  /* ---- Mapping during the run (5.1 section 3.11, R589–R592) ---- */

  'import.zuordnung.oeffnen.eins': 'Make one mapping',
  'import.zuordnung.oeffnen.mehrere': 'Make {anzahl} mappings',
  'import.zuordnung.zeile': 'Map …',
  'import.zuordnung.titel': 'Map the accounts in this submission',
  'import.zuordnung.klammer': '{datei} · a new account is created in the chart of accounts of valuation layer {ledger}',
  'import.zuordnung.ohneplan':
    'No valuation layer above raw layer {ledger} carries a line item plan — no new account can be created here. Place the layer under a group that carries one (Master data → Valuation layers). Mapping to an existing account still works.',
  'import.zuordnung.fortschritt': '{erledigt} of {gesamt} mapped',
  'import.zuordnung.leer': 'Every account in this submission is mapped.',
  'import.zuordnung.zeilen': '{anzahl} rows',
  'import.zuordnung.zugeordnet': 'mapped to {konto}',
  'import.zuordnung.angelegt': 'newly created as {konto}',
  'import.zuordnung.neuanlegen': 'Create a new group account',
  'import.zuordnung.position': 'Choose a line item …',
  'import.zuordnung.schliessen': 'Close and re-check',
  'import.zuordnung.schliessen.ohne': 'Close',
  'import.zuordnung.veraltet':
    'The figures above are from the last read. They will be re-checked once this window closes.',
  /* ---- Movement types during the run (5.1 section 3.12, R593–R596) ---- */

  'import.zuordnung.abschnitt.konten': 'Accounts',
  'import.zuordnung.abschnitt.bewegungsarten': 'Movement types',
  'import.zuordnung.abschnitt.werte': 'Other values',

  /* ---- Two panes and a tree (5.1 section 3.15, R602-R606) ---- */

  'import.zuordnung.sorten': 'What is still open in this delivery',
  'import.zuordnung.quelle': 'From the delivery',
  'import.zuordnung.ziel': 'Where it belongs',
  'import.zuordnung.grenze': 'Move the boundary between delivery and statements',
  'import.zuordnung.gewaehlt': '{anzahl} selected',
  'import.zuordnung.dazunehmen': 'Add {konto} to the selection',
  'import.zuordnung.bericht': 'Statement',
  'import.zuordnung.bericht.bilanz': 'Balance sheet',
  'import.zuordnung.bericht.guv': 'P&L',
  'import.zuordnung.ziel.suche': 'Search line item or account …',
  'import.zuordnung.ziel.keintreffer': 'Neither a line item nor an account matches.',
  'import.zuordnung.erstwaehlen': 'Choose an account of the delivery on the left.',
  'import.zuordnung.einhaengen.eins': 'Create this account under {position}',
  'import.zuordnung.einhaengen.mehrere': 'Create {anzahl} accounts under {position}',
  'import.zuordnung.ziel.ohnestelle': 'Without a place in this plan ({anzahl})',
  'import.zuordnung.ziel.ohnestelle.grund':
    'These group accounts hang on no line item of this plan. Merging still works — they appear in the statement only once they have a place.',
  'import.zuordnung.aufklappen': 'Expand {position}',
  'import.zuordnung.zuklappen': 'Collapse {position}',
  'import.zuordnung.art.waehlen': 'Choose a movement type …',
  'import.zuordnung.art.neuanlegen': 'Create a new movement type',
  'import.zuordnung.art.kern': 'Choose a core movement type …',
  'import.zuordnung.art.zugeordnet': 'mapped to {konto}',
  'import.zuordnung.art.angelegt': 'newly created as {konto}',
  'import.bewegungsart.unbekannt.meldung': 'Movement type {bewegungsart} does not exist.',
  'import.bewegungsart.unbekannt.weg':
    'Pick one from the list, or create one under its core movement type.',
  'import.bewegungsart.widerspruch.meldung': 'Code {code} is already mapped to {bewegungsart}.',
  'import.bewegungsart.widerspruch.weg':
    'Retire the existing mapping if the code is to mean something else from now on — a posted period keeps its meaning.',
  'import.bewegungsart.keinkern.meldung': '{bewegungsart} is itself a detail already.',
  'import.bewegungsart.keinkern.weg':
    'A new movement type hangs under a core one; a detail under a detail does not exist.',
  'import.bewegungsart.vortrag.meldung': '{bewegungsart} carries the prior month forward and cannot be chosen.',
  'import.bewegungsart.vortrag.weg':
    'CoKo carries the prior month forward itself. Map the code to a movement, otherwise Default.',
  'stammdaten.spaltenkopf.doppelt.meldung': '“{kopf}” already heads the column of {bewegungsart} in the same schedule.',
  'stammdaten.spaltenkopf.doppelt.weg':
    'Give this movement type its own short label — the schedule shows the short label, otherwise the name without its number.',
  'import.bewegungsart.schonda.meldung': 'A movement type already carries code {code}.',
  'import.bewegungsart.schonda.weg':
    'Map the submitted code to that movement type instead of creating a second one with the same code.',

  /* ---- The remaining list fields (5.1 section 3.13, R597–R600) ---- */

  'import.zuordnung.wert.waehlen': 'Choose a value …',
  'import.zuordnung.wert.neuanlegen': 'Create it',
  'import.zuordnung.wert.zugeordnet': 'mapped to {konto}',
  'import.zuordnung.wert.angelegt': 'newly created as {konto}',
  'import.zuordnung.wert.keinvorrat':
    'This list does not grow during a run — the value is created in master data maintenance and mapped here.',
  'import.wert.waehrung.meldung': 'A currency is not mapped.',
  'import.wert.waehrung.weg':
    'It is checked against the company master record. A differing code means the file belongs to a different company.',
  'import.wert.widerspruch.meldung': 'Value {code} is already mapped to {ziel}.',
  'import.wert.widerspruch.weg':
    'Retire the existing mapping if the value is to mean something else from now on.',
  'import.wert.keinvorrat.meldung': 'No value can be created for {feld} during a run.',
  'import.wert.keinvorrat.weg':
    'The open lists are partner codes, segments and optional dimensions. Anything that carries meaning for the statements is created in maintenance.',


  /* ---- Help (7.1, R304) ---- */

  'bereich.hilfe.name': 'Help',
  'bereich.hilfe.zweck': 'Step by step through the paths one walks rarely.',
  'bereich.hilfe.import': 'Import a submission',
  'bereich.hilfe.struktur': 'Creating and renaming an account',
  'hilfe.anleitung.zweck':
    'The whole path from an empty system to figures in the report. Every step shows the screen it happens on and marks what to press.',
  'hilfe.schritt': 'Step {nummer} of {gesamt}',
  'hilfe.zurueck': 'Back',
  'hilfe.weiter': 'Next',
  'hilfe.dorthin': 'Open this screen',
  'hilfe.bildfehlt': 'No screenshot exists for this step yet. The text still says what to do.',
  'hilfe.alleschritte': 'All steps',
  'hilfe.aufnahme': 'Screenshot from {datum}',

  /* ---- The companion (7.1 section 10, R545–R549) ---- */

  'bereich.hilfe.gesellschaft': 'Creating and including an entity',
  'bereich.hilfe.begleiter': 'The companion',
  'begleiter.weg.neuegesellschaft': 'A new entity submits for the first time',
  'begleiter.schonda': 'already on the list',
  'begleiter.anhaengen': 'Append another path',
  'begleiter.erkannt': 'Recognised by the model: something has been added here since the list was created.',
  'begleiter.stand': '{erledigt} of {gesamt} done',
  'begleiter.fertig': 'All done.',
  'begleiter.verwerfen': 'Discard list',
  'begleiter.daneben': 'Place beside',
  'begleiter.unbekannt': 'This step no longer exists in CoKo. The list is older than the current state.',

  'keinedaten.titel': 'No data exists for this selection.',
  'keinedaten.text':
    'Nothing has been delivered for this reporting object, measurement layer and period.',
  'keinedaten.konzern.titel': 'No group close has been calculated for this selection.',
  'keinedaten.konzern.text':
    'Group figures only arise in a group close. The delivered data of each company is listed above under "Delivered data".',
  'keinedaten.konzern.weg': 'The way to the group close is shown above this sentence.',
  'keinedaten.weg': 'Make a different selection above.',

  /* ======================================================================
     The texts of the calculation core
     ====================================================================== */

  'text.roh': '{text}',
  'text.unbekannt': 'No text is available for this step.',

  /* --- Rule names. The code is in the check code, the name is here. --- */
  'regel.umrechnungsdifferenz': 'Translation difference',
  'regel.umrechnungsanpassung': 'Adjustment to the account’s rate',
  'regel.waehrungsumrechnung': 'Currency translation',
  'regel.ergebnisuebernahme': 'Transfer of result',
  'regel.ergebnisspiegelung': 'Mirroring of result',
  'regel.schuldenkonsolidierung': 'Elimination of intercompany balances',
  'regel.schuldenkonsolidierung-differenz': 'Intercompany balances — difference',
  'regel.aufwandertragkonsolidierung': 'Elimination of intercompany income and expense',
  'regel.aufwandertragkonsolidierung-differenz': 'Intercompany income and expense — difference',
  'regel.dividendenkonsolidierung': 'Consolidation of intercompany distributions',
  'buchung.dividende.ertrag':
    '{konto} at {einheit}: The dividend from {partner} of {betrag} {waehrung} is not income from the group’s point of view and is eliminated.',
  'buchung.rundungsdifferenz.ausschuettung': 'Rounding difference from distributing the dividend of {einheit}: {betrag} {waehrung}.',
  'buchung.dividende.ruecklage':
    '{konto} at {einheit}: The distribution is reversed by {betrag} {waehrung} — it stayed within the group or is shown against the non-controlling interests.',
  'buchung.dividende.vortrag':
    '{konto} at {einheit}: The dividend consolidation of prior months is carried forward with {betrag} {waehrung}.',
  'buchung.aufwandertrag.vortrag':
    '{konto} at {einheit}: The intercompany income and expense elimination of prior months is carried forward with {betrag} {waehrung}.',
  'buchung.bestand.vortrag':
    '{konto} at {einheit}: The balance of the prior month opens with {betrag} {waehrung}.',
  'buchung.bestand.veraenderung':
    '{konto} at {einheit}: change from the previous month without a movement type of its own, {betrag} {waehrung}.',
  'buchung.bestand.vormonat':
    '{konto} at {einheit}: {betrag} {waehrung} already stood in the prior month and do not enter profit or loss a second time.',
  'buchung.dividende.minderheiten':
    '{einheit}: The non-controlling share ({quote}) of the distribution, {betrag} {waehrung}, reduces their equity line.',
  'meldung.dividende.differenz':
    '{einheit} shows investment income of {ertrag} {waehrung} from {partner}; the subsidiary’s distribution ({ausschuettung} {waehrung}) times the share ({quote}) is {erwartet} {waehrung}. Difference {differenz} {waehrung} — the parent’s income was eliminated.',
  'meldung.dividende.ohne-ausschuettung':
    '{einheit} shows investment income of {ertrag} {waehrung} from {partner}, but the subsidiary shows no distribution. The income was eliminated against the reserves account of the group manual.',
  'meldung.dividende.wesentlich':
    '{einheit} shows investment income of {ertrag} {waehrung} from {partner}; the subsidiary’s distribution ({ausschuettung} {waehrung}) times the share ({quote}) is {erwartet} {waehrung}. The difference of {differenz} {waehrung} is on the difference account and above the tolerance of {grenze} {waehrung} — it is not settled.',
  'meldung.dividende.unwesentlich':
    '{einheit} shows investment income of {ertrag} {waehrung} from {partner}; the subsidiary’s distribution ({ausschuettung} {waehrung}) times the share ({quote}) is {erwartet} {waehrung}. The difference of {differenz} {waehrung} is on the difference account and below the tolerance of {grenze} {waehrung}.',
  'buchung.dividende.differenz':
    '{einheit} against {partner}: investment income and distribution times share differ by {differenz} {waehrung}; the difference is on the difference account.',
  'regel.zwischenergebniseliminierung-uv': 'Elimination of unrealised profit — current assets',
  'regel.zwischenergebniseliminierung-av': 'Elimination of unrealised profit — non-current assets',
  'regel.beteiligungsbuchwert': 'Carrying amount of the investment',
  'regel.erstkonsolidierung': 'Initial consolidation',
  'regel.unterschiedsbetrag': 'Difference on consolidation',
  'regel.neubewertung': 'Carrying forward the revaluation',
  'regel.goodwillfortschreibung': 'Carrying forward the goodwill',
  'regel.anteile-nicht-beherrschend':
    'Current profit attributable to non-controlling interests',

  /* --- The fields of the derivation (K7) --- */
  'herleitung.monate': 'Months elapsed',
  'herleitung.rate': 'Amortisation per month',
  'herleitung.nutzungsdauer': 'Useful life in months',
  'herleitung.firmenwert': 'Goodwill on acquisition',
  'herleitung.einheit': 'Entity',
  'herleitung.partner': 'Partner',
  'herleitung.konto': 'Account',
  'herleitung.waehrung': 'Currency',
  'herleitung.schicht': 'Layer',
  'herleitung.landeswaehrung': 'Local currency',
  'herleitung.erfolgswirkung': 'Effect on profit or loss',
  'herleitung.summe-umgerechnet': 'Total of translated balances',
  'herleitung.vormonat': 'Balance in the prior month',
  'herleitung.betrag-lokal': 'Amount in local currency',
  'herleitung.kursart': 'Rate type',
  'herleitung.periode': 'Month',
  'herleitung.zum-stichtag': 'At the closing rate',
  'herleitung.zum-kurs': 'At the account’s rate',
  'herleitung.quellkonto': 'Source account',
  'herleitung.rundung': 'Rounding',
  'herleitung.stand': 'Balance',
  'herleitung.bewegungen': 'Movements of the month',
  'herleitung.summe-erfolgskonten': 'Total of income and expense accounts',
  'herleitung.stichtagskurs': 'Closing rate',
  'herleitung.durchschnittskurs': 'Average rate',
  'herleitung.historischer-kurs': 'Historical rate',
  'herleitung.kurs': 'Rate',
  'herleitung.ausgangswert': 'Opening value',
  'herleitung.differenz': 'Difference',
  'herleitung.wesentlichkeitsgrenze': 'Materiality threshold',
  'herleitung.waehrungsanteil': 'Of which currency difference',
  'herleitung.lieferant': 'Supplying entity',
  'herleitung.marge': 'Margin in percent',
  'herleitung.margenquelle': 'Where the margin comes from',
  'herleitung.bestand': 'Inventory',
  'herleitung.zwischenergebnis': 'Unrealised profit',
  'herleitung.mutter': 'Parent',
  'herleitung.tochter': 'Subsidiary',
  'herleitung.buchwert': 'Carrying amount',
  'herleitung.beteiligungsbuchwert': 'Carrying amount of the investment',
  'herleitung.wert-am-erwerbstag': 'Value at acquisition date',
  'herleitung.stichtag': 'Month whose equity is offset',
  'herleitung.kurs-am-erwerbstag': 'Rate at acquisition date',
  'herleitung.quote': 'Capital share',
  'herleitung.ausschuettung': 'Distribution of the subsidiary',
  'herleitung.beteiligungsertrag': 'Investment income of the parent',
  'herleitung.anteil': 'Proportionate amount',
  'herleitung.anteiliges-eigenkapital': 'Share of equity at acquisition date',
  'herleitung.unterschiedsbetrag': 'Difference on consolidation',
  'herleitung.eigenkapital': 'Equity',
  'herleitung.fremdquote': 'Non-controlling interest',
  'herleitung.betrag': 'Amount',

  /* --- Explanations of postings --- */
  'herleitung.beginn': 'Amortised from',
  'herleitung.verbleibend': 'Remaining share in percent',
  'herleitung.quotevorher': 'Ownership interest before',
  'herleitung.quotedanach': 'Ownership interest afterwards',
  'herleitung.ausgleichsposten': 'Non-controlling interests',
  'herleitung.kaufpreis': 'Purchase price',
  'herleitung.firmenwertabgang': 'Goodwill disposed of',
  'buchung.umrechnungsdifferenz':
    'Translation of {einheit} from {landeswaehrung}: balance sheet items at the closing rate, equity at the historical rate, the income statement at the average rate. The remaining difference of {differenz} {waehrung} goes to equity without affecting profit or loss.',
  'buchung.umrechnungsdifferenz.bewegung.ohne-historisch':
    'Translation difference {einheit} ({landeswaehrung}): assets and liabilities at the closing rate {kursStichtag}.',
  'buchung.umrechnungsdifferenz.bewegung':
    'Translation difference {einheit} ({landeswaehrung}): equity at the historical rate {kursHistorisch}, assets and liabilities at the closing rate {kursStichtag}.',
  'buchung.umrechnungsdifferenz.vortrag': 'Translation difference {einheit}: prior month balance carried forward.',
  'buchung.umrechnung.anpassung.stichtag': 'Adjustment to the closing rate: {konto} at {einheit}.',
  'buchung.umrechnung.anpassung.historisch': 'Adjustment to the historical rate: {konto} at {einheit}.',
  'buchung.umrechnung.anpassung.durchschnitt': 'Adjustment to the average rate: {konto} at {einheit}.',
  'buchung.umrechnung.gegenbuchung': 'Counter-entry of the translation: {quellkonto} at {einheit}.',
  'buchung.umrechnung.gegenbuchung.vortrag': 'Counter-entry of the translation: {quellkonto} at {einheit}, prior month balance.',
  'buchung.umrechnung.gegenbuchung.ohne-quellkonto': 'Counter-entry of the translation at {einheit}: balance from before the translation voucher, split onto the source accounts.',
  'buchung.umrechnung.gegenbuchung.ohne-quellkonto.vortrag': 'Counter-entry of the translation at {einheit}: prior month balance, not yet by source account.',
  'buchung.umrechnung.umbuchung': 'Reclassification of the translation: {quellkonto} at {einheit}, from {von} to {nach}.',
  'buchung.umrechnung.rundung': 'Rounding of the translation at {einheit}.',
  'buchung.umrechnung.rundung.vortrag': 'Rounding of the translation at {einheit}, prior month balance.',
  'buchung.umrechnung.rundung.konto': 'Rounding of the translation: {konto} at {einheit}.',
  'buchung.ergebnisuebernahme.ueberschuss':
    '{einheit} reports a profit for the year of {betrag} {waehrung}; it is carried into the balance sheet.',
  'buchung.ergebnisuebernahme.fehlbetrag':
    '{einheit} reports a loss for the year of {betrag} {waehrung}; it is carried into the balance sheet.',
  'buchung.ergebnisspiegelung.minderung':
    'The step reduces the result by {betrag} {waehrung}; the result for the year in the balance sheet follows the income statement.',
  'buchung.ergebnisspiegelung.erhoehung':
    'The step increases the result by {betrag} {waehrung}; the result for the year in the balance sheet follows the income statement.',
  'buchung.ic-eliminierung':
    '{konto} at {einheit} against {partner}: {betrag} {waehrung} are, from the group’s point of view, a transaction with itself and are eliminated.',
  'buchung.ic-differenz.schuldenkonsolidierung':
    '{einheit} and {partner} do not report their receivables and payables at the same amount. The difference of {differenz} {waehrung} remains after elimination and is recognised in profit or loss.',
  'buchung.ic-differenz.schuldenkonsolidierung.waehrung':
    'Currency part of the difference between {einheit} and {partner} on receivables and payables: {differenz} {waehrung}, from translation at the closing rate.',
  'buchung.ic-differenz.aufwandertragkonsolidierung.waehrung':
    'Currency part of the difference between {einheit} and {partner} on intercompany income and expense: {differenz} {waehrung}, from translation.',
  'buchung.ic-differenz.aufwandertragkonsolidierung':
    '{einheit} and {partner} do not report their intercompany income and expense at the same amount. The difference of {differenz} {waehrung} remains after elimination and is recognised in profit or loss.',
  'buchung.zwischenergebnis':
    'The inventory {konto} of {einheit} contains {betrag} {waehrung} of profit from a delivery by {lieferant}. From the group’s point of view it is unrealised as long as the goods have not left the group.',
  'buchung.zwischenergebnis.gegenbuchung':
    'Contra entry for the elimination of unrealised profit in the inventory of {einheit}.',
  'buchung.zwischengewinn.andere':
    '{tochter}: {quote} percent of the unrealised profit on its deliveries is borne by the other shareholders — their non-controlling interest falls by {betrag} {waehrung}.',
  'buchung.zwischengewinn.andere.vortrag':
    '{tochter}: Share of the other shareholders in the unrealised profit, brought forward from the previous month, {betrag} {waehrung}.',
  'buchung.zwischengewinn.andere.ergebnis':
    '{tochter}: The profit share of the other shareholders falls by their share of the unrealised profit, {betrag} {waehrung}.',
  'buchung.zwischengewinn.marge':
    '{konto} at {einheit}: The inventory supplied by {lieferant} contains {betrag} {waehrung} unrealised profit at a margin of {marge} percent — eliminated.',
  'buchung.beteiligungsbuchwert':
    'The carrying amount of {betrag} {waehrung} that {mutter} holds for {tochter} is offset against the subsidiary’s share of equity.',
  'buchung.erstkonsolidierung':
    '{konto} of {tochter} stood at {betrag} {waehrung} on the acquisition date. Of that, {quote} per cent is attributable to {mutter} and is offset against the carrying amount of the investment.',
  'buchung.unterschiedsbetrag.firmenwert':
    '{mutter} paid {buchwert} {waehrung} for {tochter} and received a share of equity of {eigenkapital} {waehrung}. The excess of {unterschied} {waehrung} is goodwill.',
  'buchung.unterschiedsbetrag.passivisch':
    '{mutter} paid less for {tochter} than the share of equity was worth. The negative difference on consolidation is {unterschied} {waehrung} and is shown as a separate item after equity.',
  'buchung.unterschiedsbetrag.ertrag':
    '{mutter} paid less for {tochter} than the share of equity was worth. Under IFRS the difference of {unterschied} {waehrung} is income in the month of acquisition.',
  'buchung.unterschiedsbetrag.ertrag-vortrag':
    'The gain on acquiring {tochter} — {unterschied} {waehrung} — arose in an earlier month of this financial year and is shown as result carried forward within the result for the year.',
  'buchung.unterschiedsbetrag.ertrag-ruecklage':
    'The gain on acquiring {tochter} — {unterschied} {waehrung} — arose in an earlier financial year and is shown in retained earnings.',
  'buchung.neubewertung':
    'On acquiring {tochter}, hidden reserves of {betrag} {waehrung} were recognised in {konto}. They are an asset of the group, not of the subsidiary.',
  'buchung.neubewertung.kumuliert':
    'By the start of the financial year, {betrag} {waehrung} had been written off the hidden reserve in {konto} of {tochter}.',
  'buchung.neubewertung.abschreibung':
    'The hidden reserve in {konto} of {tochter} is amortised on schedule: {betrag} {waehrung} this period.',
  'buchung.neubewertung.aufwand':
    'Contra entry: the amortisation of the hidden reserve at {tochter} reduces the group result by {betrag} {waehrung}.',
  'buchung.fortschreibung.ruecklage':
    'Contra entry: {betrag} {waehrung} relate to earlier financial years and sit in retained earnings, not in the result of this year.',
  'buchung.fortschreibung.ergebnisvortrag':
    'Contra entry: {betrag} {waehrung} relate to {monate} earlier months of this financial year and sit in the result of the year as brought forward.',
  'buchung.goodwill.vortrag':
    '{konto} at {einheit}: goodwill and accumulated amortisation of the previous month are carried forward with {betrag} {waehrung}.',
  'buchung.kapitalkonsolidierung.vortrag':
    '{konto} at {einheit}: the capital consolidation of the previous month is carried forward with {betrag} {waehrung}.',
  'buchung.aufsatz.eigenkapital':
    '{konto} at {tochter}: equity at the start of the takeover month, {betrag} {waehrung}, is eliminated.',
  'buchung.aufsatz.firmenwert': 'Goodwill from acquiring {tochter}, remaining amount at takeover: {betrag} {waehrung}.',
  'buchung.aufsatz.ausgleichsposten': 'Non-controlling interests in {tochter} at takeover: {betrag} {waehrung}.',
  'buchung.erstkonsolidierung.erwirtschaftet':
    'Since acquisition, {tochter} has earned {betrag} {waehrung} for the group on {konto}. The amount leaves the account …',
  'buchung.erstkonsolidierung.erwirtschaftet.vortrag':
    '… and stands in the group’s retained earnings brought forward: what the group has earned on {tochter} since acquisition, {betrag} {waehrung}.',
  'buchung.aufsatz.ruecklagen':
    'What the group has earned on {tochter} since acquisition, less goodwill amortised so far: {betrag} {waehrung} to retained earnings.',
  'meldung.aufsatz.staende':
    '{tochter} entered the group with balances at takeover: goodwill {firmenwert} {waehrung}, non-controlling interests {ausgleichsposten} {waehrung}.',
  'meldung.kapitalkonsolidierung.manuell': '{tochter} is consolidated manually. The process books nothing for it.',
  'meldung.kapitalkonsolidierung.vor-aufsatz':
    '{tochter} is taken over only from {monat}. Until then the process does not consolidate it.',
  'buchung.goodwill.kumuliert':
    'By the start of the financial year, {betrag} {waehrung} had been written off the goodwill from acquiring {tochter}.',
  'buchung.goodwill.abschreibung':
    'The goodwill from acquiring {tochter} is amortised over {monate} months: {betrag} {waehrung} this period.',
  'buchung.goodwill.aufwand':
    'Contra entry: the amortisation of the goodwill of {tochter} reduces the group result by {betrag} {waehrung}.',
  'buchung.goodwill.zukauf':
    'The goodwill from the additional acquisition of {tochter} in {beginn} is amortised over {monate} months: {betrag} {waehrung} this period.',
  'buchung.kapitalkonsolidierung.ruecknahme':
    '{konto}: The parent’s write-down or write-up of its investment in {tochter}, {betrag} {waehrung}, is reversed in the group.',
  'buchung.kapitalkonsolidierung.ruecknahme-ergebnis':
    'Contra entry on {konto}: In the group, the net assets of {tochter} replace the investment; the write-down or write-up of {betrag} {waehrung} does not apply.',
  'buchung.zukauf.beteiligung':
    '{konto}: The purchase price for further shares in {tochter}, {betrag} {waehrung}, is offset against non-controlling interests and goodwill.',
  'buchung.zukauf.ausgleichsposten':
    'The parent takes over the acquired share of the non-controlling interests in {tochter}: {betrag} {waehrung}.',
  'buchung.zukauf.firmenwert': 'Further goodwill from the additional acquisition of {tochter}: {betrag} {waehrung} — the purchase price above the share taken over.',
  'buchung.teilverkauf.beteiligung':
    '{konto}: The disposal of shares in {tochter}, {betrag} {waehrung}, is reversed in the group — what was sold is net assets and goodwill, not the investment.',
  'buchung.teilverkauf.ausgleichsposten':
    'The non-controlling interests in {tochter} rise by the share of equity sold: {betrag} {waehrung}.',
  'buchung.teilverkauf.firmenwert': 'The share of goodwill from {tochter} that was sold is derecognised: {betrag} {waehrung} at cost.',
  'buchung.teilverkauf.firmenwert-abschreibung': 'With it, the accumulated amortisation of the goodwill from {tochter} is derecognised: {betrag} {waehrung}.',
  'buchung.teilverkauf.ergebnis-mehr':
    '{konto}: The group’s gain from the partial disposal of {tochter} is {betrag} {waehrung} above the parent’s gain.',
  'buchung.teilverkauf.ergebnis-weniger':
    '{konto}: The group’s gain from the partial disposal of {tochter} is {betrag} {waehrung} below the parent’s gain.',
  'buchung.nicht-beherrschend.konto':
    'From the initial consolidation: of {konto} at {tochter}, {quote} per cent is attributable to shareholders outside the group and is reported separately.',
  'buchung.nicht-beherrschend.summe':
    'From the initial consolidation: {quote} per cent of the equity of {tochter} — {betrag} {waehrung} — does not belong to the group and is reported separately within equity.',
  'buchung.nicht-beherrschend.ergebnis':
    'Current profit: of {konto} at {tochter}, {quote} per cent is attributable to shareholders outside the group.',
  'buchung.nicht-beherrschend.ergebnis-summe':
    '{quote} per cent of the profit of this month of {tochter} — {betrag} {waehrung} — does not belong to the group and is reported separately within equity.',
  'buchung.nicht-beherrschend.ergebnis-vormonate':
    '{quote} per cent of the profit of prior months of {tochter} — {betrag} {waehrung} — does not belong to the group and is reported separately within equity.',
  'buchung.nicht-beherrschend.umrechnung':
    'Currency translation reserve: of {konto} at {tochter}, {quote} per cent is attributable to shareholders outside the group — with the same movement type as the reserve itself.',
  'buchung.nicht-beherrschend.umrechnung-summe':
    '{quote} per cent of the currency translation reserve of {tochter} — {betrag} {waehrung} — does not belong to the group and is reported separately within equity.',
  'buchung.nicht-beherrschend.bewegung':
    'Of the change in {konto} at {tochter}, {quote} per cent is attributable to shareholders outside the group — with the same movement type as at the subsidiary.',
  'buchung.kapitalerhoehung.monat':
    'Capital increase: of {konto} at {tochter}, {quote} per cent belongs to the parent — {betrag} {waehrung}, offset against its payment.',
  'buchung.kapitalerhoehung.frueher':
    'Capital increase of an earlier month: of {konto} at {tochter}, {quote} per cent belongs to the parent — {betrag} {waehrung}, offset against its payment.',
  'buchung.kapitalerhoehung.einzahlung':
    'The parent’s payment on the capital increase of {tochter} — {betrag} {waehrung} — is offset against its share of the increase; no goodwill arises.',
  'buchung.kapitalerhoehung.rest':
    'What the parent paid on earlier capital increases of {tochter} beyond or below its share — {betrag} {waehrung} — remains as an investment.',
  'buchung.nicht-beherrschend.bewegung-summe':
    '{quote} per cent of this change in the equity of {tochter} — {betrag} {waehrung} — does not belong to the group and is reported separately within equity.',

  /* --- Notes from the steps --- */
  'meldung.salden-gehen-nicht-auf':
    'The balances of {einheit} do not agree by {differenz} {waehrung}. The entity reports in the group currency, so a currency effect is not a possible explanation.',
  'meldung.umrechnungsdifferenz':
    '{einheit}: translation difference {differenz} {waehrung} to the currency translation reserve.',
  'meldung.summierung': "The entities are summed; each entity's result stands in the balance sheet.",
  'meldung.ic-differenz.wesentlich':
    'MATERIAL: {einheit} against {partner} — difference {differenz} {waehrung}. The threshold of {grenze} {waehrung} is exceeded; the release checks the difference as not settled.',
  'meldung.ic-differenz.unwesentlich':
    '{einheit} against {partner} — difference {differenz} {waehrung}, below the materiality threshold and recognised in profit or loss.',
  'meldung.ic-differenz.nur-waehrung':
    '{einheit} against {partner} — only a currency difference of {waehrungsanteil} {waehrung}; it is posted separately and blocks nothing.',
  'meldung.ic-konto-nicht-abgegrenzt':
    'These accounts carry intercompany balances but appear in no position of this process, and are therefore left standing: {konten}.',
  'meldung.ic-position-ohne-konten':
    'No intercompany account is assigned to these positions; the process covers less than was selected: {positionen}.',
  'meldung.handbuch-uebersteuert':
    'Process “{werkzeug}” sets {angabe} to {wert}, departing from the group manual.',
  'meldung.ic-differenzkonto-uneinig':
    'Several processes of type “{werkzeug}” name different difference accounts ({konten}). {gewaehlt} is used — the account of the process with the lowest rank.',
  'meldung.ic-differenzkonto-unbekannt':
    'The difference account {konto} does not exist. {ersatz} from the rule set is used instead.',
  'meldung.zwischenergebnis':
    '{einheit}: {betrag} {waehrung} of unrealised profit from deliveries by {lieferant} eliminated.',
  'meldung.zwischenergebnis.keine-uv': 'No unrealised profit reported in current assets.',
  'meldung.zwischenergebnis.keine-av': 'No unrealised profit reported in non-current assets.',
  'meldung.einbeziehungsart.quotal': '{einheit} is marked as “proportionate”. So far CoKo performs full consolidation only; the entity is left unconsolidated.',
  'meldung.einbeziehungsart.equity': '{einheit} is marked as “at equity”. So far CoKo performs full consolidation only; the entity is left unconsolidated.',
  'meldung.einbeziehungsart.nicht-einbezogen': '{einheit} is marked as “not included”. So far CoKo performs full consolidation only; the entity is left unconsolidated.',
  'meldung.keine-beteiligung': 'The group contains no investment that would need consolidating.',
  'meldung.kein-beteiligungsbuchwert':
    '{mutter} carries no investment in {tochter}. Without a carrying amount no difference on consolidation can be determined; capital consolidation is omitted for this investment.',
  'meldung.fortgeschrieben':
    '{gesellschaft} delivered nothing for this month. The balance from {aus} has been carried forward.',
  'meldung.ohne-lieferung':
    '{gesellschaft} delivered nothing for this month, and there is no balance in the financial year to carry forward. Last delivery: {aus}. The company is included at zero.',
  'meldung.nie-geliefert': '{gesellschaft} has never delivered and is included at zero.',
  'meldung.nicht-faellig':
    '{gesellschaft}: delivery for this month not due ({rhythmus}). It is included at zero, without a finding.',
  'meldung.zum-ende.mitgliedschaft':
    'According to the shareholding, {tochter} is included at the end of {erwerb}, but its membership of the consolidation group does not begin in that month. Please align the shareholding and the consolidation group.',
  'meldung.goodwill-keine-abschreibung':
    'Under this accounting framework goodwill is not amortised on schedule (IFRS 3.B63a). Whether it is impaired is decided by the impairment test — that requires a recoverable amount and is therefore an entry, not a calculation. It cannot be entered yet.',
  'meldung.firmenwert': '{tochter}: goodwill {betrag} {waehrung}.',
  'meldung.zwischenergebnis.ohne-schritt':
    '{einheit}: The reported unrealised profit of {betrag} {waehrung} on {konto}, supplied by {lieferant}, is not under the process for non-current assets and is not eliminated. For current assets the group margin applies, not the report.',
  'meldung.zwischengewinn.ohne-positionen': 'The process elimination of unrealised profit — current assets names no inventories and eliminates nothing.',
  'abbruch.zwischengewinn.marge-fehlt.meldung':
    '{einheit} carries inventory with partner code {lieferant} on {konto}, and the process names no margin for it.',
  'abbruch.zwischengewinn.marge-fehlt.weg':
    'Enter a standard margin at the process elimination of unrealised profit — current assets, or a margin for this supply relationship.',
  'meldung.kapitalerhoehung.quote':
    '{tochter}: the parent paid {einzahlung} {waehrung} on capital increases, its share of the increases is {anteil} {waehrung}; {rest} {waehrung} remain as an investment.',
  'meldung.zukauf-von-anteilen':
    '{tochter}: addition to the investment of {betrag} {waehrung} without a capital increase of the subsidiary — treated as a purchase of shares.',
  'meldung.unterschiedsbetrag-passivisch':
    '{tochter}: negative difference on consolidation {betrag} {waehrung}.',
  'meldung.nicht-beherrschend':
    '{tochter}: non-controlling interests {betrag} {waehrung} ({quote} per cent).',
  'meldung.konto-ohne-position':
    'Account {konto} carries an amount but is not assigned to any line item in period {periode}. The amount appears in no report.',
  'meldung.bilanz-geht-auf':
    'The consolidated balance sheet balances: assets and liabilities agree to the cent.',
  'meldung.bilanz-geht-nicht-auf':
    'ATTENTION: the consolidated balance sheet does not balance, by {differenz} {waehrung}.',

  /* --- Notes on the run --- */
  'hinweis.konto-mehrdeutig':
    'Account {konto} is assigned to more than one line item in period {periode}. The assignment periods overlap. The account stays out of the calculation until that is settled.',
  'hinweis.konto-unbekannt':
    'Balances exist for account {konto}, but the account is not in the group chart of accounts.',

  /* --- Aborts: what is · how much it is · what to do --- */
  'abbruch.kreis-ohne-einheit.meldung':
    'No entity belongs to the consolidation group in period {periode}.',
  'abbruch.kreis-ohne-einheit.weg':
    'Check under Group structure → Consolidation groups from which period the entities are assigned to the group.',
  'abbruch.keine-salden.meldung': 'No balances exist for period {periode}.',
  'abbruch.keine-salden.weg':
    'Submit the balances of the entities first, by import or by entry.',
  'abbruch.kurs-fehlt.stichtag':
    'The closing rate from {waehrung} to {konzernwaehrung} is missing for period {periode}.',
  'abbruch.kurs-fehlt.durchschnitt':
    'The average rate from {waehrung} to {konzernwaehrung} is missing for period {periode}.',
  'abbruch.kurs-fehlt.historisch':
    'The historical rate from {waehrung} to {konzernwaehrung} is missing for period {periode}.',
  'abbruch.kurs-fehlt.weg':
    'Enter the rate under Master data → Exchange rates and start the run again.',
  'abbruch.erstkonsolidierung-fehlt.meldung':
    'The initial consolidation values are missing for the investment of {mutter} in {tochter}.',
  'abbruch.erstkonsolidierung-fehlt.weg':
    'Enter under Group structure → Shareholding register how large the subsidiary’s equity was on the acquisition date. Without it the difference on consolidation cannot be determined.',
  'abbruch.kreis-unbekannt.meldung': 'There is no consolidation group “{kreis}”.',
  'abbruch.kreis-unbekannt.weg':
    'Choose a group from the list under Group structure → Consolidation groups.',
  'abbruch.ledger-unbekannt.meldung': 'There is no measurement layer “{ledger}”.',
  'abbruch.ledger-unbekannt.weg': 'Choose a measurement layer from the list under Master data → Measurement layers.',
  'abbruch.jahreswechsel-ohne-dezember.meldung':
    'For {einheit} there is a year-end transfer in foreign currency in {periode}, but no December run of the prior year to take the euro value of the prior-year result from.',
  'abbruch.jahreswechsel-ohne-dezember.weg':
    'The prior-year result is transferred on the result account and the reserves at the value shown in the December group balance sheet — it is not guessed. Calculate the prior-year December for this scope of consolidation first.',
  'abbruch.kapitalerhoehung.kurs.meldung':
    'For the capital increase of {einheit} on {konto} in {periode} the row in the table of historical rates is missing.',
  'abbruch.kapitalerhoehung.kurs.weg':
    'Enter the capital increase with its rate in the company’s table of historical rates and recalculate.',
  'abbruch.unterschiedsbetrag-konto.meldung':
    'A negative difference on consolidation arises for {tochter}, but {konto} is neither a liabilities-side account nor an income account.',
  'abbruch.unterschiedsbetrag-konto.weg':
    'In the group manual, enter a liabilities-side account (HGB) or an income account (IFRS) for the negative difference on consolidation — or at the capital consolidation step if it overrides the account.',
  'abbruch.unterschiedsbetrag-kontoart.hgb.meldung':
    'The negative difference on consolidation is to be posted to {konto}. In a valuation layer under {rechnungslegung} it is shown after equity and needs a liabilities-side account.',
  'abbruch.unterschiedsbetrag-kontoart.ifrs.meldung':
    'The negative difference on consolidation is to be posted to {konto}. In a valuation layer under {rechnungslegung} it is income and needs an income account.',
  'abbruch.unterschiedsbetrag-kontoart.weg':
    'In the group manual, choose an account of the matching kind for the negative difference on consolidation — or at the capital consolidation step if it overrides the account.',
  'abbruch.kein-eigenkapital.meldung':
    'No equity is recorded for {tochter} in {stichtag} in the valuation layer of this statement.',
  'abbruch.kein-eigenkapital.weg':
    'The investment book value is offset against this equity. Check whether figures for that month were delivered — and whether the inclusion date in the investment register is right (beginning of the period means the previous month counts).',
  'abbruch.kapitalkonsolidierung.vormonat-fehlt.meldung':
    'For {tochter} the run of {vormonat} is missing. Capital consolidation carries the previous month forward and does not recompute it.',
  'abbruch.kapitalkonsolidierung.vormonat-fehlt.weg':
    'Calculate {vormonat} first. If the subsidiary was acquired before its first month in CoKo, record the takeover in the investment register.',
  'abbruch.bestand.vormonat-fehlt.meldung':
    'The run for {vormonat} is missing for the {schritt}. It carries its balance sheet side forward from the previous month instead of recalculating it.',
  'abbruch.bestand.vormonat-fehlt.weg': 'Calculate {vormonat} first.',
  'abbruch.kapitalkonsolidierung.buchwert-bewegt.meldung':
    'The carrying amount of the investment of {mutter} in {tochter} moved by {betrag} {waehrung} this month with a movement type CoKo does not assign to any event.',
  'abbruch.kapitalkonsolidierung.buchwert-bewegt.weg':
    'An addition is an additional acquisition, a disposal a partial disposal, a write-down or write-up is reversed. Other movement types — and a disposal or write-down in the month of a capital increase — are not yet calculated. Check the parent’s delivery or consolidate the subsidiary manually.',
  'abbruch.aufsatz.angabe-fehlt.meldung': 'For the takeover of {tochter} the entry “{angabe}” is missing.',
  'abbruch.aufsatz.angabe-fehlt.weg': 'Enter it for the investment in the investment register and calculate again.',
  'abbruch.kapitaltransaktion.vor-dem-start.meldung':
    'The investment register shows a capital transaction of {mutter} in {tochter} in {transaktion} — not after the first month in CoKo, {start}.',
  'abbruch.kapitaltransaktion.vor-dem-start.weg':
    'Up to the acquisition or the starting point, every transaction is part of the balance, and the ownership interest of the investment applies at that point. Delete the entry or correct the month.',
  'abbruch.kapitaltransaktion.erhoehung-ohne-eintrag.meldung':
    '{tochter} delivers a capital increase of {betrag} {waehrung} in {monat}; the investment register shows none.',
  'abbruch.kapitaltransaktion.erhoehung-ohne-eintrag.weg':
    'Enter it for the investment under capital transactions: month, timing and ownership interest afterwards — for an increase in proportion to the shares, the previous one.',
  'abbruch.kapitaltransaktion.zugang-ohne-eintrag.meldung':
    '{mutter} delivers an addition of {betrag} {waehrung} to its investment in {tochter} in {monat}; the investment register shows no additional acquisition.',
  'abbruch.kapitaltransaktion.zugang-ohne-eintrag.weg':
    'Enter the additional acquisition for the investment under capital transactions — or correct the parent’s delivery if the addition belongs to another month.',
  'abbruch.kapitaltransaktion.abgang-ohne-eintrag.meldung':
    '{mutter} delivers a disposal of {betrag} {waehrung} from its investment in {tochter} in {monat}; the investment register shows no partial disposal.',
  'abbruch.kapitaltransaktion.abgang-ohne-eintrag.weg':
    'Enter the partial disposal for the investment under capital transactions — or correct the parent’s delivery if the disposal belongs to another month.',
  'abbruch.kapitaltransaktion.eintrag-ohne-bewegung.meldung':
    'The investment register shows an entry “{art}” for {tochter} in {monat}; the delivery shows no movement for it.',
  'abbruch.kapitaltransaktion.eintrag-ohne-bewegung.weg':
    'CoKo takes the amount from the delivery: the addition to the parent’s investment for an additional acquisition, the disposal for a partial disposal, the increase at the subsidiary for a capital increase. Check the delivery and the month of the entry.',
  'abbruch.kapitaltransaktion.erhoehung-quote.meldung':
    'The capital increase of {tochter} in {monat} changes the ownership interest from {vorher} to {danach} percent.',
  'abbruch.kapitaltransaktion.erhoehung-quote.weg':
    'A capital increase that changes the ownership interest is not yet calculated. Consolidate the subsidiary manually until then.',
  'abbruch.kapitaltransaktion.quote.meldung':
    'The entry “{art}” for {tochter} in {monat} takes the ownership interest from {vorher} to {danach} percent.',
  'abbruch.kapitaltransaktion.quote.weg':
    'An additional acquisition raises the ownership interest, a partial disposal lowers it. Correct the type or the ownership interest afterwards.',
  'abbruch.kapitaltransaktion.zukauf-passiv.meldung':
    'The purchase price for further shares in {tochter}, {kaufpreis} {waehrung}, is below the share of non-controlling interests taken over, {anteil} {waehrung}.',
  'abbruch.kapitaltransaktion.zukauf-passiv.weg':
    'A negative difference from an additional acquisition is not yet calculated. Consolidate the subsidiary manually until then.',
  'abbruch.kapitalkonsolidierung.ohne-positionen.meldung':
    'The capital consolidation has no positions chosen for investments or equity.',
  'abbruch.kapitalkonsolidierung.ohne-positionen.weg':
    'Choose the positions of the investments and of equity at the capital consolidation process under [[Group statements]]. Then recalculate.',
  'abbruch.differenzkonto-fehlt.meldung':
    'No difference account is assigned to process “{werkzeug}”.',
  'abbruch.differenzkonto-fehlt.weg':
    'Enter it under [[Group statements]] at the process, button “Scope”. That is where the process posts whatever remains after both sides are eliminated — without this account it would not know where to put it.',
  'abbruch.differenzkonto-unbekannt.meldung':
    'The difference account {konto} of process “{werkzeug}” does not exist.',
  'abbruch.differenzkonto-waehrung-fehlt.meldung':
    'The process “{werkzeug}” has no difference account for currency differences.',
  'abbruch.differenzkonto-waehrung-fehlt.weg':
    'Enter it under [[Group statements]] at the process, button “Scope”. It may be the same as the difference account.',
  'abbruch.differenzkonto-waehrung-unbekannt.meldung':
    'The difference account for currency differences {konto} of the process “{werkzeug}” does not exist.',
  'abbruch.differenzkonto-unbekannt.weg':
    'Choose an existing account under Group close → Consolidation steps. It was probably renamed or retired after the process was set up.',
  'abbruch.keine-regelwerksausgabe.meldung':
    'No rule version is recorded for period {periode}.',
  'abbruch.keine-regelwerksausgabe.weg':
    'Without a version no close can be computed, because it is part of the evidence. Create a version under Rule set.',
  'abbruch.kein-regelwerkskapitel.meldung':
    'Rule version {ausgabe} has no chapter for {rechnungslegung}.',
  'abbruch.kein-regelwerkskapitel.weg':
    'Every accounting framework to be computed needs its own settings in the applicable version — rounding, tolerances, clearing accounts.',
  'abbruch.lauf-unbekannt.meldung': 'This run no longer exists.',
  'abbruch.lauf-unbekannt.weg': 'Choose a run from the list of computed closes.',
  'abbruch.berichtsart.meldung': 'There is no report type “{art}”.',
  'abbruch.berichtsart.weg': 'Balance sheet and income statement are allowed.',
  'abbruch.achse.meldung': 'There is no breakdown axis "{achse}".',

  /* --- Findings of the release check --- */
  'befund.bereits-eingefroren.meldung': 'The close is already “{status}” and therefore final.',
  'befund.bereits-eingefroren.weg':
    'A later correction is a retrospective adjustment as a second run on the same period, not the withdrawal of the release.',
  'befund.veraltet-regelwerk.meldung': 'A new edition of the group manual now applies to “{abschluss}”.',
  'befund.veraltet-regelwerk.weg':
    'Go to [[Group statements]] and recalculate the group statement. This message then disappears.',
  'befund.veraltet-daten.meldung':
    'Individual statement values of at least one included company are more recent than the data of “{abschluss}”.',
  'befund.veraltet-daten.weg':
    'Go to [[Group statements]] and recalculate the group statement. This message then disappears.',
  'befund.veraltet-vorperiode.meldung':
    'The prior month of “{abschluss}” has been recalculated; the opening balances of this month are therefore no longer current.',
  'befund.veraltet-frueherer-monat.meldung':
    '{periode} has new data since it was calculated; the carry-forwards of this month are no longer current.',
  'befund.veraltet-frueherer-monat.weg':
    'Go to [[Group statements]] and recalculate month by month from that month. This message then disappears.',
  'befund.veraltet-vorperiode.weg':
    'Go to [[Group statements]] and recalculate the group statement. This message then disappears.',
  'befund.bilanz-geht-nicht-auf.meldung':
    'The consolidated balance sheet of “{abschluss}” does not balance: assets and liabilities do not agree.',
  'befund.bilanz-geht-nicht-auf.weg':
    'Open the [[Consolidated balance sheet]] and trace where the difference comes from. Once it balances, this message disappears.',
  'befund.anhang-unvollstaendig.meldung':
    '{anzahl} mandatory notes disclosures at the fiscal year end are missing, outdated or come from a different calculation.',
  'befund.anhang-unvollstaendig.meldung.eins':
    'One mandatory notes disclosure at the fiscal year end is missing, outdated or comes from a different calculation.',
  'befund.anhang-unvollstaendig.weg':
    'Under [[Completeness]], create each disclosure or explicitly mark it as not applicable. With the last disclosure, this message disappears.',
  'befund.anhang-wahlrechte.meldung':
    '{anzahl} optional notes disclosures are unanswered or not yet translated.',
  'befund.anhang-wahlrechte.meldung.eins':
    'One optional notes disclosure is unanswered or not yet translated.',
  'befund.anhang-wahlrechte.weg':
    'Answer them in the [[Notes book]] or waive them with a reason. They do not block the release.',
  'befund.ic-ohne-partner.meldung':
    '{anzahl} amounts on intercompany accounts have no partner; debt consolidation does not process them.',
  'befund.ic-ohne-partner.meldung.eins':
    'One amount on an intercompany account has no partner; debt consolidation does not process it.',
  'befund.ic-ohne-partner.weg':
    'Add the partner with the reporting package or a manual entry. The IC reconciliation under [[Group statements]] lists every amount under “Without partner”. With the last partner, this message disappears.',
  'befund.beteiligung-ohne-partner.meldung':
    '{anzahl} investments have no company held; capital consolidation is skipped for them.',
  'befund.beteiligung-ohne-partner.meldung.eins':
    'One investment has no company held; capital consolidation is skipped for it.',
  'befund.beteiligung-ohne-partner.ohnebuchwert':
    '{anzahl} investments without carrying amount: {mutter} carries no investment in {tochter}. Capital consolidation is skipped for them.',
  'befund.beteiligung-ohne-partner.ohnebuchwert.eins':
    'One investment without carrying amount: {mutter} carries no investment in {tochter}. Capital consolidation is skipped for it.',
  'befund.beteiligung-ohne-partner.weg':
    'Add the company held as partner — with the reporting package or a manual entry. The IC reconciliation under [[Group statements]] lists the amounts under “Without partner”.',
  'befund.ohne-lieferung.meldung':
    '{anzahl} companies have not delivered for this month although the delivery was due: {gesellschaften}.',
  'befund.ohne-lieferung.meldung.eins':
    '{gesellschaften} has not delivered for this month although the delivery was due.',
  'befund.ohne-lieferung.luecke.meldung':
    '{anzahl} due deliveries are missing since the start of the financial year, the first from {gesellschaft} for {monat}: {gesellschaften}.',
  'befund.ohne-lieferung.luecke.meldung.eins': 'Delivery missing: {gesellschaft}, {monat}.',
  'befund.ohne-lieferung.luecke.eine.meldung':
    '{gesellschaft}: {anzahl} due deliveries are missing since the start of the financial year, the first for {monat}.',
  'befund.ohne-lieferung.begruendet.meldung':
    '{anzahl} missing deliveries were justified at an earlier release, the first from {gesellschaft} for {monat}: “{begruendung}” ({anwender}, {am}).',
  'befund.ohne-lieferung.begruendet.meldung.eins':
    'Delivery missing: {gesellschaft}, {monat} — justified by {anwender} on {am}: “{begruendung}”',
  'befund.ohne-lieferung.weg':
    'Import their delivery under [[Data imports]] and recalculate the group statement. This message then disappears.',
  'befund.kfr-umgliederung-nicht-null.meldung':
    '{anzahl} reclassifications within equity do not net to zero.',
  'befund.kfr-umgliederung-nicht-null.meldung.eins':
    'One reclassification within equity does not net to zero.',
  'befund.kfr-umgliederung-nicht-null.weg':
    'In the [[Statement of cash flows]], open the reconciliation with the movement balance: the column “Reclassification within equity” shows both sides. The missing side usually carries a different movement type; once it is corrected, this message disappears.',
  'befund.eintrittskurs-nachtragen.meldung':
    'The entry rate is missing for {anzahl} companies: {gesellschaften}. Until then, “{abschluss}” calculates with the closing rate of the entry month.',
  'befund.eintrittskurs-nachtragen.meldung.eins':
    'The entry rate is missing for {gesellschaften}. Until then, “{abschluss}” calculates with the closing rate of the entry month.',
  'befund.ergebnisjahr-im-eigenkapital.meldung':
    'The {jahr} result of {gesellschaft} stands in equity at a different rate than it was booked out of the net income.',
  'befund.ergebnisjahr-im-eigenkapital.weg':
    'Under [[Historical rates]], check the lines of the result year: their rate is that of the net income. Once it is right, this message disappears.',
  'befund.zwischenergebnis-ohne-schritt.meldung':
    '{anzahl} intercompany profits are not assigned to a step and therefore not eliminated, among them at {einheit}.',
  'befund.zwischenergebnis-ohne-schritt.meldung.eins':
    'One intercompany profit of {einheit} is not assigned to a step and therefore not eliminated.',
  'befund.zwischenergebnis-ohne-schritt.weg':
    'Under [[Group statements]], add the account’s item to the process “intercompany profit elimination, current assets” or “fixed assets” and recalculate the group statement. This message then disappears.',
  'befund.einzahlung-passt-nicht-zur-quote.meldung':
    'The payment into {tochter} does not match the equity interest: {rest} {waehrung} remain as an investment.',
  'befund.einzahlung-passt-nicht-zur-quote.weg':
    'Usually the equity interest has changed: enter it in the [[Shareholding register]] and recalculate the group statement. If it is right, reconcile the deliveries of parent and subsidiary.',
  'befund.zukauf-von-anteilen.meldung':
    'The investment in {tochter} shows an addition of {betrag} {waehrung} without a capital increase; it is treated as an acquisition with goodwill.',
  'befund.zukauf-von-anteilen.weg':
    'If it was a capital increase, the subsidiary delivers it with the movement type “capital increase”. If it was a purchase, enter the new equity interest in the [[Shareholding register]] and justify it at release.',
  'befund.ic-differenz-nicht-geklaert.meldung':
    '{anzahl} intercompany differences are not settled, among them {gesellschaft} against {partner}.',
  'befund.ic-differenz-nicht-geklaert.meldung.eins':
    'The intercompany difference {gesellschaft} against {partner} is not settled.',
  'befund.ic-differenz-nicht-geklaert.weg':
    'Reconcile the deliveries of both companies; the IC reconciliation under [[Group statements]] shows every difference. If a difference is justified, set the check to “Warning” under “Define check rules” and give the reason at release.',
  'befund.eintrittskurs-nachtragen.weg':
    'Under [[Consolidation groups]], enter the entry rate for the members or take the closing rate, and recalculate the group statement. This message then disappears.',
  'befund.kurse-aktualisieren.meldung':
    '{anzahl} rates of this month were not determined for it; “{abschluss}” calculates with them.',
  'befund.kurse-aktualisieren.meldung.eins':
    'One rate of this month was not determined for it; “{abschluss}” calculates with it.',
  'befund.kurse-aktualisieren.weg':
    'Under [[Exchange rates]], enter the rate again after month end or fetch it from the central bank, and recalculate the group statement. This message then disappears.',
  'kurs.befund.fortgeschrieben':
    'The rate {waehrung} ({kursart}) for {periode} is taken over from another month.',
  'kurs.befund.gerechnet':
    'The rate {waehrung} ({kursart}) for {periode} is calculated from other rates and was not determined for this month.',
  'kurs.befund.frueh-manuell':
    'The rate {waehrung} ({kursart}) for {periode} was entered by hand on {datum}, before month end.',
  'kurs.befund.frueh-datei':
    'The rate {waehrung} ({kursart}) for {periode} comes from a rate file read on {datum}, before month end.',
  'kurs.befund.nicht-letzter-handelstag':
    'The rate {waehrung} ({kursart}) for {periode} is dated {datum}, not the last trading day of the month.',
  'kurs.befund.anderer-monat': 'The rate {waehrung} ({kursart}) for {periode} comes from {quelle}.',
  'befund.kfr-geht-nicht-auf.meldung':
    'The cash flow statement of “{abschluss}” does not balance: an amount cannot be derived from movements.',
  'befund.kfr-geht-nicht-auf.weg':
    'In the [[Statement of cash flows]], open the reconciliation with the movement balance: the column “Difference” names the account. Usually the movement type lacks an assignment rule; once it is added, this message disappears.',
  'befund.spiegel.nichtauf': '{spiegel} does not reconcile on {anzahl} accounts, among them at {einheit}.',
  'befund.spiegel.nichtauf.eins': '{spiegel} does not reconcile on one account of {einheit}.',
  'befund.spiegel.offen':
    '{spiegel} is incomplete on {anzahl} accounts — amounts are “unassigned”, among them at {einheit}.',
  'befund.spiegel.offen.eins':
    '{spiegel} is incomplete on one account of {einheit} — an amount is “unassigned”.',
  'befund.spiegel.beides':
    '{spiegel} does not reconcile on {nichtauf} accounts and is incomplete on {offen} accounts, among them at {einheit}.',
  'befund.spiegel.weg':
    'Deliver or assign the missing movement types; [[{seite}]] shows the accounts affected. Then this message disappears. If you do not need the schedule, set it to “Do not check” under “Define check rules”.',

  /* --- Check rules (2.13 section 13, R910–R914) --- */
  'pruefregeln.titel': 'Define check rules',
  'pruefregeln.ab': 'Valid from',
  'pruefregeln.ohnerecht':
    'You can see how strictly the release is checked. Changing it requires the permission "Define check rules".',
  'pruefregeln.spalte.pruefung': 'Check',
  'pruefregeln.spalte.stufe': 'Strictness',
  'pruefregeln.spalte.festgelegt': 'Defined',
  'pruefregeln.stufe.aus': 'Do not check',
  'pruefregeln.stufe.hinweis': 'Notice',
  'pruefregeln.stufe.warnung': 'Warning',
  'pruefregeln.stufe.sperrt': 'Blocks the release',
  'pruefregeln.ausgeliefert': 'As delivered',
  'pruefregeln.seit': 'since {periode} · {wer}, {am}',
  'pruefregeln.ohnebewegungsarten': 'No movement types created yet — there is nothing to check.',
  'pruefregeln.ungeprueft': 'Not checked since {periode} — defined by {wer}.',
  'pruefung.bilanz-geht-nicht-auf': 'The balance sheet does not balance',
  'pruefung.veraltet-regelwerk': 'The closing was calculated with an old edition of the rule set',
  'pruefung.veraltet-daten': 'Data has arrived since the calculation',
  'pruefung.veraltet-vorperiode': 'The previous period was recalculated',
  'pruefung.veraltet-frueherer-monat': 'An earlier month of the financial year has new data since it was calculated',
  'pruefung.anhang-unvollstaendig': 'At year end a mandatory notes disclosure is missing',
  'pruefung.anhang-wahlrechte': 'At year end an optional notes disclosure is unanswered',
  'pruefung.ic-ohne-partner': 'An intercompany account carries an amount without partner',
  'pruefung.beteiligung-ohne-partner': 'An investment stands without the company held',
  'pruefung.ohne-lieferung': 'A company in the scope of consolidation delivered nothing for the month',
  'pruefung.kfr-geht-nicht-auf': 'The cash flow statement does not balance',
  'pruefung.kfr-umgliederung-nicht-null': 'The reclassification within equity does not net to zero',
  'pruefung.kurse-aktualisieren': 'Update rates: a rate of the month is assumed or was determined before month end',
  'pruefung.eintrittskurs-nachtragen': 'Add entry rate: historical rates were not available at entry',
  'pruefung.ic-differenz-nicht-geklaert': 'Intercompany difference not settled: two companies differ beyond the tolerance',
  'pruefung.zwischenergebnis-ohne-schritt': 'Intercompany profit not assigned to a step: the account of a report is under no item of the two steps',
  'pruefung.einzahlung-passt-nicht-zur-quote': 'Payment does not match the share: the parent pays more or less on a capital increase than its share',
  'pruefung.zukauf-von-anteilen': 'Purchase of shares: addition to the investment without a capital increase of the subsidiary',
  'pruefung.ergebnisjahr-im-eigenkapital': 'The prior-year result stands differently in equity than it was booked out of the net income',
  'pruefung.spiegel-anlagen': 'The fixed asset schedule is incomplete or does not reconcile',
  'pruefung.spiegel-eigenkapital': 'The statement of changes in equity is incomplete or does not reconcile',
  'pruefung.spiegel-sonderposten': 'The special items schedule is incomplete or does not reconcile',
  'pruefung.spiegel-rueckstellungen': 'The provisions schedule is incomplete or does not reconcile',
  'pruefung.spiegel-verbindlichkeiten': 'The debt schedule is incomplete or does not reconcile',
  'pruefung.inaktiv-im-kreis': 'The company is inactive but still in the group',
  /* R1644 — A moved account inherits the account group of the accounts there, if it is unambiguous. */
  'pruefung.kontengruppe-uneins': 'Accounts of different account groups hang under one line item',
  'pruefung.historischer-kurs-fehlt': 'A row of the historical rates is missing for movements',
  'pruefung.monat-nicht-konsolidiert': 'A month of the period is not consolidated',
  'pruefung.konsolidierungshinweis': 'A consolidation step has issued a note',
  'pruefung.ic-differenz-nicht-geklaert/schuldenkonsolidierung':
    'Intercompany difference in receivables and payables not resolved',
  'pruefung.ic-differenz-nicht-geklaert/aufwandertragkonsolidierung':
    'Intercompany difference in expenses and income not resolved',
  'pruefung.ic-differenz-nicht-geklaert/dividendenkonsolidierung':
    'Intercompany difference in the distribution not resolved',
  'pruefung.spiegel.anlagen': 'The fixed asset schedule',
  'pruefung.spiegel.eigenkapital': 'The statement of changes in equity',
  'pruefung.spiegel.sonderposten': 'The special items schedule',
  'pruefung.spiegel.rueckstellungen': 'The provisions schedule',
  'pruefung.spiegel.verbindlichkeiten': 'The debt schedule',
  'freigabe.begruendung': 'Why release anyway? Required — the sentence is shown to the auditor.',
  'freigabe.ungeprueft': 'Not checked: {liste}.',
  'uebergangene.leer': 'No check has been overridden at any release so far, and every check ran.',
  'uebergangene.ohnekommentar': 'Overridden without comment',
  'uebergangene.spalte.abschluss': 'Closing',
  'uebergangene.spalte.befund': 'Finding',
  'uebergangene.spalte.begruendung': 'Reason',
  'uebergangene.wer': '{wer}, {am}',
  'uebergangene.nichtgeprueft': 'Not checked',
  'abbruch.pruefregel-unbekannt.meldung': 'The check "{pruefung}" does not exist.',
  'abbruch.pruefregel-unbekannt.weg': 'Choose a check from the list "Define check rules".',
  'abbruch.pruefregel-untergrenze.meldung': '"{pruefung}" detects a contradiction and cannot be switched off.',
  'abbruch.pruefregel-untergrenze.weg':
    'Set the check to "Warning": it no longer blocks, but is overridden with a reason at release.',
  'abbruch.pruefregel-periode.meldung': '"{periode}" is not a period.',
  'abbruch.pruefregel-periode.weg': 'Choose the period from which the level should apply.',

  /* --- Change log --- */
  'nachweis.pruefung.leer': 'No entries exist yet.',
  'nachweis.pruefung.unversehrt':
    'All {anzahl} entries are intact. The chain has been recomputed from beginning to end.',
  'nachweis.pruefung.kette-bricht':
    'The chain breaks at entry {eintrag}. From that point on the evidence no longer agrees with the data.',

  /* --- Master data maintenance (7.4) --- */

  /* --- Editing the structure (7.4 section 12, R319–R322) --- */
  'gliederung.keinplan':
    'No line item plan is assigned to this measurement layer — without a plan there is no structure to rearrange.',
  'gliederung.plan': 'Line item plan',
  'gliederung.abperiode': 'Effective from',
  'gliederung.korrektur': 'It was always meant this way',
  'gliederung.umgliederung.erlaeuterung':
    'What you move here applies from {periode}. The previous structure stays in place for earlier periods — a report on January shows January.',
  'gliederung.korrektur.erlaeuterung':
    'The row is corrected, not reclassified: the structure then applies to every period. Released closes stay untouched, they read on their own version.',
  'gliederung.gegriffen': '{gegenstand} is held — now click the target.',
  'gliederung.loslassen': 'let go',
  'gliederung.greifen': 'Hold — drag or click',
  'gliederung.hierunter': 'put here',
  'gliederung.vor': 'before {position}',
  'gliederung.einfuegen': 'insert here',
  'gliederung.einfuegen.erlaeuterung':
    'The new line item is created under {position} — its type comes from there and is not asked for — under "Assets" hangs an asset item.',

  /* Creating structures from the report (7.4 section 14, R494–R501) */
  /* The four element types of a report (2.1 section 3.6, R1704, R1705) */
  'anlage.menue.konto': 'New account …',
  'anlage.menue.position': 'New line item …',
  'anlage.menue.referenz': 'New reference …',
  'anlage.menue.summenzeile': 'New total line …',
  'anlage.menue.loeschen.position': 'Delete line item …',
  'anlage.menue.loeschen.konto': 'Delete account …',
  'anlage.menue.loeschen.referenz': 'Delete reference …',
  'anlage.menue.loeschen.summenzeile': 'Delete total line …',
  'anlage.titel.konto': 'New account under {position}',
  'anlage.titel.position': 'New line item under {position}',
  'anlage.nummer': 'Number',
  'anlage.code': 'Code',
  'anlage.bezeichnung': 'Name',
  'anlage.abperiode': 'Effective from period',
  'anlage.gruppe': 'Account group',
  'anlage.gruppe.uneins':
    'The accounts under {position} belong to several account groups ({gruppen}). Which one applies has to be stated — nothing is guessed.',
  'anlage.gruppe.ohne':
    'No account hangs under {position} yet. The account group therefore has to be stated — nothing is guessed.',
  'anlage.machtzursumme':
    '{position} then also adds up the line items below it.',
  'anlage.code.vorschlag': 'This is how the structure under {position} counts on. You may overwrite it.',
  'anlage.nummer.vorschlag': 'This is how the chart of accounts counts on here. You may overwrite it.',
  'anlage.hindernis.rechenposition':
    'Nothing is created under this line: its value is a formula over other lines, and anything below it would count in no total.',
  'anlage.position.rechenposition.meldung':
    'No child line can be created under {position}: the line names its own terms, its value is a formula.',
  'anlage.position.rechenposition.weg':
    'Attach the child line to one of the lines named in the formula — from there it counts.',
  'anlage.konto.rechenposition.meldung':
    'No account can be created on {position}: the line names its own terms, its value is a formula.',
  'anlage.konto.rechenposition.weg':
    'Attach the account to one of the lines named in the formula — from there it counts.',
  'anlage.anlegen': 'Create',
  // 'anlage.nochnichtgerechnet' dropped on 2026-09-16 (R569) — see de.ts.
  'anlage.offeneplaene':
    'In {anzahl} further line item plans the account has no place yet. As long as it is unassigned there, it is missing from those closings.',
  'anlage.konto.ohnenummer.meldung': 'No number, no account: the key is formed from it.',

  /* The refusals — finding and way out, never an error code (R145). */
  'gliederung.zyklus.meldung':
    '{gegenstand} cannot go under {position}: {position} already sits below it.',
  'gliederung.zyklus.weg':
    'Move {position} elsewhere first — a branch below itself has no root left and disappears from the balance sheet.',
  'gliederung.andereart.meldung': '{gegenstand} is {art}, {zielart} is something else.',
  'gliederung.andereart.weg':
    'A position can move between assets, liabilities, expenses and income. A line of the cash flow statement or the statement of changes in equity stays in its own outline.',
  'gliederung.saldowechsel.meldung':
    '{gegenstand} shows a reversed balance under {ziel}. After the move one would sit in the balance sheet and the other in the income statement.',
  'gliederung.saldowechsel.weg':
    'A reversed balance stays within its own statement. Remove the target in the properties window or move both together.',
  'gliederung.fremderplan.meldung': '{position} belongs to a different line item plan.',
  'gliederung.fremderplan.weg':
    'Each plan is a structure of its own. Switch the plan above, or create the line item within this plan.',
  'gliederung.position.unbekannt.meldung': '{position} does not exist.',
  'gliederung.konto.unbekannt.meldung': '{konto} does not exist.',
  'gliederung.schondort.meldung': '{gegenstand} already sits under {position}.',
  'gliederung.ohnezeile.meldung': '{position} has no structure row for this period.',
  'gliederung.ohnezeile.weg':
    'Create it on the sheet below — master data kind "Structure", with parent and validity.',
  'gliederung.ohnecode.meldung': 'No code, no line item: the key is formed from it.',
  'gliederung.wurzel.meldung': 'A new root is not created here.',
  'gliederung.wurzel.weg':
    'Assets, equity and liabilities, income and expenses are the sections of the statements, not rows within them. A new line item belongs under an existing one.',
  'gliederung.code.vergeben.meldung': 'The code {code} already exists.',
  'gliederung.code.vergeben.weg':
    'Choose another one. You can change it at any time later — the object ID is unaffected.',

  // ---- Master data exchange (5.1 section 10, R560-R565) ----

  'bereich.stammdaten.exportimport': 'Export and import',
  'austausch.exportieren': 'Export master data',
  'austausch.importieren': 'Import master data',
  'austausch.datei': 'Read: {datei}',
  'austausch.zaehler.neu': 'new',
  'austausch.zaehler.geaendert': 'changed',
  'austausch.zaehler.befunde': 'findings',
  'austausch.nichtszutun': 'Nothing to do — the workbook says what the data already says.',
  'austausch.uebergangen': 'Skipped, not a kind of master data: {blaetter}',
  'austausch.gestrichen': 'Skipped, these fields no longer exist: {angaben}',
  'austausch.liste.befunde': 'These rows stop the workbook',
  'austausch.liste.rechnend': 'These rows change a property that feeds a calculation',
  'austausch.liste.uebrige': 'These rows are new or change a label',
  'austausch.bestaetigen': 'I have seen the {anzahl} calculating changes.',
  'austausch.bestaetigen.eine': 'I have seen the one calculating change.',
  'austausch.uebernehmen': 'Take over',
  'austausch.grund.befunde': 'While findings are open, nothing is taken over.',
  'austausch.grund.bestaetigung': 'Please confirm the calculating changes first.',
  'austausch.spalte.stelle': 'Place in the workbook',
  'austausch.spalte.code': 'Code',
  'austausch.spalte.was': 'What happens',
  'austausch.zeile': 'row {nummer}',
  'austausch.zeile.neu': 'will be created and given an object ID',
  'austausch.zeile.geaendert': 'gets a new version',

  'austausch.keinemappe.meldung': 'This file is not an Excel workbook.',
  'austausch.keinemappe.weg':
    'The way in is the workbook with one sheet per kind of master data — a single table can arrive half complete.',
  'austausch.befunde.meldung': '{anzahl} rows cannot be taken over. The first: {erste}',
  'austausch.befunde.weg':
    'Nothing was taken over. A half-loaded workbook would be worse than none — correct the file and upload it again.',
  'austausch.rechnend.meldung': '{anzahl} rows change a property that feeds a calculation.',
  'austausch.rechnend.weg':
    'Review the list in the preview and confirm it explicitly. Released statements stay unchanged; the next run calculates with the new values.',

  // ---- The house field catalogue (7.4 section 18.5, R554) ----

  'feld.wert.zahl.meldung': '"{wert}" is not a number — {feld} expects one.',
  'feld.wert.janein.meldung': '"{wert}" is neither yes nor no — {feld} expects one of the two.',
  'feld.wert.periode.meldung': '"{wert}" is not a period — {feld} expects one.',
  'feld.wert.vorrat.meldung': '"{wert}" is not among the values of {feld}.',
  'feld.wert.weg': 'Expected: {erwartet}',
  'feld.werksfeld.meldung': '{objektart} already has a field {feld} out of the box.',
  'feld.werksfeld.weg':
    'Pick a different name. Two fields of the same name would sit twice on the sheet and twice in the export — and only one of them calculates.',
  'feld.fest.meldung': '{was} is fixed, now that {feld} carries values.',
  'feld.fest.weg':
    'Create a new field and retire this one. The values stay readable; converting them would silently make them unreadable.',

  // ---- Composition of measurement layers (2.4 section 9, R396-R400) ----

  // A sub-group in one column (7.3 section 9, R407-R409)
  'spalte.tk.marke': 'SG {code}',
  'spalte.tk.band': '{name} sub-group',
  'spalte.tk.summe': 'SG subtotal',
  'spalte.tk.gesamt': 'SG total',
  'spalte.tk.konsolidierung': 'SG eliminations',
  'spalte.tk.aufklappen':
    'Open up {name} — its entities appear, the view of the group stays.',
  'spalte.tk.zuklappen': 'Fold {name} back into a single column.',

  'spalte.hb2.aufklappen':
    'Show what the local GAAP balance sheet consists of — one column per layer.',
  'spalte.hb2.zuklappen': 'Fold the layers back together.',

  'ledgeraufbau.titel': 'Composition of measurement layers',
  'ledgeraufbau.art.rohdaten': 'As delivered',
  'ledgeraufbau.art.delta': 'Delta',
  'ledgeraufbau.art.gruppe': 'Group',
  'ledgeraufbau.art.rohdaten.erlaeuterung': 'What the entity delivered, untouched. Only the import writes here — CoKo rejects a manual posting.',
  'ledgeraufbau.art.delta.erlaeuterung':
    'An adjustment layer with a name of its own. Reconciliation, reclassification and manual entry live here.',
  'ledgeraufbau.art.gruppe.erlaeuterung':
    'A named container above layers. Nothing is ever posted into a group.',
  'ledgeraufbau.belege.keine': 'nothing posted yet',
  'ledgeraufbau.neu.gruppe': 'New group',
  'ledgeraufbau.neu.schicht': 'New layer',
  'ledgeraufbau.neu.titel': 'New in {gruppe}',
  'ledgeraufbau.neu.titel.wurzel': 'New group',
  'ledgeraufbau.neu.rechnungslegung': 'Accounting framework',
  'ledgeraufbau.neu.rechnungslegung.erlaeuterung':
    'Which set of rules this group is prepared under: HGB, IFRS, US GAAP. The settings the closing applies hang on it — write it exactly as it already appears beside the field. The layers below inherit it. CoKo assigns the key itself.',
  'ledgeraufbau.neu.name': 'Name',
  'ledgeraufbau.neu.name.erlaeuterung':
    'The name under which the layer appears everywhere. It can be changed at any time.',
  'ledgeraufbau.neu.art': 'Kind',
  'ledgeraufbau.aufnehmen': 'Add an existing layer',
  'ledgeraufbau.aushaengen': 'Remove from this group',
  'ledgeraufbau.aushaengen.frage':
    'Remove {mitglied} from {gruppe}? The layer stays, with everything posted in it — only the membership ends.',
  'ledgeraufbau.hoch': 'One place up',
  'ledgeraufbau.runter': 'One place down',
  'ledgeraufbau.umbenennen': 'Rename',
  'ledgeraufbau.freie': 'In no group',
  'ledgeraufbau.freie.erlaeuterung':
    'Created but not attached: these layers do not contribute to any statement.',
  'ledgeraufbau.leer': 'This group is empty. Create its first layer.',
  'ledgeraufbau.offen.keine':
    'This group reaches no delivered layer — a statement without a delivery.',
  'ledgeraufbau.offen.mehrere':
    'This group reaches several delivered layers. Nothing then tells apart which one is the entity delivery.',
  'ledgeraufbau.ohnelegung.meldung':
    'A group at the top level needs its accounting framework: there is nothing above it to inherit from.',
  'ledgeraufbau.ohnelegung.weg':
    'HGB, IFRS, US GAAP — the settings the closing applies hang on it.',
  'ledgeraufbau.ohnename.meldung': 'A name is required: it appears wherever the key would be too technical.',
  'ledgeraufbau.art.unbekannt.meldung': '{art} is not a kind of measurement layer.',
  'ledgeraufbau.unbekannt.meldung': '{ledger} does not exist.',
  'ledgeraufbau.keinegruppe.meldung': '{ledger} is not a group.',
  'ledgeraufbau.keinegruppe.weg':
    'Only a group has members. A layer carries postings, not further layers.',
  'ledgeraufbau.ohnegruppe.meldung': 'A layer is not created without a group.',
  'ledgeraufbau.ohnegruppe.weg':
    'It would contribute to no statement. Create it under the group it belongs to — or create that group first.',
  'ledgeraufbau.schondrin.meldung': '{mitglied} is already in {gruppe}.',
  'ledgeraufbau.nichtdrin.meldung': '{mitglied} is not in {gruppe}.',
  'ledgeraufbau.zyklus.meldung': '{gruppe} already sits inside {mitglied}.',
  'ledgeraufbau.zyklus.weg':
    'Placing a group inside itself would make a sum that contains itself — "how much is this?" would have no answer.',

  'stammdaten.art.dimensionswert': 'Dimension values',
  'stammdaten.art.positionsgliederung': 'Structure',
  'stammdaten.art.kontozuordnung': 'Account in the structure',
  'stammdaten.art.kfr_zeile': 'Cash flow statement lines',
  'stammdaten.art.kfr_zuordnung': 'Cash flow statement assignment',
  'stammdaten.art.beteiligung': 'Investments',
  'stammdaten.art.erstkonsolidierung': 'Initial consolidation',
  'stammdaten.art.kapitaltransaktion': 'Capital transactions',
  'stammdaten.art.kreis_einheit': 'Scope membership',
  'stammdaten.art.lieferrhythmus': 'Expected data delivery rhythm',
  'stammdaten.art.datenquelle': 'Data sources',
  'stammdaten.neu': 'Create',
  'stammdaten.neu.code.waehrung.erlaeuterung':
    'The ISO 4217 code, three capital letters: EUR, USD, KES. It is a global standard — CoKo does not assign it, and it appears exactly like this in every delivery.',
  'stammdaten.neu.titel': 'New: {objektart}',
  'stammdaten.neu.code': 'Code',
  'stammdaten.neu.code.erlaeuterung':
    'The key is formed from it and cannot be changed later: the whole version series hangs on it.',
  'stammdaten.neu.codeverweis.erlaeuterung':
    'The key is formed from it and cannot be changed later. Anything missing from this list does not exist yet — create it first.',
  'stammdaten.neu.name': 'Name',
  'stammdaten.neu.nummer.schonda':
    '{nummer} already starts another entry. That is allowed — just check that you meant it.',
  'stammdaten.neu.speichern': 'Create',

  // ---- The closed table over an area (7.4 section 20) ----
  'tafel.zeile': 'Entry',
  'tafel.spaltewaehlen': 'Select the whole column — then Ctrl+V fills every row at once',
  'tafel.einfuegen.laeuft': 'Writing … {fertig} of {gesamt}',
  'tafel.einfuegen.teilweise': 'Wrote {fertig} rows. {unbekannt} values are not in the list of choices — those are unchanged.',
  'tafel.einfuegen.unbekannt': 'Nothing written: {anzahl} values are not in the list of choices.',
  'tafel.einfuegen.keineperiode': '{anzahl} values do not read as a period — they are left unchanged.',
  'tafel.einfuegen.keinezahl': '{anzahl} values do not read as a number — they are left unchanged.',
  'tafel.zelle.keinezahl': 'This does not read as a number — the cell is left unchanged.',
  'stammdaten.aussondern': 'Delete …',
  'stammdaten.benutzt.titel': 'This element is in use.',
  'stammdaten.benutzt.text':
    'It cannot be removed without changing older closes — a figure without its account is no longer a derivation. It is therefore retired from a period onwards: what was calculated before stays unchanged; new submissions no longer run on it.',
  'stammdaten.unbenutzt.titel': 'This element is used nowhere.',
  'stammdaten.unbenutzt.text':
    'It can be removed. That too is an append and not an overwrite: the version series stays readable, and the change record says who removed it and when.',
  /* Where a master record occurs — in words, not table names (R316). */
  'stammdaten.stelle.core.journalzeile': 'Journal lines',
  'stammdaten.stelle.core.buchung': 'Postings',
  'stammdaten.stelle.core.ergebniszeile': 'Result lines of runs',
  'stammdaten.stelle.core.zwischenergebnis': 'Intermediate results',
  'stammdaten.stelle.core.kontenmapping': 'Account mappings',
  'stammdaten.stelle.core.konto_zuordnung': 'Accounts in the structure',
  'stammdaten.stelle.core.position_zuordnung': 'Line item structure',
  'stammdaten.stelle.core.position': 'Line items',
  'stammdaten.stelle.core.konto': 'Accounts',
  'stammdaten.stelle.core.kontengruppe': 'Account groups',
  'stammdaten.stelle.core.bewegungsart': 'Movement types',
  'stammdaten.stelle.core.ledger': 'Measurement layers',
  'stammdaten.stelle.core.einheit': 'Companies',
  'stammdaten.stelle.core.beteiligung': 'Shareholdings',
  'stammdaten.stelle.core.kreis_einheit': 'Group memberships',
  'stammdaten.stelle.core.lieferrhythmus': 'Expected data delivery rhythm',
  'stammdaten.stelle.core.konsolidierungskreis': 'Consolidation groups',
  'stammdaten.stelle.core.abschlusslauf': 'Closing runs',
  'stammdaten.stelle.core.datenquelle': 'Data sources',
  'stammdaten.stelle.core.importlauf': 'Import runs',
  'stammdaten.stelle.core.dimensionswert': 'Dimension values',
  'stammdaten.stelle.core.ledgerelement': 'Measurement layer groups',
  'stammdaten.stelle.core.partnercode': 'Partner codes',
  'position.einordnung.titel': 'Placement',
  'position.einordnung.unter': 'under {eltern}',
  'position.einordnung.oberste': 'top level',
  'position.einordnung.ab': 'from {periode}',
  'position.einordnung.bis': 'until {periode}',
  'position.einordnung.leer': 'The item is not placed in any structure.',
  'stammdaten.abperiode': 'Retired from period',
  'stammdaten.zeile.gueltigab': 'valid from {periode}',
  'stammdaten.stilllegen': 'Retire from {periode}',
  'stammdaten.entfernen': 'Remove',
  'stammdaten.arten': 'Kinds',
  'stammdaten.breite': 'Width of the list — adjust with the arrow keys',
  'stammdaten.art.einheit': 'Companies',
  'stammdaten.art.kontengruppe': 'Account groups',
  'stammdaten.art.konto': 'Accounts',
  'stammdaten.art.bewegungsart': 'Movement types',
  'stammdaten.art.ledger': 'Measurement layers',
  'stammdaten.art.konsolidierungskreis': 'Consolidation groups',
  'stammdaten.art.positionsplan': 'Presentation schemes',
  'stammdaten.art.position': 'Line items',
  'stammdaten.art.analysedimension': 'Dimensions',
  'stammdaten.art.segment': 'Segments',
  'stammdaten.art.waehrung': 'Currencies',
  'stammdaten.art.land': 'Countries',


  /* --- What a field is maintained for (7.4 section 23.4, R697) --- */
  'stammdaten.zweck.stillgelegt_ab': 'From this period the entry is no longer offered; what is posted stays readable',
  'stammdaten.zweck.sortierung': 'The order in lists and reports',
  'stammdaten.zweck.gueltig_von': 'From which period the assignment applies',
  'stammdaten.zweck.gueltig_bis': 'Until which period it applies — empty means: still applies',
  'stammdaten.zweck.ist_aktiv': 'Whether the entry is in use today',
  'stammdaten.zweck.zahlungswirksam': 'Whether the item enters the cash flow statement',
  'stammdaten.zweck.umgliederung_eigenkapital':
    'Enters no line of the cash flow statement and must net to zero per company and month',
  'stammdaten.zweck.bewegungsartpflicht': 'Requires a movement type for every balance — the basis of every schedule',
  'stammdaten.zweck.ist_fonds': 'Belongs to the cash fund of the cash flow statement',
  'stammdaten.zweck.ist_kontokorrent': 'Current account: the balance switches between receivable and payable',
  'stammdaten.zweck.umrechnungsmethode': 'Which rate applies: closing, average or historical',
  'stammdaten.zweck.kontoart': 'Decides whether the balance goes to the balance sheet or the income statement',
  'stammdaten.zweck.waehrung': 'Drives translation into group currency',
  'stammdaten.zweck.einheit.waehrung': 'The currency the entity reports in — the starting point of translation',
  'stammdaten.zweck.einheit.einheitsart': 'Groups entities by their role in the group',
  'stammdaten.zweck.einheit.sitz_ort': 'The registered office in the list of shareholdings, § 313 (2) HGB',
  'stammdaten.zweck.einheit.land': 'Completes the registered office in the list of shareholdings, § 313 (2) HGB',
  'stammdaten.zweck.einheit.stillgelegt_ab':
    'From this month the entity no longer reports and is offered nowhere; leaving the group is recorded on the shareholding and the scope membership',
  'stammdaten.feld.einheit.stillgelegt_ab': 'Inactive since',
  'stammdaten.zweck.kontengruppe.eltern': 'Places the group in the chain — it inherits from there what it does not state itself',
  'stammdaten.zweck.konto.gruppe': 'The account inherits from this group what it does not state itself',
  'stammdaten.zweck.konto.ist_ic': 'Marks the account for intercompany reconciliation',
  'stammdaten.zweck.konto.ist_beteiligung': 'The investment carrying amount offset in capital consolidation',
  'stammdaten.zweck.konto.ist_beteiligungsertrag': 'Income from investments — the dividend of a consolidated subsidiary is recognised and eliminated here (2.9 section 17)',
  'stammdaten.zweck.konto.ist_eigenkapital': 'Equity: translated at historical rates and subject to initial consolidation',
  'stammdaten.zweck.bewegungsart.ist_kern': 'A core movement type from which others inherit their properties',
  'stammdaten.zweck.bewegungsart.kern': 'The core movement type this one inherits from',
  'stammdaten.zweck.bewegungsart.spiegel': 'Which schedule the movement runs into — assets, equity, liabilities',
  'stammdaten.zweck.bewegungsart.spiegel_spalte': 'Which column of the schedule it fills',
  'stammdaten.zweck.bewegungsart.spiegel_block': 'Which of the two blocks of the fixed asset schedule — cost or depreciation',
  'stammdaten.zweck.bewegungsart.kfr_zeile': 'The cash flow line it enters',
  'stammdaten.zweck.bewegungsart.kurstyp': 'Which rate translates the movement',
  'stammdaten.zweck.bewegungsart.ist_eroeffnung': 'The movement carries the opening balance of the period',
  'stammdaten.zweck.ledger.rechnungslegung': 'Which framework this layer measures by — local GAAP, IFRS, tax',
  'stammdaten.zweck.ledger.art': 'Source data, delta or group: how the layer is formed',
  'stammdaten.zweck.beteiligung.tochter': 'The entity held',
  'stammdaten.zweck.beteiligung.mutter': 'The holding entity',
  'stammdaten.zweck.beteiligung.quote': 'The capital share — it determines group share and minorities',
  'stammdaten.zweck.beteiligung.stimmrechtsanteil': 'Decides control where it differs from the capital share. Empty means: same as the capital share',
  'stammdaten.zweck.beteiligung.beteiligungsart': 'Subsidiary, joint venture, associate or other investment',
  'stammdaten.zweck.beteiligung.einbeziehungsart': 'How the entity is included: full, proportionate, at equity or not at all',
  'stammdaten.zweck.beteiligung.erwerbsperiode': 'The date of acquisition — the basis of initial consolidation',
  'stammdaten.zweck.beteiligung.eintritt_zeitpunkt': 'Whether entry is at the start or the end of the period',
  'stammdaten.zweck.beteiligung.austritt_zeitpunkt': 'Whether exit is at the start or the end of the period',
  'stammdaten.zweck.beteiligung.anschaffungskosten': 'The carrying amount offset against the subsidiary equity',
  'stammdaten.zweck.beteiligung.anschaffungswaehrung': 'The currency of the acquisition cost',
  'stammdaten.zweck.beteiligung.aufsatzart': 'Only for a subsidiary acquired before its first month in CoKo: how it enters the group',
  'stammdaten.zweck.beteiligung.aufsatzmonat': 'The subsidiary’s first month in CoKo — its position is booked then and carried forward afterwards',
  'stammdaten.zweck.beteiligung.firmenwert_nutzungsdauer': 'With values at acquisition: over how many months from acquisition goodwill is amortised',
  'stammdaten.zweck.beteiligung.firmenwert_rest': 'With balances at takeover: goodwill as stated in the last group accounts before CoKo',
  'stammdaten.zweck.beteiligung.firmenwert_restnutzungsdauer': 'With balances at takeover: over how many months from takeover the rest is amortised',
  'stammdaten.zweck.beteiligung.ausgleichsposten': 'With balances at takeover: the non-controlling interests as stated in the last group accounts before CoKo',
  'stammdaten.zweck.beteiligung.gueltig_von': 'From which period the shareholding applies',
  'stammdaten.zweck.beteiligung.gueltig_bis': 'Until when it applies — empty means: still applies',
  'stammdaten.zweck.erstkonsolidierung.beteiligung': 'Which shareholding the frozen value refers to',
  'stammdaten.zweck.erstkonsolidierung.konto': 'Which account the frozen value refers to',
  'stammdaten.zweck.erstkonsolidierung.betrag': 'The value frozen at acquisition — it never changes again',
  'stammdaten.zweck.erstkonsolidierung.waehrung': 'The currency of the frozen value',
  'stammdaten.zweck.erstkonsolidierung.kurs': 'The historical rate at acquisition',
  'stammdaten.zweck.erstkonsolidierung.art': 'Equity at acquisition or hidden reserve',
  'stammdaten.zweck.erstkonsolidierung.nutzungsdauer_monate': 'Over how many months a hidden reserve or goodwill is amortised',
  'stammdaten.zweck.kapitaltransaktion.beteiligung': 'Which investment the transaction belongs to',
  'stammdaten.zweck.kapitaltransaktion.periode': 'The month of the transaction — it is booked in that month',
  'stammdaten.zweck.kapitaltransaktion.zeitpunkt': 'Start of period: the month’s result is already shared with the new interest. End of period: still with the old one',
  'stammdaten.zweck.kapitaltransaktion.art': 'Additional acquisition, partial disposal or capital increase — CoKo takes the amount from the delivery',
  'stammdaten.zweck.kapitaltransaktion.quote_danach': 'The parent’s ownership interest after the transaction; for a capital increase in proportion to the shares, the previous one',
  'stammdaten.zweck.kreis_einheit.einheit': 'Which entity belongs to the group',
  'stammdaten.zweck.kreis_einheit.kreis': 'Which consolidation group it belongs to',
  'stammdaten.zweck.kreis_einheit.gueltig_von': 'From which period it belongs — without this row it appears in no report',
  'stammdaten.zweck.kreis_einheit.eintrittskurs': 'At which rate its opening balance enters the group currency — fixed, one rate for everything',
  'stammdaten.zweck.kreis_einheit.eintrittskurs_weg': 'How the entry rate came about: entered, to be added, or taken as closing rate after consulting the auditor',
  'stammdaten.zweck.kreis_einheit.eintrittskurs_quelle': 'Which month the closing rate taken comes from',
  'stammdaten.zweck.lieferrhythmus.einheit': 'Which entity the rhythm applies to',
  'stammdaten.zweck.lieferrhythmus.gueltig_von': 'From which month it applies — until the next row',
  'stammdaten.zweck.lieferrhythmus.rhythmus':
    'How often the entity must deliver. A missing due delivery blocks the release; a month in which nothing is due raises no finding. An entity that delivers only at year end is set to “yearly”.',
  'stammdaten.feld.eintrittskurs': 'Entry rate',
  'stammdaten.feld.eintrittskurs_weg': 'Way to the entry rate',
  'stammdaten.feld.eintrittskurs_quelle': 'Closing rate taken from',
  'stammdaten.wert.eintrittskurs_weg.eingetragen': 'entered',
  'stammdaten.wert.eintrittskurs_weg.nachtragen': 'not available',
  'stammdaten.wert.eintrittskurs_weg.stichtag': 'closing rate after consulting the auditor',
  'stammdaten.zweck.konsolidierungskreis.oberste_einheit': 'The top: the group value lands on it, and its currency is the group currency',
  'stammdaten.zweck.konsolidierungskreis.zweck': 'Statutory statements, sub-group or management view',
  'stammdaten.zweck.konsolidierungskreis.uebergeordneter_kreis': 'The group this one sits in as a sub-group — empty means: this is the top. Changes no amount',
  'stammdaten.zweck.konsolidierungskreis.konzernwaehrung': 'The currency this group reports in',
  'stammdaten.zweck.konsolidierungskreis.guv_umrechnung': 'Monthly average or cumulative: how the income statement is translated',
  'stammdaten.zweck.konsolidierungskreis.fonds_mit_kontokorrent': 'Whether current account overdrafts count towards the cash fund',
  'stammdaten.zweck.konsolidierungskreis.geschaeftsjahr_beginn': 'Defines which periods form a financial year of the group',
  'stammdaten.zweck.positionsplan.ist_vorgabe': 'The structure that applies without a choice',
  'stammdaten.zweck.position.positionsplan': 'Which statement structure the line belongs to',
  'stammdaten.zweck.position.art': 'Assets, equity and liabilities, income, expense, cash flow or equity schedule',
  'stammdaten.zweck.position.vorzeichen': 'Whether the value appears with reversed sign',
  'stammdaten.zweck.position.ist_summe': 'The line sums up the lines below it',
  'stammdaten.zweck.position.spiegel': 'Which schedule the line enters',
  /* R1429–R1432 — Balance reversal on line item, account and account group (2.7 section 3.7a). */
  'stammdaten.zweck.position.saldowechsel_ziel':
    'Where the netted amount of the line is reported when it carries the opposite sign — e.g. the currency result under income instead of expense',
  'stammdaten.zweck.konto.saldowechsel_ziel':
    'Where the account is reported per company when its balance carries the opposite sign — e.g. an overdrawn bank account under liabilities',
  'stammdaten.zweck.kontengruppe.saldowechsel_ziel':
    'Where each account of the group is reported per company when its balance carries the opposite sign',
  'stammdaten.sperre.saldowechsel_ziel.unterpositionen':
    'A line item with sub-items does not move as a whole; the target is set on its sub-items.',
  'stammdaten.sperre.saldowechsel_ziel.konto':
    'No balance reversal on the line item: the account {konto} moves on its own. The line item moves as a whole only once none of its accounts carries a target.',
  'stammdaten.sperre.saldowechsel_ziel.position':
    'No balance reversal of its own: the line item {position} moves as a whole to {ziel}. To move each account on its own, remove the target there first.',
  'stammdaten.zweck.analysedimension.nummer': 'The axis the dimension appears under when drilling down',
  'stammdaten.zweck.dimensionswert.dimension': 'Which axis the value belongs to',
  'stammdaten.zweck.dimensionswert.eltern': 'The parent value — values can be nested',
  'stammdaten.zweck.partnercode.einheit': 'Which entity the source system code stands for',
  'stammdaten.zweck.partnercode.eltern': 'The parent code — codes can be nested',
  'stammdaten.zweck.partnercode.ist_gruppe': 'The row groups codes instead of being one',
  'stammdaten.zweck.positionsgliederung.position': 'Which line is being placed',
  'stammdaten.zweck.positionsgliederung.eltern': 'Which line it is placed under',
  'stammdaten.zweck.kontozuordnung.konto': 'Which account is being assigned',
  'stammdaten.zweck.kontozuordnung.position': 'Which statement line it goes into',
  'stammdaten.zweck.kontozuordnung.bemerkung': 'Why the assignment reads this way — for the auditor',
  'stammdaten.zweck.kontozuordnung.gruppe':
    'Which account group the account has in these months — it sets the rate, the movement-type duty and the cash fund',
  'stammdaten.zweck.kfr_zeile.standard': 'Which standard the line belongs to — DRS 21 or IAS 7',
  'stammdaten.zweck.kfr_zeile.bereich': 'Which part of the statement it sits in: operating, investing, financing or cash funds',
  'stammdaten.zweck.kfr_zeile.art': 'Whether it takes amounts or totals other lines',
  'stammdaten.zweck.kfr_zeile.rolle': 'Which task the statement gives it, regardless of its number',
  'stammdaten.zweck.kfr_zeile.zaehlweise': 'What a total adds up — only ordinary lines, never other totals',
  'stammdaten.zweck.kfr_zeile.schrift': 'Whether a total line is bold or normal — empty means the form of its kind',
  'stammdaten.zweck.kfr_zeile.hintergrund': 'Which background a total line carries — empty means the form of its kind',
  'stammdaten.zweck.kfr_zeile.schriftfarbe': 'Which colour a total line is written in — empty means the ordinary text',
  'stammdaten.zweck.kfr_zuordnung.position': 'Which balance sheet or income statement line item the reference hangs on — it also applies to the items below, unless they carry their own',
  'stammdaten.zweck.kfr_zuordnung.konto': 'Which single account the reference hangs on — more specific than any line item',
  'stammdaten.zweck.kfr_zuordnung.kontengruppe': 'Which account group the rule hangs on — only for statements without a structure',
  'stammdaten.zweck.kfr_zuordnung.bewegungsart': 'Which movement type the reference drew — only on references from before 2026-10-06',
  'stammdaten.zweck.kfr_zuordnung.bewegungsarten': 'Which movement types the reference draws — each ticked explicitly',
  'stammdaten.zweck.kfr_zuordnung.alle_bewegungsarten': 'Whether the reference draws every movement type, including those another reference already draws',
  'stammdaten.zweck.kfr_zuordnung.vorzeichen_umkehren': 'Whether the reference reverses the sign of its source — for instance to take interest expense out of operating activities',
  'stammdaten.zweck.kfr_zuordnung.zeile': 'Which cash flow statement line the reference draws into',
  'stammdaten.zweck.datenquelle.art': 'How data arrives: spreadsheet, text file, database',
  'stammdaten.zweck.datenquelle.herkunft': 'Which source system delivers',
  'stammdaten.zweck.datenquelle.zustaendig': 'Who is responsible for the delivery',
  'stammdaten.zweck.organisationsordner.eltern': 'The parent folder — a folder without a parent is its own hierarchy',
  'stammdaten.zweck.organisationszuordnung.einheit': 'Which entity is being filed',
  'stammdaten.zweck.organisationszuordnung.ordner': 'Which folder it goes into',
  'stammdaten.zweck.rolle.ist_lesend': 'Whether the role only reads and may not change anything',
  'stammdaten.zweck.sprache.ist_stammdatensprache': 'Whether labels are maintained in this language',
  'stammdaten.zweck.sprache.sortierung': 'The order the fallback chain searches when a label is missing',
  'stammdaten.zweck.sprache.eigenname': 'What the language calls itself',
  'stammdaten.zweck.waehrung.ezb_referenzkurs': 'Whether the ECB publishes a rate for this currency — only then does the button fetch anything',
  'stammdaten.zweck.feld.objektart': 'Which object type the custom field applies to',
  'stammdaten.zweck.feld.feldart': 'What kind of value it takes: text, number, yes/no, period, choice',
  'stammdaten.zweck.feld.klasse': 'Whether the field groups, evidences or labels',
  'stammdaten.zweck.feld.pflicht': 'Whether the field must be filled',
  'stammdaten.zweck.feld.vorrat': 'The selectable values, separated by \'|\'',

  'stammdaten.zweck': 'Purpose of the entry',
  'stammdaten.zweck.stift': 'change',
  'stammdaten.zweck.aendern': 'What {feld} is maintained for',
  'stammdaten.zweck.giltfueralle': 'This sentence applies to every object of this kind, not just this one.',

  'stammdaten.eintraege': 'Entries',
  'stammdaten.gruppieren': 'Group by',
  'stammdaten.gruppieren.keine': '— not grouped —',
  'stammdaten.sortieren': 'Sort by',
  'stammdaten.sortieren.code': 'Code',
  'stammdaten.sortieren.name': 'Name',
  'stammdaten.sortieren.reihenfolge': 'Order in the schedule',
  'stammdaten.sortieren.reihenfolge.allgemein': 'Order',
  'stammdaten.einordnen.greifen': 'Pick up and drag to another position',
  'stammdaten.einordnen.vor': 'before {name}',
  'stammdaten.einordnen.ende': 'to the end',
  'stammdaten.gruppe.ohne': 'Not stated',
  'stammdaten.suche': 'Name, short form or key',
  'stammdaten.nichts': 'No entry matches this search.',
  'stammdaten.leer.titel': 'Nothing selected',
  'stammdaten.leer.text':
    'Pick an entry on the left. The sheet then shows its names in every language kept for it, its properties including where each value comes from, and the versions in which it was changed.',
  'stammdaten.leer.dimensionen.titel': 'Dimensions you define yourself',
  'stammdaten.leer.dimensionen.text':
    'This is where you set up additional dimensions — cost centre, profit centre, cost object or whatever else your organisation analyses. Eight are provided. You give each one its name and keep its values underneath. Pick one on the left or create a new one.',
  'stammdaten.objektid': 'Object ID',
  'stammdaten.objektid.kopieren': 'copy',
  'stammdaten.objektid.kopiert': 'copied',
  'stammdaten.objektid.ging.nicht': 'did not work — please select it by hand',

  'stammdaten.beschriftungen': 'Names and short forms',
  'stammdaten.sprache': 'Language',
  'stammdaten.name': 'Name',
  'stammdaten.kuerzel': 'Short form',
  'stammdaten.sprache.anlegen': 'Create',

  // ---- The languages of the house (7.4 section 18.6, R556) ----

  'sprache.unbekannt.meldung': '"{sprache}" is not a language this house has declared.',
  'sprache.unbekannt.weg':
    'Declare it under Operations → Languages and house terms. A language is created there and not on a single master record — otherwise the house ends up with two spellings of the same language and nobody notices.',
  'sprache.stillgelegt.meldung': '{sprache} is retired and accepts no new texts.',
  'sprache.stillgelegt.weg':
    'The existing texts stay readable. To maintain this language again, lift the retirement under Operations → Languages.',
  'sprache.keine.meldung':
    'This house keeps no language for master data at all — so nothing can be named.',
  'sprache.keine.weg':
    'Declare at least one language as a master data language under Operations → Languages and house terms. As long as none is declared, no name can be written.',
  'sprachen.haus': 'The languages of this house',
  'sprachen.spalte.sprache': 'Language',
  'sprachen.spalte.stammdaten': 'Master data',
  'sprachen.spalte.bedienung': 'Interface',
  'sprachen.spalte.reihenfolge': 'Order',
  'sprachen.ja': 'yes',
  'sprachen.nein': 'no',
  'sprachen.bedienung.mitdatei': 'available',
  'sprachen.bedienung.ohnedatei': 'with the next release',
  'sprachen.stillgelegt': 'retired',
  'sprachen.leer': 'This house has not declared a language yet.',
  'sprachen.fuehren': 'keep data in it too',
  'sprachen.nichtmehrfuehren': 'stop keeping data in it',
  'sprachen.stilllegen': 'retire',
  'sprachen.aufnehmen': 'reinstate',

  'stammdaten.eigenschaften': 'Properties',
  'stammdaten.feld': 'Property',
  'stammdaten.wert': 'Value',
  'stammdaten.herkunft': 'Origin',
  'stammdaten.rechnend': 'affects figures',
  'stammdaten.rechnend.warnung':
    '{anzahl} closings have been released. They stay as they are — each reads at its own data cut-off. A property that affects the figures takes effect with the next run.',
  'stammdaten.geerbt': 'inherited from {von}',
  'fassung.veraltet': 'A newer version of CoKo is available. This page is older — please reload. Until then nothing is saved.',
  'fassung.neuladen': 'Reload',
  'anfrage.unbekannt.meldung': 'CoKo does not know the entry “{angabe}” — the page is probably outdated. Nothing was saved.',
  'anfrage.unbekannt.weg': 'Please reload the page and repeat the entry.',
  'fassung.veraltet.speichern': 'This page is older than CoKo and therefore does not save. Please reload.',
  'stammdaten.abwerk.hinweis':
    'Account delivered with CoKo: name, number, type and properties cannot be changed, and it can be neither deactivated nor deleted. You can change where it sits in the structure.',
  'stammdaten.abwerk.meldung': '{konto} is delivered with CoKo and cannot be changed, deactivated or deleted.',
  'stammdaten.abwerk.weg': 'Only where it sits in the structure can be changed — under Reporting structures.',
  'stammdaten.eigeneskonto.meldung': '{konto} is the own account of a closing rate adjustment: CoKo maintains it, and its name comes from the source account.',
  'stammdaten.eigeneskonto.weg': 'Change the source account, or clear the checkbox there.',
  'stichtagskursanpassung.titel': 'Closing rate adjustment',
  'stichtagskursanpassung.haken': 'Show the closing rate adjustment on a separate account',
  'stichtagskursanpassung.ab': 'from',
  'stichtagskursanpassung.gilt': 'Since {ab} on {konto}.',
  'stichtagskursanpassung.giltnicht': 'Since {ab} back on {konto}.',
  'stichtagskursanpassung.ohnemonat': 'Please choose the month from which the checkbox applies first.',
  'stichtagskursanpassung.periode.meldung': '{periode} is not a month.',
  'stichtagskursanpassung.konto.meldung': 'The account {konto} does not exist.',
  'stichtagskursanpassung.stichtag.meldung': '{konto} is translated at the closing rate; there is no closing rate adjustment.',
  'stichtagskursanpassung.ruecklage.meldung': 'Without {konto} under a line item, no own account can be created.',
  'abbruch.jahresergebnis-fehlt.meldung':
    'The account {konto} is missing from the chart of accounts. It is delivered with CoKo and cannot be deleted — the installation is incomplete.',
  'abbruch.jahresergebnis-fehlt.weg': 'Report this to operations.',
  'stammdaten.gesetzt': 'set here',
  'stammdaten.ungepflegt': 'not kept anywhere',
  'stammdaten.ueberschreiben': 'override',
  'stammdaten.erben': 'inherit',
  'stammdaten.keinefelder': 'Apart from its name, this kind of master data carries no properties.',

  'stammdaten.fassungen': 'Versions',
  'stammdaten.fassungen.einklappen': 'Collapse versions',
  'stammdaten.fassungen.ausklappen': 'Expand versions',
  'stammdaten.geloescht': 'deleted',

  /* --- Properties, alphabetically --- */
  'stammdaten.feld.art': 'Type',
  'stammdaten.feld.basis_ledger': 'Base measurement layer',
  'stammdaten.feld.bewegungsartpflicht': 'Movement type required',
  'stammdaten.feld.code': 'Code',
  'stammdaten.feld.eltern': 'Parent group',
  'stammdaten.feld.fonds_mit_kontokorrent': 'Cash pool includes overdrafts',
  'stammdaten.feld.geschaeftsjahr_beginn': 'First month of the financial year',
  'stammdaten.feld.gruppe': 'Account group',
  'stammdaten.feld.guv_umrechnung': 'P&L translation',
  'stammdaten.feld.ist_aktiv': 'In use',
  'stammdaten.feld.ist_beteiligung': 'Investment account',
  'stammdaten.feld.ist_beteiligungsertrag': 'Investment income',
  'stammdaten.feld.ist_delta': 'Delta measurement layer',
  'stammdaten.feld.ist_eigenkapital': 'Equity account',
  'stammdaten.feld.ist_eroeffnung': 'Opening value',
  'stammdaten.feld.ist_fonds': 'Part of cash and cash equivalents',
  'stammdaten.feld.ist_kontokorrent': 'Overdraft repayable on demand',
  'stammdaten.feld.ist_ic': 'Intercompany account',
  'stammdaten.feld.ist_kern': 'Is a core type itself',
  'stammdaten.feld.ist_summe': 'Line item with line items below it',
  'stammdaten.feld.ist_vorgabe': 'Default',
  'stammdaten.feld.kern': 'Core movement type',
  'stammdaten.feld.kfr_zeile': 'Cash flow line',
  'stammdaten.feld.konzernwaehrung': 'Group currency',
  'stammdaten.feld.kontoart': 'Account type',
  'stammdaten.feld.kurstyp': 'Rate type',
  'stammdaten.wert.wirtschaftsjahr_beginn.1': 'January',
  'stammdaten.wert.wirtschaftsjahr_beginn.2': 'February',
  'stammdaten.wert.wirtschaftsjahr_beginn.3': 'March',
  'stammdaten.wert.wirtschaftsjahr_beginn.4': 'April',
  'stammdaten.wert.wirtschaftsjahr_beginn.5': 'May',
  'stammdaten.wert.wirtschaftsjahr_beginn.6': 'June',
  'stammdaten.wert.wirtschaftsjahr_beginn.7': 'July',
  'stammdaten.wert.wirtschaftsjahr_beginn.8': 'August',
  'stammdaten.wert.wirtschaftsjahr_beginn.9': 'September',
  'stammdaten.wert.wirtschaftsjahr_beginn.10': 'October',
  'stammdaten.wert.wirtschaftsjahr_beginn.11': 'November',
  'stammdaten.wert.wirtschaftsjahr_beginn.12': 'December',
  'stammdaten.wert.geschaeftsjahr_beginn.1': 'January',
  'stammdaten.wert.geschaeftsjahr_beginn.2': 'February',
  'stammdaten.wert.geschaeftsjahr_beginn.3': 'March',
  'stammdaten.wert.geschaeftsjahr_beginn.4': 'April',
  'stammdaten.wert.geschaeftsjahr_beginn.5': 'May',
  'stammdaten.wert.geschaeftsjahr_beginn.6': 'June',
  'stammdaten.wert.geschaeftsjahr_beginn.7': 'July',
  'stammdaten.wert.geschaeftsjahr_beginn.8': 'August',
  'stammdaten.wert.geschaeftsjahr_beginn.9': 'September',
  'stammdaten.wert.geschaeftsjahr_beginn.10': 'October',
  'stammdaten.wert.geschaeftsjahr_beginn.11': 'November',
  'stammdaten.wert.geschaeftsjahr_beginn.12': 'December',
  'stammdaten.feld.einheitsart': 'Kind of entity',
  'stammdaten.feld.rhythmus': 'Expected data delivery rhythm',
  'stammdaten.feld.sitz_ort': 'Registered office (town)',
  'ordner.eltern.fehlt.meldung': 'There is no folder {ordner}.',
  'ordner.eltern.fehlt.weg': 'Pick an existing folder from the list.',
  'ordner.zirkel.meldung': 'A folder cannot sit below itself.',
  'ordner.zirkel.weg': 'Pick a folder that is not already below this one.',
  'ordner.doppelt.meldung': 'This entity already sits in {ordner} within the same hierarchy.',
  'ordner.doppelt.weg': 'Within one hierarchy an entity sits in exactly one place. End the existing assignment, or pick a different hierarchy.',
  'stammdaten.art.organisationsordner': 'Folder',
  'stammdaten.art.organisationszuordnung': 'Assignment to folder',
  'stammdaten.feld.ordner': 'Folder',
  'stammdaten.feld.wurzel': 'Hierarchy',
  'bereich.stammdaten.ordner': 'Folders of entities',

  // ---- What a report is tied to (2.3 section 10, R712-R716) ----

  'bereich.stammdaten.organisationselemente': 'Reporting objects',
  'stammdaten.art.organisationselement': 'Reporting object',
  'stammdaten.feld.objekt': 'Object referred to',
  'stammdaten.zweck.organisationselement.art':
    'What the report refers to — it decides what the selection next to it offers',
  'stammdaten.zweck.organisationselement.objekt':
    'Which object is meant. Its name and its validity come from there and are not repeated here',
  'stammdaten.zweck.organisationselement.sortierung':
    'Where it appears in the selection above the report — empty means: last, by name',
  'stammdaten.wert.art.kreis': 'Consolidation group',
  'stammdaten.wert.art.gesellschaft': 'Entity',
  'stammdaten.wert.art.ordner': 'Folder',
  'stammdaten.wert.art.dimensionswert': 'Value of a custom dimension',
  'stammdaten.aufnehmen': 'Add {art}',
  'stammdaten.aufnehmen.speichern': 'Add',
  'stammdaten.aufgenommen': 'Existing objects are added here — a {art} itself is created where it belongs',
  'stammdaten.leer.organisationselemente.titel': 'What the report offers at the top',
  'stammdaten.leer.organisationselemente.text':
    'A report always shows one section — the whole consolidation group, a single entity, a folder, or a value of a custom dimension. Whatever is added here appears in the selection above the balance sheet. The objects themselves are still created where they belong; this list only decides which of them can be picked.',

  // ---- The house field catalogue (7.4 section 18.5, R554) ----

  'bereich.stammdaten.eigenschaften': 'Declare properties',
  'stammdaten.art.feld': 'Properties',
  'stammdaten.feld.objektart': 'Applies to',
  'stammdaten.feld.feldart': 'Kind of entry',
  'stammdaten.feld.klasse': 'What it is for',
  'stammdaten.feld.vorrat': 'Values to choose from, separated by |',
  'stammdaten.feld.pflicht': 'Required',
  'stammdaten.vorschlag.uebernehmen': 'Apply suggestion: {wert}',
  'stammdaten.vorschlagsgrund.beherrscht_direkt':
    'The holder has the voting majority with {stimmen} %.',
  'stammdaten.vorschlagsgrund.beherrscht_mittelbar':
    'The holder controls this company through {ueber} — direct votes: {stimmen} %.',
  'stammdaten.vorschlagsgrund.einfluss':
    'With {stimmen} % of the votes there is significant influence, but no majority.',
  'stammdaten.vorschlagsgrund.keiner':
    '{stimmen} % of the votes — neither majority nor significant influence.',
  'stammdaten.vorschlagsgrund.aus_beteiligungsart': 'Follows the type of holding above.',
  'stammdaten.hausfeld': 'declared by this house',
  'stammdaten.feld.land': 'Country',
  'stammdaten.feld.nummer': 'Number',
  'stammdaten.feld.oberste_einheit': 'Top entity',
  'stammdaten.feld.positionsplan': 'Presentation scheme',
  'stammdaten.feld.rechnungslegung': 'Accounting framework',
  'stammdaten.feld.saldowechsel_ziel': 'Presentation of negative balances when balance reversal is active',
  'stammdaten.feld.dimension': 'Analysis dimension',
  'stammdaten.feld.position': 'Line item',
  'stammdaten.feld.konto': 'Account',
  'stammdaten.feld.gueltig_von': 'Valid from period',
  'stammdaten.feld.mutter': 'Holder (parent)',
  'stammdaten.feld.tochter': 'Held entity (subsidiary)',
  /* The heading carries the name; the hint lives in the purpose sentence (R840,
     decision 4 of 2026-09-30) */
  'stammdaten.feld.quote': 'Capital share',
  'stammdaten.feld.stimmrechtsanteil': 'Voting share',
  'stammdaten.zahl.nurganz': 'Only a whole number goes here, without decimals.',
  'stammdaten.feld.beteiligungsart': 'Type of holding',
  'stammdaten.feld.einbeziehungsart': 'Consolidation method',
  'stammdaten.feld.erwerbsperiode': 'Acquisition period',
  'stammdaten.feld.eintritt_zeitpunkt': 'Included from',
  'stammdaten.feld.austritt_zeitpunkt': 'Exit at',
  'stammdaten.feld.anschaffungskosten': 'Acquisition cost',
  'stammdaten.feld.anschaffungswaehrung': 'Acquisition currency',
  'stammdaten.feld.aufsatzart': 'Takeover type',
  'stammdaten.feld.aufsatzmonat': 'Takeover month',
  'stammdaten.feld.firmenwert_nutzungsdauer': 'Useful life of goodwill (months)',
  'stammdaten.feld.firmenwert_rest': 'Remaining goodwill',
  'stammdaten.feld.firmenwert_restnutzungsdauer': 'Remaining useful life of goodwill (months)',
  'stammdaten.feld.ausgleichsposten': 'Non-controlling interests',
  'stammdaten.feld.periode': 'Month',
  'stammdaten.feld.zeitpunkt': 'Timing',
  'stammdaten.feld.quote_danach': 'Ownership interest afterwards',
  'stammdaten.feld.beteiligung': 'Investment',
  'stammdaten.feld.betrag': 'Amount',
  'stammdaten.feld.kurs': 'Rate',
  'stammdaten.feld.nutzungsdauer_monate': 'Useful life in months',
  'stammdaten.feld.kreis': 'Consolidation scope',
  'stammdaten.feld.gueltig_bis': 'Valid to period',
  'stammdaten.feld.bemerkung': 'Note',
  'stammdaten.feld.herkunft': 'Origin',
  'stammdaten.feld.vorlage': 'Import template',
  'stammdaten.feld.einheit': 'Company',
  'stammdaten.feld.ledger': 'Measurement layer',
  'stammdaten.feld.zustaendig': 'Responsible',
  'stammdaten.feld.sortierung': 'Sort order',
  'stammdaten.feld.spiegel': 'Movement schedule',
  'stammdaten.feld.spiegel_block': 'Schedule block',
  'stammdaten.feld.spiegel_spalte': 'Schedule column',
  'stammdaten.feld.ezb_referenzkurs': 'ECB reference rate available',
  'stammdaten.feld.stillgelegt_ab': 'Closed for postings from',
  'stammdaten.feld.umrechnungsmethode': 'Translation method',
  'stammdaten.feld.vorzeichen': 'Display sign',
  'stammdaten.feld.waehrung': 'Currency',
  'stammdaten.feld.zahlungswirksam': 'Cash-effective',
  'stammdaten.feld.umgliederung_eigenkapital': 'Reclassification within equity',
  'stammdaten.feld.zweck': 'Purpose',

  /* --- Value vocabularies --- */
  'stammdaten.wert.rohdaten': 'As delivered — delivery only',
  'stammdaten.wert.delta': 'Delta — adjustment layer',
  'stammdaten.wert.gruppe': 'Group — sum of its layers',
  'stammdaten.wert.einheitsart.operativ': 'Operating',
  'stammdaten.wert.einheitsart.holding': 'Holding',
  'stammdaten.wert.einheitsart.zweckgesellschaft': 'Special purpose entity',
  'stammdaten.wert.einheitsart.ruhend': 'Dormant',
  'stammdaten.wert.rhythmus.monatlich': 'monthly',
  'stammdaten.wert.rhythmus.quartalsweise': 'quarterly',
  'stammdaten.wert.rhythmus.halbjaehrlich': 'half-yearly',
  'stammdaten.wert.rhythmus.jaehrlich': 'yearly',
  'stammdaten.wert.beteiligungsart.tochter': 'Subsidiary (§ 290 HGB)',
  'stammdaten.wert.beteiligungsart.gemeinschaft': 'Joint venture (§ 310 HGB)',
  'stammdaten.wert.beteiligungsart.assoziiert': 'Associate (§ 311 HGB)',
  'stammdaten.wert.beteiligungsart.sonstige': 'Other investment',
  'stammdaten.wert.voll': 'Full consolidation',
  'stammdaten.wert.quotal': 'Proportionate consolidation',
  // A yes/no field of the house in the table (R554, R840) — stored as true and false
  'stammdaten.wert.true': 'Yes',
  'stammdaten.wert.false': 'No',
  'stammdaten.wert.equity': 'Equity method',
  'stammdaten.wert.nicht': 'not consolidated',
  'stammdaten.wert.beginn': 'Start of period',
  'stammdaten.wert.ende': 'End of period',
  'stammdaten.wert.art.eigenkapital': 'Equity at acquisition',
  'stammdaten.wert.art.stille_reserve': 'Hidden reserve',
  'stammdaten.wert.art.zukauf': 'Additional acquisition',
  'stammdaten.wert.art.teilverkauf': 'Partial disposal',
  'stammdaten.wert.art.kapitalerhoehung': 'Capital increase',
  'stammdaten.wert.aufsatzart.werte_zum_erwerb': 'Values at acquisition',
  'stammdaten.wert.aufsatzart.staende_zum_aufsatz': 'Balances at takeover',
  'stammdaten.wert.aufsatzart.manuell': 'Book manually',
  'stammdaten.wert.aktiv': 'Asset account',
  'stammdaten.wert.passiv': 'Liability account',
  'stammdaten.wert.ertrag': 'Income account',
  'stammdaten.wert.aufwand': 'Expense account',
  'stammdaten.wert.stichtag': 'Closing rate',
  'stammdaten.wert.stichtag_vorperiode': 'Closing rate of the prior period',
  'stammdaten.wert.durchschnitt': 'Average rate',
  'stammdaten.wert.historisch': 'Historical rate',
  'stammdaten.wert.einbeziehung': 'Rate at the date of inclusion',
  'stammdaten.wert.uebertrag': 'Carried forward, not translated',
  'stammdaten.wert.keiner': 'No rate',
  'stammdaten.wert.operativ': 'Operating activities',
  'stammdaten.wert.investition': 'Investing activities',
  'stammdaten.wert.finanzierung': 'Financing activities',
  'stammdaten.wert.keine': 'No effect',
  'stammdaten.wert.spiegel.keiner': 'No statement of changes',
  'stammdaten.wert.alle': 'All schedules',
  'stammdaten.wert.anlagen': 'Fixed asset schedule',
  'stammdaten.wert.eigenkapital': 'Statement of changes in equity',
  'stammdaten.wert.verbindlichkeiten': 'Liabilities schedule',
  'stammdaten.wert.abschreibung': 'Accumulated depreciation',
  'stammdaten.wert.aktiva': 'Assets',
  'stammdaten.wert.passiva': 'Equity and liabilities',
  'stammdaten.wert.kfr': 'Cash flow statement',
  'stammdaten.wert.ek_spiegel': 'Statement of changes in equity',
  'stammdaten.wert.handelsrecht': 'German GAAP',
  'stammdaten.wert.ifrs': 'IFRS',
  'stammdaten.wert.steuerlich': 'Tax',
  'stammdaten.wert.management': 'Management',
  'stammdaten.wert.umsatzkosten': 'Cost of sales method',
  'stammdaten.wert.intern': 'Internal',
  'stammdaten.wert.gesetzlich': 'Statutory group',
  'stammdaten.wert.teilkonzern': 'Sub-group',
  'stammdaten.wert.monatsdurchschnitt': 'Monthly average',
  'stammdaten.wert.ytd': 'Year to date',
  'stammdaten.wert.vorzeichen.1': 'As posted',
  'stammdaten.wert.vorzeichen.-1': 'Reversed',


  /* ---- Statement of cash flows, DRS 21 ------------------------------ */

  'kfr.kontrolle': 'Check: closing cash fund against balance sheet',
  'kfr.kontrolle.stimmt': 'Reconciles: the closing cash fund matches the balance of the fund accounts to the cent.',
  'kfr.kontrolle.differenz': 'Does not reconcile: the closing cash fund differs from the balance of the fund accounts by this amount.',
  'kfr.spalte.nummer': 'No.',
  'kfr.spalte.zeile': 'Line',
  'kfr.spalte.betrag': 'Amount',

  // ---- Cash-fund boundary (R63, R271, R272) --------------------------------
  'kfr.befund.kontokorrent-ungewaehlt':
    '{anzahl} accounts are flagged as overdrafts, but the consolidation group has made no choice on the fund boundary. They therefore stay outside cash and cash equivalents — permissible under DRS 21, but possibly not intended. The choice lives in the group master data.',
  'kfr.befund.zeile-ohne-summe':
    'Line {zeile} is in no total — {betrag} is missing from the cash flow. The control line shows the difference.',
  'kfr.befund.zeile-doppelt':
    'Line {zeile} counts in more than one total — {betrag} appears twice in the cash flow. The control line shows the difference.',
  'kfr.befund.rolle-fehlt':
    'The line set {standard} leaves these roles unfilled: {rollen}. Amounts belonging there drop out of the statement — visible in the reconciliation below the cash proof. Nothing is guessed.',

  // ---- Reclassification proposals (2.8 section 6, R5) ---------------------

  // ---- The audit trail from a cash flow line (2.8 section 13.14) ----------
  'kfrnachweis.aufbau':
    'What this line took up between {von} and {bis} — by entity, account and movement type, with its origin.',
  'kfrnachweis.art.gebucht': 'posted',
  'kfrnachweis.art.gerechnet': 'derived',
  'kfrnachweis.art.anfangsbestand': 'opening balance',
  'kfrnachweis.zeitraum': 'period',
  'kfrnachweis.keinbeleg': 'no posting',
  'kfrnachweis.regel.position': 'Line item',
  'kfrnachweis.regel.konten': 'Accounts',
  'kfrnachweis.regel.ueberall': 'everywhere',
  'kfrnachweis.regel.alleuebrigen': 'all other',
  'kfrnachweis.regel.ausser': 'except {arten}',
  'kfrnachweis.regel.alle': 'all',
  'kfrnachweis.regel.umgekehrt': 'sign reversed',
  'kfrnachweis.regel.beteiligung': "Parent's investment account — purchase price or sale proceeds",
  'kfrnachweis.regel.kreis': 'Cash from additions to or disposals from the scope of consolidation',
  'kfrnachweis.regel.kurs': 'Closing-rate adjustment on cash and cash equivalents',
  'kfrnachweis.regel.rueckfall': 'Line set at the movement type',
  'kfrnachweis.regel.fonds': 'Cash and cash equivalents at the beginning',
  'menue.eigenschaften': 'Properties',
  'kfreigenschaften.titel': 'Properties · {zeile}',
  'kfreigenschaften.regeln': 'Referenced items from balance sheet/income statement',
  'kfreigenschaften.zaehlt': 'What it adds up',
  'kfreigenschaften.zaehlt.abschnitt': 'Adds up all lines of section {abschnitt}.',
  'kfreigenschaften.zaehlt.darueber': 'Adds up all lines above.',
  'kfreigenschaften.zaehlt.seit_voriger': 'Adds up the lines of its section since the previous total.',
  'kfreigenschaften.zaehlt.unterzeilen': 'Adds up its references and the line items below it.',
  'kfreigenschaften.quelle': 'Item/account reference',
  'kfreigenschaften.fonds': 'What belongs to cash and cash equivalents',
  'kfreigenschaften.fonds.zweck': 'A balance sheet line item with the items below it, or an account — it belongs to the fund as a whole',
  'kfreigenschaften.fonds.hinweis': 'Applies to the beginning and end of the period and to both line sets.',
  'kfreigenschaften.fonds.leer': 'Nothing belongs to cash and cash equivalents yet.',
  'kfreigenschaften.quelle.zweck': 'Which balance sheet or income statement line item — with the items below — or which account the line draws from',
  'kfreigenschaften.alleankreuzen': 'Tick all',
  'kfreigenschaften.arten.fertig': 'Done',
  'kfreigenschaften.format': 'Format',
  'kfreigenschaften.format.standard': 'Standard',
  'kfreigenschaften.gruppe.bilanz': 'Balance sheet',
  'kfreigenschaften.gruppe.guv': 'Income statement',
  'kfreigenschaften.gruppe.konten': 'Accounts',
  'kfreigenschaften.kontengruppe': 'Account group {name}',
  'kfreigenschaften.leer': 'This line does not draw anything yet.',
  /* --- The direction of a line (2.8 section 13.31, R1090–R1095) --- */
  'kfreigenschaften.neu': 'Add reference',
  'kfreigenschaften.entfernen': 'Remove',
  'positionskonten.titel': 'Accounts under this line item',
  'positionskonten.wirkung': 'Moving and removing apply',
  'positionskonten.ab': 'from',
  'positionskonten.immer': 'always',
  'positionskonten.freigegeben': '“Always” reaches back into released closings ({abschluesse}). They keep their frozen state; every newly calculated report shows the account in its new place.',
  'positionskonten.ohneperiode': 'From which period? Without a period only “always” applies.',
  'positionskonten.leer': 'No account is assigned to this line item.',
  'positionskonten.vonanfang': 'from the start',
  'positionskonten.umhaengen': 'Move to',
  'kachel.bericht.bilanz': 'Balance sheet',
  'kachel.bericht.guv': 'Income statement',
  'positionskonten.entfernen': 'Remove',
  'kfr.nachweis.summenzeile':
    'Line {zeile} is a subtotal — its value sits in the lines it adds up. The trail into the journal leads from those, not from here: a subtotal would bring a thousand journal lines nobody reads.',
  'kfr.bereich.operativ': '1. Operating activities',
  'kfr.bereich.investition': '2. Investing activities',
  'kfr.bereich.finanzierung': '3. Financing activities',
  'kfr.bereich.fonds': '4. Cash and cash equivalents',
  'kfr.probe.differenz':
    'The cash reconciliation differs from the balance sheet by {betrag} {waehrung}: an amount could not be derived from movements. The reconciliation with the movement balance names the account in the column “Difference”.',
  'kfr.kursanpassung.an': 'Closing-rate adjustment shown separately',
  'kfr.kursanpassung.aus': 'Closing-rate adjustment not shown separately',
  'kfr.kursanpassung.an.hilfe':
    'The movement type "currency delta" has its own line. Switching off leaves it in the line of its account.',
  'kfr.kursanpassung.aus.hilfe':
    'The movement type "currency delta" stays in the line of its account. Switching on gives it a line of its own.',
  'kfr.abstimmung.titel': 'Reconciliation against the movement balance sheet ({konten} accounts, {abweichend} with a difference)',
  'kfr.abstimmung.nurabweichungen': 'Differences only',
  'kfr.abstimmung.block.bilanz': 'Balance sheet',
  'kfr.abstimmung.block.guv': 'Income statement',
  'kfr.abstimmung.zeile': 'Reference',
  // Why a movement deviates (2.1 section 3.6, R1714).
  'kfr.abstimmung.grund.fehlt': 'missing',
  'kfr.abstimmung.grund.doppelt': 'double',
  'kfr.abstimmung.grund.fehlt.erklaerung': 'No line item draws this movement.',
  'kfr.abstimmung.grund.doppelt.erklaerung': 'Two references draw this movement with the same sign.',
  'kfr.abstimmung.keine': 'no difference',
  'kfr.abstimmung.keinezeile': 'no reference',
  'kfr.abstimmung.ohneart': 'Remainder, not assigned to any posting',
  'kfr.abstimmung.konto': 'Account',
  'kfr.abstimmung.anfang': 'Opening balance',
  'kfr.abstimmung.ende': 'Closing balance',
  'kfr.abstimmung.bewegungsbilanz': 'Movement balance sheet',
  'kfr.abstimmung.stichtagskurs': 'of which closing-rate',
  'kfr.abstimmung.kreis': 'Effect of changes in the scope of consolidation',
  'kfr.abstimmung.umgliederung': 'Reclassification within equity',
  'kfr.abstimmung.inrechnung': 'In the statement',
  'kfr.abstimmung.abweichung': 'Difference',
  /* 2.8 section 13.51, R1675–R1677 — what no line may name. */
  'kfr.abstimmung.ausgenommen': 'Excluded',
  'kfr.abstimmung.ausgenommen.zweck':
    'Movement excluded from the cash flow statement — what an exclusion at the bottom of the table names. If a line names it as well, it counts twice and shows as a difference.',
  'kfr.abstimmung.ausgenommen.marke': 'excluded',
  'kfr.ausnahmen.titel': 'Movements excluded from the cash flow statement',
  'kfr.ausnahmen.zweck': 'What no line of the cash flow statement may name — shared by all line sets',
  'kfr.ausnahmen.zeile': '{quelle} × {arten}',
  'kfr.ausnahmen.leer': 'No exclusion set up.',
  'kfr.ausnahmen.neu': 'Add exclusion',
  'kfr.ausnahmen.gegenprobe.auf':
    'Cross-check {bewegungsart}: excluded {ausgenommen}, on cash and cash equivalents {fonds} — together zero, it reconciles.',
  'kfr.ausnahmen.gegenprobe.nicht':
    'Cross-check {bewegungsart}: excluded {ausgenommen}, on cash and cash equivalents {fonds} — off by {rest}. The note below the statement names the month and the companies.',
  'kfr.leer.titel': 'No statement of cash flows for this selection',
  'kfr.titel.inwaehrung': '{titel} · in {waehrung}',
  'kfr.leer.grund': 'No close has been calculated for this period.',
  'kfr.befund.kein-lauf':
    'No data exists between {von} and {bis}. A statement of cash flows needs one run per period, and a run needs delivered balances.',
  'kfr.befund.kein-anfangsbestand':
    'No data exists for {periode} — without the prior period there is no opening balance, and every change in a balance would be the whole balance. A later start of the period range helps.',
  'kfr.befund.kein-katalog': 'There is no line catalogue for measurement layer {ledger} for this run. The run is older than the catalogue and reads the structure of that time; an open close is therefore recomputed, a released one stays as it was released.',
  'kfr.befund.kein-fondskonto':
    'No account is assigned to cash and cash equivalents. Without that account property the statement has nothing to explain.',
  'kfr.befund.umgliederung-nicht-null':
    '{gesellschaft}, {periode}, column {spalte}: the reclassification within equity does not net to zero but to {betrag}. One side probably carries a different movement type — the reconciliation with the movement balance shows both accounts in the column “Reclassification within equity”.',
  /* R1677 — the cross-check of an exclusion does not reconcile. */
  'kfr.befund.gegenprobe':
    '{periode}, {bewegungsart}: {ausgenommen} is excluded and {fonds} is on cash and cash equivalents — together they should be zero. The movement type is not booked completely ({gesellschaften}); the reconciliation against the movement balance sheet shows the accounts.',
  'kfr.befund.kreiszugang-fehlt':
    '{gesellschaft} has been in the scope of consolidation since {periode}. Its opening values do not carry the movement type "Addition to scope of consolidation" and therefore cannot be derived at {betrag} — the cash flow statement does not balance by this amount. Remedy: import the trial balance of the previous month and recalculate — CoKo forms the addition from its balance.',

  /* ---- Reclassifications via statistical accounts (DRS 21) ---------- */


  /* --- The tile board (7.5, R337–R349) --- */
  'kachel.brett.gestalten': 'Arrange board',
  'kachel.brett.fertig': 'Done',
  'kachel.brett.gestalten.hinweis':
    'Dragging reorders the tiles; the arrows do the same without a mouse.',
  'kachel.brett.eigen': 'your own arrangement',
  'kachel.hinzufuegen': 'Add tile',
  'kachel.zuruecksetzen': 'Reset to the delivered board',
  'kachel.zuruecksetzen.frage': 'Reset the board to the delivered state?',
  'kachel.zuruecksetzen.hinweis': 'Your arrangement will be lost.',
  'kachel.leer': 'The board holds nothing but the work list.',
  'kachel.leer.weg': 'Use “Arrange board” and “Add tile” to put tiles on it.',

  'kachel.katalog.titel': 'What should the tile show?',
  'kachel.art.zahl': 'A figure with a way forward',
  'kachel.art.zahl.zweck':
    'One figure, its state and the sentence saying what to do — it opens where the matter is settled.',
  'kachel.art.verlauf': 'A series along the time axis',
  'kachel.art.verlauf.zweck':
    'The same figure across the time axis — months, quarters or years, depending on the grid, as bars or a line.',
  'kachel.art.stand': 'Progress',
  'kachel.art.stand.zweck': 'Named steps with their state — every step opens.',
  'kachel.gegenstand.schritte': 'Consolidation steps',
  'kachel.gegenstand.lieferstand': 'Submissions by entity',
  'kachel.gegenstand.systemzustand': 'System health',

  'kachel.blatt.titel': 'Set up the tile',
  'kachel.feld.titel': 'Heading',
  'kachel.feld.titel.vorgabe': 'Left empty, the tile carries its catalogue name.',
  'kachel.feld.quelle': 'The figure comes',
  'kachel.quelle.gestellt': 'from the closing',
  'kachel.quelle.formel': 'from a formula',
  'kachel.vorrat.formel': 'Your own ratio',
  'kachel.feld.groesse': 'Figure',
  'kachel.feld.formel': 'Formula',
  'kachel.formel.hinweis':
    'References in square brackets, arguments separated by a semicolon — for example max([bilanz.A]; 0).',
  'kachel.formel.bezuege': 'Available references',
  'kachel.formel.einsetzen': 'Insert into the formula',
  'kachel.formel.rechnung': 'The calculation with the figures filled in',
  'kachel.formel.anzeige': 'Formula — display only, never a closing figure',
  'kachel.formel.anzeige.erklaerung':
    'It works on committed figures and reaches no report, no closing and no notes.',
  'kachel.formel.steht': 'The formula is valid.',
  'kachel.feld.einheit': 'Kind of figure',
  'kachel.einheit.betrag': 'Amount',
  'kachel.einheit.anzahl': 'Count',
  'kachel.einheit.prozent': 'Per cent',
  'kachel.einheit.mswahl': 'Milliseconds',
  'kachel.einheit.ms': '{ms} ms',
  'kachel.feld.herkunft': 'Scope',
  'kachel.herkunft.folgt': 'Follows the selection above',
  'kachel.herkunft.fest': 'Pinned',
  'kachel.herkunft.folgt.kurz': 'follows the selection',
  'kachel.herkunft.fest.kurz': 'pinned',
  'kachel.feld.darstellung': 'Shown as',
  'kachel.darstellung.balken': 'Bars',
  'kachel.darstellung.linie': 'Line',
  'kachel.feld.einheiten.monat': 'Months',
  'kachel.feld.einheiten.quartal': 'Quarters',
  'kachel.feld.einheiten.jahr': 'Years',
  'kachel.feld.schwellen': 'Traffic light',
  'kachel.schwelle.gruen': 'green from',
  'kachel.schwelle.gelb': 'amber from',
  'kachel.schwelle.richtung': 'Good is',
  'kachel.schwelle.hoch': 'a high figure',
  'kachel.schwelle.niedrig': 'a low figure',
  'kachel.schwelle.ohne': 'Left empty, the tile shows the state the figure brings with it.',
  'kachel.feld.breite': 'Width',
  'kachel.breite.3': 'quarter',
  'kachel.breite.4': 'third',
  'kachel.breite.6': 'half',
  'kachel.breite.12': 'full width',
  'kachel.feld.hoehe': 'Height',
  'kachel.hoehe.1': 'single',
  'kachel.hoehe.2': 'double',

  'kachel.vor': 'Move up',
  'kachel.zurueck': 'Move down',
  'kachel.einrichten': 'Set up',
  'kachel.verdoppeln': 'Duplicate',
  'kachel.entfernen': 'Remove',
  'kachel.einklappen': 'Collapse',
  'kachel.ausklappen': 'Expand',
  'kachel.uebernehmen': 'Apply',
  'kachel.abbrechen': 'Cancel',

  'kachel.laedt': 'loading …',
  'kachel.keinlauf': 'No calculation exists for this scope.',
  'kachel.keinlauf.weg': 'Choose another period or change the scope of the tile.',
  'kachel.verlauf.ohne': 'This report does not carry that line item.',
  'kachel.stand.ohne': 'No figures are available for this.',
  'kachel.stand.geliefert': '{geliefert} of {gesamt} submitted',
  'kachel.stand.buchung': '1 posting',
  'kachel.stand.buchungen': '{anzahl} postings',
  'kachel.stand.zusage': 'Above the {budget} ms target',
  'kachel.stand.ueberbudget': '{anzahl} of {gesamt} calls',
  'kachel.nachweis': 'Open the derivation',

  /* --- Signing in (8.1, R355–R357, R362) --- */
  'anmeldung.name': 'User name',
  'anmeldung.kennwort': 'Password',
  'anmeldung.anmelden': 'Sign in',
  'anmeldung.abgeschaltet': 'Sign-in disabled',
  'anmeldung.abgeschaltet.hinweis': 'This installation runs without sign-in — for development. Every call counts as “admin”. The switch has no effect in a delivered installation.',
  'anmeldung.abmelden': 'Sign out',
  'profil.titel': 'My profile',
  'profil.zahlen': 'Number display',
  'profil.zahlen.hinweis': 'Applies on every computer you sign in on.',
  'profil.spiel': 'Playful elements',
  'profil.spiel.hinweis': 'Visible only to you. Everything is off unless you turn it on.',
  'profil.spiel.erfolgsmomente': 'Moments of success',
  'profil.spiel.erfolgsmomente.hinweis':
    'A short line above the worklist when a close has been approved. It appears once and disappears with your next click.',
  'spiel.moment.freigabe': 'Close {periode} is done — {kreis} is approved.',
  'profil.spiel.serie': 'Streak and best time',
  'profil.spiel.serie.hinweis':
    'A line above the start page: how many periods in a row the group was approved without a justified warning, and the fewest days from the first data import to approval.',
  'spiel.serie.keine': 'Streak: no period without a justified warning yet',
  'spiel.serie.eins': 'Streak: 1 period without a justified warning',
  'spiel.serie.mehrere': 'Streak: {anzahl} periods in a row without a justified warning',
  'spiel.bestzeit.eins': 'Best time: 1 day from first data import to approval ({periode})',
  'spiel.bestzeit.mehrere': 'Best time: {tage} days from first data import to approval ({periode})',
  'spiel.zuletzt.eins': 'last 1 day ({periode})',
  'spiel.zuletzt.mehrere': 'last {tage} days ({periode})',
  'anmeldung.laeuft': 'one moment …',
  'anmeldung.falsch': 'User name or password is not correct.',
  'anmeldung.gesperrt': 'Too many failed attempts. Access is locked for {minuten} minutes.',
  'anmeldung.vergessen':
    'Forgotten your password? Your administrator sets a new one — this system sends no email.',
  'anmeldung.angemeldetals': 'Signed in as {name}',

  'kennwort.titel': 'Change password',
  'kennwort.warum':
    '{name}, this password came with the delivery and must be changed before you continue.',
  'kennwort.altes': 'Current password',
  'kennwort.neues': 'New password',
  'kennwort.nochmal': 'Repeat the new password',
  'kennwort.wechseln': 'Change password',
  'kennwort.richtlinie':
    'At least twelve characters. Length counts, not special characters — and your user name must not appear in it.',
  'kennwort.zukurz': 'The password is too short — it needs at least twelve characters.',
  'kennwort.enthaeltnamen': 'The password contains your user name.',
  'kennwort.altfalsch': 'The current password is not correct.',
  'kennwort.ungleich': 'The two new passwords do not match.',
  'kennwort.wechselnoetig': 'The password must be changed.',

  /* --- User administration (8.1, R358–R362) --- */
  'anwender.anlegen': 'Add user',
  'anwender.spalte.name': 'Name',
  'anwender.spalte.rolle': 'Role',
  'anwender.spalte.rechte': 'Permissions',
  'anwender.spalte.geltung': 'Scope',
  'anwender.spalte.zuletzt': 'Last signed in',
  'anwender.nie': 'never',
  'anwender.gesperrt': 'locked',
  'anwender.musswechseln': 'password change pending',
  'anwender.aufallem': 'everything',
  'anwender.aktiv': 'Access active',
  'anwender.erstkennwort': 'Initial password',
  'anwender.neueskennwort': 'Set a new password',
  'anwender.kennwortbleibt': 'leave empty — the current one stays',
  'anwender.rollevorschlag':
    'The role sets the tile board and proposes the permissions. What counts is below — permissions belong to the user.',
  'anwender.geltung.leer':
    'Empty means everything. A consolidation group is visible only if all its entities are in scope — consolidated statements without one entity would not be statements but a wrong figure.',
  'anwender.geltung.einheiten': 'Entities',
  'anwender.geltung.ledger': 'Measurement layers',
  'anwender.geltung.von': 'Periods from',
  'anwender.geltung.bis': 'Periods to',
  'anwender.blatt.anlegen': 'Add user',
  'anwender.blatt.aendern': 'Change user',
  'anwender.namevergeben': 'That user name already exists.',
  'anwender.letzterverwalter':
    'This is the last user who may administer. Without them nobody could grant permissions again.',
  /* Machine identities carry the name of their origin (7.2 section 20, R838) */
  'anwender.einrichtung': 'Setup',
  'anwender.datenimport': 'Data import',
  'anwender.unbekannt': 'unknown',

  'verb.lesen': 'Read — view reports, journal and structures',
  'verb.lesen.kurz': 'read',
  'verb.pflegen': 'Maintain — change master data and structures',
  'verb.pflegen.kurz': 'maintain',
  'verb.importieren': 'Import — submit, map, accept',
  'verb.importieren.kurz': 'import',
  'verb.rechnen': 'Calculate — run a closing explicitly',
  'verb.rechnen.kurz': 'calculate',
  'verb.freigeben': 'Release — release a closing',
  'verb.freigeben.kurz': 'release',
  'verb.freigabe_zuruecknehmen': 'Withdraw a release',
  'verb.freigabe_zuruecknehmen.kurz': 'withdraw',
  'verb.periode_wiedereroeffnen': 'Reopen a period',
  'verb.periode_wiedereroeffnen.kurz': 'reopen',
  'verb.verwalten': 'Administer — users, permissions, rule set, operations',
  'verb.verwalten.kurz': 'administer',
  'verb.pruefregeln_festlegen': 'Define check rules — how strictly the release is checked',
  'verb.pruefregeln_festlegen.kurz': 'check rules',

  /* --- The auditor’s first tile (R353) --- */
  'abschlusstafel.titel': 'Closings and their check codes',
  'abschlusstafel.leer': 'No closing has been created yet.',
  'abschlusstafel.ohnecode': 'not yet calculated',

  'kachel.groesse.befunde': 'Blocking findings',
  'kachel.groesse.bilanzdifferenz': 'Balance sheet difference',
  'kachel.groesse.nachweis': 'Entries in the audit trail',
  'kachel.groesse.antwortzeit': 'Response time, 95 % of calls',
  'kachel.groesse.buchungen': 'Postings in the calculation',
  'kachel.groesse.vorgaenge': 'Steps in the calculation',

  /* --- Findings of the formula language (R342) --- */
  'formel.leer': 'The formula is empty.',
  'formel.bezug.offen': 'The reference “{text}” is not closed — the bracket “]” is missing.',
  'formel.bezug.leer': 'There is no reference between the square brackets.',
  'formel.zahl.unlesbar': '“{text}” is not a number.',
  'formel.zeichen.unbekannt': 'The character “{zeichen}” does not belong in a formula.',
  'formel.operand.fehlt': 'After “{zeichen}” a number or a reference is missing.',
  'formel.klammer.zu': 'A closing bracket is missing.',
  'formel.klammer.auf': 'A closing bracket stands without an opening one.',
  'formel.ueberhang': 'After the calculation stands “{text}” — that is no longer part of it.',
  'formel.funktion.unbekannt': 'There is no function “{name}”. There are: {vorrat}.',
  'formel.funktion.klammer': 'After “{name}” the opening bracket is missing.',
  'formel.funktion.stelligkeit': '“{name}” expects {erwartet} values, here are {tatsaechlich}.',
  'formel.funktion.mindestens':
    '“{name}” expects at least {erwartet} values, here are {tatsaechlich}.',
  'formel.bezug.unbekannt': 'This report does not carry the reference “{bezug}”.',
  'formel.nenner.null': 'Not calculable: the denominator “{nenner}” is zero.',
  'formel.nenner.null.ohne': 'Not calculable: this divides by zero.',
  'formel.verlauf.gestellt':
    'Only line items can be drawn along the time axis — “{bezug}” is a figure of the current closing.',
  'formel.waehrungen':
    'The references are in {eine} and {andere}. A ratio across two currencies is not a figure.',

  /* --- Filtering at the column head (7.6 section 4, R650) -------------- */

  'filter.hilfe': 'Filter {spalte}',
  'filter.aktiv.hilfe': '{spalte} — filtered. Click to change',
  'filter.enthaelt': 'contains',
  'filter.von': 'from',
  'filter.bis': 'to',
  'filter.loesen': 'Clear filter',
  'filter.fertig': 'Done',
  'filter.alleloesen': 'Clear all filters',
  'filter.wirkt': '{spalten} filters active: {gezeigt} of {gesamt} rows.',

  /* --- Exchange rates (7.6) -------------------------------------------- */

  'kurse.paare': 'Exchange rate tables',
  'kurse.paare.suche': 'Currency or entity',
  'kurse.paare.keine': 'No table matches this search.',
  'kurse.paar.bestand': '{von} to {bis}',
  'kurse.paar.luecken': '{anzahl} months without a closing rate',
  'kurse.paar.vorgeschlagen': 'No table yet — reported in by: {einheiten}',
  'kurse.paar.angelegt': 'Created — no rates yet',
  'kurse.paar.ohneezb': 'The ECB publishes no reference rate for {waehrung}. Fill this table via “Read from file”.',
  'kurse.paar.ohneeuro': 'The ECB quotes against the euro only. Fill this table via “Read from file”.',

  'kurse.tabelle.neu': 'New table',
  'kurse.tabelle.neu.titel': 'New exchange rate table',
  'kurse.tabelle.neu.von': 'From currency',
  'kurse.tabelle.neu.nach': 'To currency',
  'kurse.tabelle.neu.erlaeuterung':
    'Both currencies come from the list under Master data → Currencies. Anything missing there has to be created there first.',
  'kurse.tabelle.anlegen': 'Create',
  'kurse.tabelle.unvollstaendig.meldung': 'A table needs two currencies.',
  'kurse.historisch.abgewiesen.meldung': 'The monthly table no longer accepts a historical rate.',
  'kurse.historisch.abgewiesen.weg': 'Historical rates are kept per company under Master data › Historical rates.',
  'kurse.tabelle.gleich.meldung': 'Converting {waehrung} into {waehrung} always gives one — that is not a table.',
  'kurse.tabelle.schonda.meldung': 'The table {von} → {nach} already exists.',

  'kurse.leer.titel': 'Choose an exchange rate table',
  'kurse.leer.text':
    'On the left are the tables you maintain and those that should exist given your entities and consolidation groups.',

  'kurse.zeitraum.von': 'From period',
  'kurse.zeitraum.bis': 'To period',

  'kurse.spalte.periode': 'Period',
  'kurse.spalte.stichtag': 'Closing rate',
  'kurse.spalte.durchschnitt': 'Average rate',
  'kurse.spalte.ytd': 'Year to date',
  'kurse.spalte.herkunft': 'Origin',
  'kurse.spalte.paar': 'Currencies',
  'kurse.spalte.art': 'Rate type',
  'kurse.spalte.bisher': 'Current',
  'kurse.spalte.neu': 'New',
  'kurse.spalte.befund': 'Finding',

  'kurse.art.stichtag': 'Closing',
  'kurse.art.durchschnitt': 'Average',
  'kurse.art.ytd': 'Year to date',
  'kurse.art.historisch': 'historical',

  'kurse.tafel.erlaeuterung':
    'An amount in {von} times the rate gives the amount in {nach}. The financial year starts in month {beginn} — that is what the year-to-date rate averages over.',
  'kurse.tafel.leer': 'No row in this range.',
  'kurse.zelle.hilfe': 'Click to overwrite',
  'kurse.ytd.vorschlag.hilfe':
    'What CoKo would calculate from the monthly averages. Take it over via “Calculate year to date”.',

  'kurse.herkunft.importiert': 'imported',
  'kurse.herkunft.manuell': 'manual',
  'kurse.herkunft.fortgeschrieben': 'carried forward',
  'kurse.herkunft.gerechnet': 'calculated',
  'kurse.herkunft.unbekannt': 'not recorded',

  'kurse.quelle.datei': 'Read a file',
  'kurse.sammel': 'Fetch every table from the ECB',
  'kurse.sammel.hinweis':
    'The ECB supplies reference rates for every table against the euro — including the ones that are still empty. A pair without the euro then appears as a rejected row in the preview.',
  'kurse.sammel.hilfe':
    'Fetch all exchange rate tables at once from {adresse} — no data is sent. Nothing is written until you confirm the preview.',
  'kurse.quelle.ezb': 'Fetch from the ECB',
  'kurse.quelle.ezb.hilfe': 'Fetch reference rates from {adresse} — no data is sent.',
  'kurse.quelle.ezb.zu':
    'This installation has no outbound route configured. Download the file from the ECB site and read it in.',
  'kurse.ezb.warten': 'Fetching the rates from the ECB — this takes at most 30 seconds.',
  'kurse.ezb.warten.erneut':
    'The ECB is not answering yet. Trying again; the fetch stops after 30 seconds ({sekunden} of 30 seconds).',
  'kurse.quelle.ytd': 'Calculate year to date',
  'kurse.quelle.ytd.hilfe':
    'From the monthly averages since the start of the financial year — as a proposal you take over.',

  'kurse.nachweis': 'Origin of the cell',
  'kurse.nachweis.leer': 'Click a rate to see where it came from.',
  'kurse.nachweis.keinkurs': 'No rate is maintained here for this month.',
  'kurse.nachweis.ytdvorschlag': 'CoKo would calculate {wert}.',
  'kurse.nachweis.wert': 'Rate',
  'kurse.nachweis.woher': 'Exactly where from',
  'kurse.nachweis.erfasst': 'Entered',
  'kurse.zuruecknehmen': 'Withdraw rate',
  'kurse.zuruecknehmen.hinweis':
    'Withdrawing deletes nothing: the row stays readable in the version series, and the month shows as a gap again.',


  'kurse.vorschau.datei': 'From the file',
  'kurse.vorschau.ezb': 'From the ECB',
  'kurse.vorschau.ytd': 'Calculated year-to-date rate',
  'kurse.vorschau.leer':
    'This yields no row. Check the headings: Period · From · To · Closing · Average · Year to date.',
  'kurse.vorschau.uebernehmen': 'Accept',
  'kurse.vorschau.abbrechen': 'Discard',

  'kurse.befund.neu': 'new',
  'kurse.befund.geaendert': 'changed',
  'kurse.befund.unveraendert': 'unchanged',
  'kurse.befund.abgewiesen': 'rejected',



  /* --- The Periods area (2.19, R670-R675) --- */
  'perioden.zweck': 'This installation keeps data only within this span. CoKo rejects a delivery before or after it, whichever way it arrives.',
  'perioden.anfang': 'Starts with',
  'perioden.ende': 'Ends with',
  'perioden.setzen': 'Set the span',
  'perioden.vorrat': '{monate} months long, {vorrat} of them still ahead of you.',
  'perioden.vorrat.knapp':
    'Only {vorrat} months left. Extend the span before the first delivery is rejected.',
  'perioden.vorrat.abgelaufen':
    'The span ran out with {ende}: every delivery for a later month is rejected.',
  'perioden.bestand': 'Postings run from {aelteste} to {juengste} — the span must cover at least that much.',
  'perioden.bestand.leer': 'Nothing has been posted yet — the span can be set freely.',
  'perioden.fassung': 'Last set by {wer} on {wann}.',
  'perioden.ohne.titel': 'This installation has no span',
  'perioden.ohne.text': "Without a span CoKo checks nothing: a delivery for any month at all would go through. Set a start and an end — they apply from CoKo's next update.",

  'abbruch.zeitachse.anfang':
    'This data model begins with {grenze}. What was delivered for {periode} has not been taken over.',
  'abbruch.zeitachse.anfang.weg':
    'If this installation is to keep earlier months as well, move the start back under Periods — then deliver again.',
  'abbruch.zeitachse.ende':
    'The time axis of this installation ends with {grenze}. Nothing can be posted for {periode}.',
  'abbruch.zeitachse.ende.weg':
    'Extend the span under Periods. Check the month of the delivery while you are at it: a mistyped year looks exactly like this.',
  'abbruch.zeitachse.bestand.anfang':
    '{periode} holds {zeilen} posting lines. The start cannot lie after it.',
  'abbruch.zeitachse.bestand.ende':
    '{periode} holds {zeilen} posting lines. The end cannot lie before it.',
  'abbruch.zeitachse.bestand.weg':
    'Widening always works. To narrow the span, first delete what would then lie outside it.',
  'abbruch.zeitachse.unstimmig': '{anfang} to {ende} is not a span.',
  'abbruch.zeitachse.unstimmig.weg': 'The start must lie before the end — or be the same month.',
  'abbruch.datenbank.beleg.gruppe':
    'Document {nummer} is to be posted into a group. A group is the sum of its layers and carries no posting of its own.',
  'abbruch.datenbank.beleg.gruppe.weg': 'Choose a delta layer as valuation layer, such as German GAAP adjustments.',
  'abbruch.datenbank.beleg.rohdaten':
    'Document {nummer} is to be posted into the raw data. Only what the company delivered stands there.',
  'abbruch.datenbank.beleg.rohdaten.weg': 'Post adjustments into a delta layer, such as German GAAP adjustments.',
  'abbruch.datenbank.beleg.nichtauf': 'Document {nummer} does not balance in {waehrung}: {betrag} is missing.',
  'abbruch.datenbank.beleg.nichtauf.weg': 'Balance debit and credit. No balancing line is created automatically.',
  'abbruch.datenbank.berichtigen.unveraenderlich':
    'Document {nummer} was not corrected: number, company, valuation layer and period of a document stay as they are.',
  'abbruch.datenbank.berichtigen.unveraenderlich.weg': 'This cannot be fixed on screen. Please notify operations.',
  'abbruch.datenbank.berichtigen.belegart':
    'Document {nummer} was not entered by hand and therefore cannot be corrected.',
  'abbruch.datenbank.berichtigen.belegart.weg': 'Reverse it, or correct it with a posting of your own.',
  'abbruch.datenbank.berichtigen.storniert':
    'Document {nummer} has been reversed by {storno} and can no longer be corrected.',
  'abbruch.datenbank.berichtigen.storniert.weg': 'Enter the correction as a new document.',
  'abbruch.datenbank.berichtigen.protokoll':
    'Document {nummer} was not corrected because its previous state could not be recorded.',
  'abbruch.datenbank.berichtigen.protokoll.weg': 'This cannot be fixed on screen. Please notify operations.',
  'abbruch.datenbank.loeschen.belegart':
    'Document {nummer} cannot be deleted. Only documents entered by hand and data imports can be deleted.',
  'abbruch.datenbank.loeschen.belegart.weg': 'Reverse it.',
  'abbruch.datenbank.loeschen.geschlossen':
    'Data import {nummer} belongs to {periode}, and that close is locked.',
  'abbruch.datenbank.loeschen.geschlossen.weg':
    'A new file is still accepted and kept as a late posting. Deleting and replacing works only once the period is reopened.',
  'abbruch.datenbank.loeschen.storniert':
    'Document {nummer} has been reversed by {storno} and can no longer be deleted.',
  'abbruch.datenbank.loeschen.storniert.weg':
    'Leave both in place: a reversal without the document it cancels would be a posting without reference.',
  'abbruch.datenbank.loeschen.protokoll':
    'Document {nummer} was not deleted because its state could not be recorded.',
  'abbruch.datenbank.loeschen.protokoll.weg': 'This cannot be fixed on screen. Please notify operations.',
  'abbruch.datenbank.sprache.unbekannt': 'The language {sprache} is not set up or has been retired.',
  'abbruch.datenbank.sprache.unbekannt.weg': 'Choose a language that is set up.',
  'abbruch.datenbank.saldowechsel.summe': 'This position is a total and cannot carry a balance switch.',
  'abbruch.datenbank.saldowechsel.summe.weg': 'Set the balance switch on a position below it.',
  'abbruch.datenbank.saldowechsel.zielfehlt': 'The chosen target position does not exist in this structure.',
  'abbruch.datenbank.saldowechsel.zielfehlt.weg': 'Choose a position of the same structure.',
  'abbruch.datenbank.saldowechsel.zielsumme':
    'The chosen target position is a total and carries no value of its own.',
  'abbruch.datenbank.saldowechsel.zielsumme.weg': 'Choose a position without sub-positions.',
  'abbruch.datenbank.saldowechsel.bericht':
    'The chosen target position belongs to a different report than this position.',
  'abbruch.datenbank.saldowechsel.bericht.weg':
    'A balance switch stays within the balance sheet or income statement where it starts. Choose a target in the same report.',
  'abbruch.datenbank.saldowechsel.kreis':
    'The balance switch leads through its targets in a circle back to this position.',
  'abbruch.datenbank.saldowechsel.kreis.weg': 'Choose a target that does not forward back here.',
  'abbruch.datenbank.saldowechsel.kontobericht':
    'An account with balance reversal can only sit under a line item of the same statement as its target.',
  'abbruch.datenbank.saldowechsel.kontobericht.weg':
    'First adjust the balance reversal target on the account or its account group.',
  'abbruch.datenbank.saldowechsel.kontoposition':
    'Account and line item cannot both carry a balance reversal: the line item already moves as a whole.',
  'abbruch.datenbank.saldowechsel.kontoposition.weg':
    'Remove the target on the line item first if each account is to move on its own.',
  'abbruch.datenbank.saldowechsel.positionkonto':
    'Account and line item cannot both carry a balance reversal: an account of this line item already moves on its own.',
  'abbruch.datenbank.saldowechsel.positionkonto.weg':
    'Remove the target on the accounts or their account group first if the line item is to move as a whole.',
  'abbruch.erfassung.name': 'The document has no name.',
  'abbruch.erfassung.name.weg': 'Give it a short name under which you will find it again in the folder.',
  'abbruch.erfassung.begruendung': 'The reason is missing.',
  'abbruch.erfassung.begruendung.weg': 'State why you are intervening — the auditor reads it.',
  'abbruch.erfassung.zeilen': 'The document has fewer than two lines with an amount.',
  'abbruch.erfassung.zeilen.weg': 'A document posts debit against credit: enter at least two lines with an amount.',
  'abbruch.erfassung.konto': 'The account {konto} no longer exists.',
  'abbruch.erfassung.nurcoko': '{konto} is a technical account delivered with CoKo — only CoKo posts to it.',
  'abbruch.erfassung.konto.weg': 'Choose a different account in this line.',
  'abbruch.erfassung.bewegungsart.unbekannt': 'The movement type {art} no longer exists.',
  'abbruch.erfassung.bewegungsart.unbekannt.weg': 'Choose a different movement type in this line.',
  'abbruch.erfassung.bewegungsart': 'The movement type {art} is not permitted on {konto}.',
  'abbruch.erfassung.bewegungsart.weg':
    'The account belongs to a schedule. Choose a movement of that schedule, such as an addition or a reclassification.',
  'abbruch.erfassung.bewegungsart.guv.weg':
    'An income statement account is posted without a movement type. Leave the field empty.',
  'abbruch.erfassung.doppelt': 'Two lines on {konto} do not differ in anything.',
  'abbruch.erfassung.doppelt.weg':
    'Combine them into one line, or make them differ in one attribute, such as the movement type.',
  'abbruch.aendern.belegart': 'Document {nummer} was not entered by hand and therefore cannot be corrected.',
  'abbruch.aendern.belegart.weg': 'Reverse it, or correct it with a posting of your own.',
  'abbruch.aendern.storniert': 'Document {nummer} has been reversed by {storno} and can no longer be corrected.',
  'abbruch.aendern.storniert.weg': 'Enter the correction as a new document.',
  'abbruch.umkehrung.belegart':
    'Document {nummer} was not entered by hand and therefore cannot be released in the following period.',
  'abbruch.umkehrung.belegart.weg': 'Only documents entered by hand are released.',
  'abbruch.umkehrung.schon': 'Document {nummer} has already been released in the following period by {umkehrung}.',
  'abbruch.umkehrung.schon.weg': 'A document is released once. Have a look at {umkehrung}.',
  'abbruch.umkehrung.kette': 'Document {nummer} is itself a release and is not released again.',
  'abbruch.umkehrung.kette.weg': 'If the facts are different, reverse the release and enter anew.',
  'abbruch.umkehrung.zeitpunkt': 'There is no release “{zeitpunkt}”.',
  'abbruch.ergebnisvortrag-ohne-vormonat.meldung':
    '{einheit} carries a result brought forward in foreign currency. It stands at the value from {vormonat} — the run for it is missing.',
  'abbruch.ergebnisvortrag-ohne-vormonat.weg': 'Calculate {vormonat} first.',
  'abbruch.umkehrung.zeitpunkt.weg':
    'Choose in the document folder whether the document ends in the following period, at the end of the financial year or at the start of the new one.',
  'abbruch.beleg.unbekannt': 'This document no longer exists.',
  'abbruch.beleg.unbekannt.weg': 'Reload the folder — someone has probably deleted it in the meantime.',
  'abbruch.spiegelart.meldung': 'There is no schedule "{art}".',
  'abbruch.spiegelart.weg': 'There are the fixed asset, equity and liabilities schedules.',
  'abbruch.raster.meldung': 'There is no grid "{raster}".',
  'abbruch.raster.weg': 'Figures are calculated per month, quarter or year.',
  'import.mapping.widerspruch.meldung': 'The local number {lokal} with this name is already mapped to {konto}.',
  'import.mapping.widerspruch.weg':
    'A number with the same name belongs to one account. Change the existing mapping instead of creating a second one.',
  /* R1386, R1388 — Inclusion is required for every included investment, exit once it ends. */
  'beteiligung.eintritt.pflicht.weg':
    'Choose whether the company enters at the beginning or at the end of the period. This decides whether the result of the acquisition month appears in the group P&L.',
  'beteiligung.austritt.pflicht.weg':
    'The investment ends. Choose whether the company leaves at the beginning or at the end of that period.',
  'beteiligung.aufsatz.pflicht.weg':
    'Each takeover type needs its entries: values at acquisition the useful life of goodwill, balances at takeover the remaining goodwill, remaining useful life and non-controlling interests. Enter it or choose another takeover type.',
  'beteiligung.aufsatz.ohne-art.meldung': 'The field “{feld}” belongs to the takeover, but no takeover type is chosen.',
  'beteiligung.aufsatz.ohne-art.weg': 'Choose the takeover type or clear the field. A subsidiary acquired in CoKo needs no takeover.',
  'beteiligung.aufsatz.nach-erwerb.meldung': 'A takeover only applies to a subsidiary acquired before its first month in CoKo.',
  'beteiligung.aufsatz.nach-erwerb.weg': 'Enter the acquisition period; it must lie before the takeover month. If the acquisition is in the takeover month or later, the delivery counts and the takeover type stays empty.',
  'erstkonsolidierung.nur-aufsatz.meldung': 'Equity at inclusion is only recorded for a takeover with values at acquisition.',
  'erstkonsolidierung.nur-aufsatz.weg': 'Choose the takeover type “values at acquisition” for the investment. If the subsidiary was acquired in CoKo, its delivery counts; a wrong delivery is corrected in the adjusted statements.',
  'abbruch.beteiligung.ohne-eintritt.meldung':
    'The investment in {tochter} does not say from when it is included.',
  'abbruch.beteiligung.ohne-eintritt.weg':
    'Choose “Included from” in the investment register. Until then the capital consolidation does not calculate.',
  'stammdaten.pflicht.meldung': 'The field "{feld}" is empty. Nothing can be saved without it.',
  'meldung.kein-positionsplan':
    'No structure is released for this valuation layer. The run therefore has no lines; release a structure under Master data.',
  'abbruch.ezb.paar': 'The ECB quotes only against the euro; {von} to {nach} does not exist there.',
  'abbruch.ezb.paar.weg':
    'Fetch both rates against the euro and enter the cross rate by hand — a calculated cross rate is proposed, never used automatically.',
  'abbruch.ezb.nichterreichbar': 'The ECB did not answer within 30 seconds — {versuche} attempts.',
  'abbruch.ezb.nichterreichbar.weg':
    'Fetch the rates again in a few minutes. If your data centre does not allow outbound calls, download the rate file from the ECB site and read it in via “Read a file” — the preview and the record of origin are the same.',
  'abbruch.ezb.leer': 'The ECB published no rate for {waehrung} → {gegenwaehrung} from {von} to {bis}.',
  'abbruch.ezb.leer.alle': 'The ECB published no rate for any rate table from {von} to {bis}.',
  'abbruch.ezb.leer.weg':
    'The ECB publishes rates only for days that have passed. Choose a period that has already begun.',
  /* --- Eingabefehler: fachlich, am Ort (7.2 Abschnitt 30, R1004) --- */
  'eingabe.keinedatei': 'No file is selected.',
  'eingabe.keinedatei.weg': 'Select a file and start again.',
  'eingabe.package.einemappe': 'A reporting package is exactly one workbook.',
  'eingabe.package.einemappe.weg':
    'For several entities or periods, upload each workbook on its own (a package is one item).',
  'eingabe.package.gesellschaft': 'A package applies to one entity, and none is selected.',
  'eingabe.package.gesellschaft.weg': 'Select the entity; it is compared with the cover sheet of the workbook.',
  'eingabe.quelle.unbekannt': 'The data source {quelle} does not exist.',
  'eingabe.quelle.unbekannt.weg': 'Select a data source from the list.',
  'eingabe.quelle.nichtlesbar': 'The source {quelle} cannot be read from today ({art}).',
  'eingabe.quelle.nichtlesbar.weg':
    'File sources work. For databases and LucaNet extracts a person delivers the file today — choose a file source for it.',
  'eingabe.lieferung.einheitperiode': 'Entity or period is missing for the delivery.',
  'eingabe.lieferung.einheitperiode.weg':
    'Choose both when starting the import — a source applies to every entity that delivers this way.',
  'eingabe.stapel.gesellschaft': 'No entity is selected for the batch.',
  'eingabe.stapel.gesellschaft.weg': 'Select the entity — the files only bring the period.',
  'eingabe.stapel.quelle': 'No data source is selected for the batch.',
  'eingabe.stapel.quelle.weg':
    'Select the data source — it says whether the period comes from the file name or from a fixed cell.',
  'abbruch.kurs-fehlt.ytd':
    'For {waehrung} to {konzernwaehrung} the year-to-date rate is missing in period {periode}.',
  /* The entry form does not post into a closed period (2.16). The sentence was
     missing altogether until 2026-09-30; the period arrives as a period and
     reads as month and year (R839) */
  'abbruch.erfassung.geschlossen':
    '{periode} is closed in group {kreis} — nothing more is posted in this month.',
  'abbruch.periode.geschlossen.meldung': '{periode} is closed in group {kreis} and is not recalculated.',
  'abbruch.periode.geschlossen.weg':
    'A change belongs in {folge}, with the balances or a posting there. To change {periode} itself, reopen it in the [[Group close monitor]].',
  'abbruch.periode.geschlossen.weg.jahresende':
    '{periode} ends the financial year; a change cannot follow in the new year. Reopen {periode} in the [[Group close monitor]].',
  'datenimport.geschlossen.meldung': '{periode} is closed in group {kreis} — this delivery is not taken over.',
  'konzernabschluss.geschlossen.meldung': '{periode} is closed and was not recalculated; its saved run applies.',
  'monitor.schluss.schon.meldung': '{periode} is already closed.',
  'monitor.schluss.schon.weg': 'Reload the page — the month has been closed in the meantime.',
  'monitor.schluss.offen.meldung': '{periode} is not closed.',
  'monitor.schluss.offen.weg': 'Reload the page — the month has been reopened in the meantime.',
  'monitor.zuruecknahme.nichtfrei.meldung': '{periode} is not released.',
  'monitor.zuruecknahme.nichtfrei.weg': 'Only a release can be withdrawn. Reload the page.',
  'monitor.zuruecknahme.testiert.meldung': '{periode} is audited.',
  'monitor.zuruecknahme.testiert.weg': 'An audited closing is not reopened; a later correction is a second run.',
  'monitor.begruendung.fehlt.meldung': 'The reason is missing.',
  'monitor.begruendung.fehlt.weg': 'Say why in one sentence — it is kept in the audit trail.',
  'schloss.titel': 'Closing and release',
  'schloss.zustand.geschlossen': '{periode} is closed.',
  'schloss.zustand.geschlossen.folge': 'No delivery, no posting and no recalculation for this month.',
  'schloss.zustand.offen': '{periode} is open.',
  'schloss.zustand.offen.folge': 'Deliveries and postings take effect in this month; it can be recalculated.',
  'schloss.schliessen': 'Close period',
  'schloss.schliessen.frage':
    'Close {periode}? The month then accepts no delivery and no posting and is not recalculated. A correction comes with the balances of the following month.',
  'schloss.schliessen.frage.jahresende':
    'Close {periode}? The month then accepts no delivery and no posting and is not recalculated. Because it ends the financial year, a correction cannot follow with the January balances — reopen it for that.',
  'schloss.schliessen.bestaetigen': 'Close month',
  'schloss.wiedereroeffnen': 'Reopen period',
  'schloss.wiedereroeffnen.frage': 'Reopen {periode}? The month then accepts deliveries and postings again and can be recalculated.',
  'schloss.wiedereroeffnen.bestaetigen': 'Reopen',
  'schloss.zuruecknehmen': 'Withdraw release',
  'schloss.zuruecknehmen.frage': 'Withdraw the release of {periode}? The released states are kept with their check code.',
  'schloss.zuruecknehmen.betroffen': 'These months lose their release:',
  'schloss.zuruecknehmen.bestaetigen': 'Withdraw release',
  'schloss.begruendung': 'Reason',
  'schloss.erfolg.geschlossen': '{periode} is closed.',
  'schloss.erfolg.wiedereroeffnet': '{periode} is reopened.',
  'schloss.erfolg.zurueckgenommen': 'Release withdrawn: {liste}.',
  'abbruch.erfassung.geschlossen.weg':
    'Post in an open period. Reopening a closed period is a permission of its own and is done at the closing, not in the entry form.',

  ja: 'Yes',
  nein: 'No',

  /* How a period is written in the input field (R502) — since 2026-09-30 the
     field accepts several notations and names them in its hint (R839) */
  'periode.muster': 'MM.YYYY',
  'periode.muster.hinweis': 'Accepted: 12.2026, 12/2026, 12-2026, 2026-12 and 202612.',
  'periode.ungueltig':
    'This does not read as a period. Accepted: 12.2026, 12/2026, 12-2026, 2026-12 and 202612 — month and year, for example 09.2026 for September 2026.',
  /* How a number is written in the number field of master data (7.2 section
     22, R840) — the examples follow the user's number format */
  'zahl.muster.hinweis': 'Accepted: {mit} and {ohne}.',
  'zahl.ungueltig': 'This does not read as a number. Accepted: {mit} and {ohne}.',
  'anteil.muster.hinweis': 'In percent: 80, 80 % or {beispiel}.',
  'anteil.ungueltig':
    'This does not read as a share. Type it in percent between 0 and 100 — for example 80 for eighty percent, or {beispiel}.',
  'anlage.nummer.ungueltig':
    'An account number consists of digits only. What the account is belongs in the name, not in the number.',
  'anlage.konto.nichtnumerisch.meldung':
    'The number {nummer} is not an account number: an account number consists of digits only.',
  'anlage.konto.nichtnumerisch.weg':
    'Enter the digits as the number and the wording as the name. The number becomes the key and cannot be changed afterwards.',

  /* Cash flow statement: new elements are created below only (2.1 section 3.6, R1706) */
  'anlage.titel.kfrzeile': 'New line item under {position}',
  'anlage.titel.kfrsummenzeile': 'New total line under {position}',
  'reihenfolge.menue': 'Change order …',
  'reihenfolge.titel': 'Change order · {element}',
  'reihenfolge.liste': 'The elements of this level',
  'reihenfolge.anfang': 'To the top',
  'reihenfolge.hoch': 'Up',
  'reihenfolge.runter': 'Down',
  'reihenfolge.ende': 'To the bottom',
  'reihenfolge.sortieren': 'Sort alphanumerically',
  'reihenfolge.immer': 'Always keep sorted alphanumerically',
  'reihenfolge.uebernehmen': 'Apply',
  'reihenfolge.fehler': 'The order could not be changed.',
  'reihenfolge.veraltet.meldung': 'The level changed while the window was open. Please open it again.',
  'reihenfolge.element.meldung': '{element} is not on a level whose order can be changed.',
  'reihenfolge.art.meldung': 'There is no order to change for {art}.',
  'kfr.loeschen.titel': 'Delete line item',
  'kfr.loeschen.titel.summenzeile': 'Delete total line',
  'kfr.loeschen.frage': 'Delete {zeile}?',
  'kfr.loeschen.ohnereferenzen': 'Nothing hangs below it. It no longer counts anywhere afterwards.',
  'kfr.loeschen.wirdzeile': 'No line item hangs below {summe} afterwards.',
  'kfr.loeschen.knopf': 'Delete',
  'kfr.loeschen.fehler': '{zeile} could not be deleted.',
  'kfr.loeschen.hindernis.rolle': '{zeile} has a fixed task in the statement and cannot be deleted.',
  'kfr.loeschen.hindernis.referenzen':
    '{anzahl} references hang below {zeile}. Delete the references first; a line item with something below it is not deleted.',
  'kfr.loeschen.hindernis.summe': 'Line items hang below {zeile}. Delete the line items below it first.',
  'kfr.loeschen.hindernis.ohnesumme': '{zeile} counts in no total line and therefore cannot be deleted.',
  'kfr.loeschen.hindernis.letzte': '{zeile} is the last line item below {summe} in an older version and cannot be deleted.',
  'stammdaten.nummer': 'Number',
  'stammdaten.art.kfr_abschnitt': 'Sections of the cash flow statement',
  'stammdaten.art.kfr_fonds': 'Cash and cash equivalents',
  'stammdaten.zweck.kfr_fonds.position': 'Which balance sheet line item belongs to cash and cash equivalents — with all items below it',
  'stammdaten.zweck.kfr_fonds.konto': 'Which single account belongs to cash and cash equivalents',
  'stammdaten.art.kfr_ausnahme': 'Movement excluded from the cash flow statement',
  'stammdaten.zweck.kfr_ausnahme.position': 'Which balance sheet or income statement line item the exclusion names — with all items below it',
  'stammdaten.zweck.kfr_ausnahme.konto': 'Which single account the exclusion names',
  'stammdaten.zweck.kfr_ausnahme.bewegungsarten': 'Which movement types no line of the cash flow statement may name — each ticked explicitly',
  'stammdaten.zweck.kfr_abschnitt.standard': 'Which standard the section belongs to — DRS 21 or IAS 7',
  'stammdaten.zweck.kfr_abschnitt.bereich': 'Which of the four sections: operating, investing, financing or cash',
  'anlage.kfr.machtzursumme': '{position} then adds up its references and the line items below it.',
  'anlage.hindernis.summenzeile': '{position} is a total line. Nothing hangs below a total line.',
  'anlage.hindernis.fondsabschnitt':
    'Nothing is created below cash and cash equivalents: cash at the end would not count it.',
  // References as their own lines (2.1 section 3.6, R1705, R1710, R1712).
  'kfr.referenz.umgekehrt': 'sign reversed',
  // Where an amount comes from, before the name (2.1 section 3.6, R1730).
  'element.kurz.referenz': 'Ref.',
  'element.kurz.statistik': 'Stat.',
  'element.art.referenz': 'Reference — the amount comes from the balance sheet or income statement',
  'element.art.statistik': 'Statistical account — the amount is posted by hand',
  // The statistical account (2.1 section 3.6, R1715, R1726–R1732).
  'kfr.statistik.anlegen.titel': 'New account under {position}',
  'kfr.statistik.anlegen.hinweis':
    'An account in the cash flow statement is a statistical account: it is posted by hand, ' +
    'without debit and credit, and does not appear in the balance sheet or income statement.',
  'erfolg.statistikkonto.angelegt': 'Statistical account created successfully',
  'kfr.befund.statistik-kurs':
    'The average rate {waehrung} → {konzernwaehrung} is missing for {periode}. Statistical accounts in {waehrung} ' +
    'are not included this month.',
  'beleg.spalte.betrag': 'Amount (+/−)',
  'beleg.statistik.hinweis':
    'Statistical accounts are not subject to bookkeeping rules: no debit and credit, and the document ' +
    'need not balance. Enter the amount with + or − exactly as it should appear in the cash flow statement.',
  'beleg.statistik.bewegungsart': 'Statistical account — no movement type',
  'beleg.statistik.gemischt':
    'A statistical account cannot share a document with other accounts. Post it in a document of its own.',
  'abbruch.erfassung.statistikgemischt': 'The document contains statistical accounts and other accounts.',
  'abbruch.erfassung.statistikgemischt.weg':
    'Post the statistical accounts in a document of their own — a statistical document contains only statistical accounts.',
  'abbruch.datenbank.beleg.statistikgemischt':
    'Document {nummer} contains statistical accounts and other accounts.',
  'abbruch.datenbank.beleg.statistikgemischt.weg':
    'Post the statistical accounts in a document of their own — a statistical document contains only statistical accounts.',
  'abbruch.datenbank.konto.statistikwechsel':
    'An account was not changed: it never becomes a statistical account and never stops being one.',
  'abbruch.datenbank.konto.statistikwechsel.weg': 'This cannot be fixed on screen. Please notify operations.',
  'journal.art.statistik': 'Statistical document',
  'belegtyp.statistik': 'Statistical posting',
  'stammdaten.wert.statistik': 'Statistical account',
  'kfr.referenz.anlegen.titel': 'New reference under {position}',
  'kfr.referenz.bezeichnung.hinweis': 'Prefilled with the source. The name stands in the line; no check reads it.',
  'kfr.referenz.loeschen.titel': 'Delete reference',
  'kfr.referenz.loeschen.frage': 'Delete {referenz} under {position}?',
  'kfr.referenz.loeschen.hinweis': 'The line item then no longer draws what this reference drew.',
  'kfr.referenz.loeschen.fonds': 'What belonged to cash and cash equivalents through this reference no longer does.',
  'kfr.referenz.loeschen.fehler': 'The reference could not be deleted.',
  'erfolg.referenz.angelegt': 'Reference created successfully',
  'erfolg.referenz.geloescht': 'Reference deleted successfully',
  'kfr.referenz.unvollstaendig.meldung':
    'A reference is not complete yet. Choose the item/account reference and tick at least one movement type.',
  'kfr.summenzeile.zaehlweise.meldung':
    'Choose what the total line adds up: everything above or since the previous total line. A total line is created below the band of a section.',
  'kfr.summenzeile.referenzen.meldung': 'A total line draws nothing itself and therefore has no references.',
  'kfr.zeile.summenzeile.meldung': 'Nothing can be created below {zeile}: {zeile} is a total line.',
  'kfr.abschnitt.fonds.meldung': 'Nothing is created below cash and cash equivalents: cash at the end would not count it.',
  'kfr.zeile.keinplatz.meldung':
    'There is no room left next to {zeile} for another line. Add it at a neighbouring line.',
  'anlage.hindernis.kfrkonto':
    'No account hangs on a cash flow line: amounts reach it through its mapping of movement type and account group, not through an account below it.',
  'anlage.hindernis.keinesumme':
    'There is no level below a line that takes amounts. A new line is created under a subtotal — where it is counted.',
  'kfr.zeile.unbekannt.meldung': 'Line {zeile} does not exist in the line catalogue.',
  'kfr.zeile.ohnecode.meldung': 'No number, no line: the key is formed from it.',
  'kfr.zeile.ohnename.meldung': 'No name, no line. Enter what it should be called in the statement.',
  'kfr.zeile.keinesumme.meldung': 'No line can be created under {zeile}: {zeile} takes amounts itself.',
  'kfr.zeile.keinesumme.weg': 'Create the line under the subtotal it should feed into — then that subtotal counts it.',

  /* The fields of a cash flow line (R507) */
  'stammdaten.feld.standard': 'Layout standard',
  'stammdaten.feld.bereich': 'Section of the statement',
  'stammdaten.feld.rolle': 'Role in the statement',
  'stammdaten.feld.kontengruppe': 'Account group',
  'stammdaten.feld.bewegungsart': 'Movement type',
  'stammdaten.feld.bewegungsarten': 'Movement types',
  'stammdaten.feld.zeile': 'Line',
  'stammdaten.feld.alle_bewegungsarten': 'All movement types',
  'stammdaten.feld.vorzeichen_umkehren': 'Reverse sign',
  'stammdaten.wert.bereich.operativ': 'Operating activities',
  'stammdaten.wert.bereich.investition': 'Investing activities',
  'stammdaten.wert.bereich.finanzierung': 'Financing activities',
  'stammdaten.wert.bereich.fonds': 'Cash funds',
  'stammdaten.wert.rolle.zahlungsunwirksam': 'Non-cash items',
  'stammdaten.wert.rolle.erwerb': 'Acquisition of consolidated companies',
  'stammdaten.wert.rolle.veraeusserung': 'Sale of consolidated companies',
  'stammdaten.wert.rolle.wechselkurs': 'Exchange-rate change in cash funds',
  'stammdaten.wert.rolle.kreisaenderung': 'Scope-related change in cash funds',
  'stammdaten.wert.rolle.fonds_anfang': 'Cash funds at the beginning',
  'stammdaten.wert.rolle.fonds_ende': 'Cash funds at the end',
  'stammdaten.wert.rolle.periodenergebnis': 'Result for the period',
  'stammdaten.feld.zaehlweise': 'What it adds up',
  'stammdaten.wert.zaehlweise.abschnitt': 'All lines of its section',
  'stammdaten.wert.zaehlweise.darueber': 'Everything above',
  'stammdaten.wert.zaehlweise.seit_voriger': 'Since the previous total',
  'stammdaten.wert.zaehlweise.unterzeilen': 'Its references and the line items below it',
  'stammdaten.feld.schrift': 'Font',
  'stammdaten.feld.hintergrund': 'Background',
  'stammdaten.feld.schriftfarbe': 'Text colour',
  'stammdaten.wert.schrift.fett': 'bold',
  'stammdaten.wert.schrift.normal': 'normal',
  'stammdaten.wert.hintergrund.keiner': 'none',
  'stammdaten.wert.hintergrund.grau': 'grey',
  'stammdaten.wert.hintergrund.blau': 'blue',
  'stammdaten.wert.hintergrund.gruen': 'green',
  'stammdaten.wert.hintergrund.gelb': 'yellow',
  'stammdaten.wert.hintergrund.rot': 'red',
  'stammdaten.wert.hintergrund.violett': 'violet',
  'stammdaten.wert.schriftfarbe.grau': 'grey',
  'stammdaten.wert.schriftfarbe.blau': 'blue',
  'stammdaten.wert.schriftfarbe.gruen': 'green',
  'stammdaten.wert.schriftfarbe.rot': 'red',
  'stammdaten.wert.schriftfarbe.violett': 'violet',
  'stammdaten.wert.art.zeile': 'Line that takes amounts',
  'stammdaten.wert.art.summe': 'Subtotal over other lines',
  'stammdaten.wert.art.nachweis': 'Note below the statement',

  /* Deleting: two ways, and the user chooses (R509–R512) */
  'stammdaten.stelle.buchungsbelege': 'Journal documents',
  'stammdaten.stelle.datenimporte': 'Data imports',
  'loeschen.stammdatum.titel': 'Delete {code}',
  'loeschen.element.darunter.eins':
    '{name} cannot be deleted: one element still hangs below it. A line item with something below it is not deleted — cut the element and paste it elsewhere first, or delete it.',
  'loeschen.element.darunter':
    '{name} cannot be deleted: {anzahl} elements still hang below it. A line item with something below it is not deleted — cut the elements and paste them elsewhere first, or delete them.',
  'loeschen.nichtsgefunden':
    'Nothing stands on this element. It will be removed — the version history stays readable, and the change log records who removed it when.',
  'loeschen.gefunden': 'Where this element occurs',
  'loeschen.eigen.titel': 'Goes with it',
  'loeschen.eigen.text':
    'These assignments exist only for this element. They block nothing and disappear with it.',
  'loeschen.kinder.titel': 'Hangs on it',
  'loeschen.kinder.text':
    'These elements remain when this one goes. Say where they belong — without a target they would silently lose their place.',
  'loeschen.ziel': 'The elements below now belong to',
  'loeschen.weg': 'How should it be deleted?',
  'loeschen.weg.stilllegen': 'Deactivate from period — the past remains',
  'loeschen.weg.stilllegen.text':
    'The element no longer applies from the given period, and the elements below move from that same period. What was calculated before stays unchanged.',
  'loeschen.weg.entfernen': 'Remove entirely — without a period-accurate trail',
  'loeschen.weg.entfernen.text':
    'The elements below move as a correction, as if it had never been otherwise, and the element disappears. Meant for the case where a structural error surfaces at the very first closing and should not remain as dead weight in the model.',
  'loeschen.verzicht':
    'I am deliberately moving the elements below and expressly waive the period-accurate deactivation that preserves the past.',
  'loeschen.gesperrt':
    'Removing entirely is no longer possible: {anzahl} released closings know this element. Their check code covers exactly those rows — what it holds stands still.',
  'loeschen.tun.stilllegen': 'Deactivate from {periode}',
  'loeschen.tun.entfernen': 'Remove entirely',
  'loeschen.beleg.oeffnen': 'View document {nummer}',

  /* The refusals of the delete path */
  'loeschen.objektart.meldung': '{objektart} is not a maintained kind of master data.',
  'loeschen.unbekannt.meldung': 'This element no longer exists — someone probably deleted it. Reload the page.',
  'loeschen.freigegeben.meldung':
    'The closing {abschluss} for {periode} is released and knows this element.',
  'loeschen.freigegeben.weg':
    'Deactivate it from a period instead. A released closing is a snapshot: its check code covers the rows the element appears in — removing it would leave the check code out of step with what it secures.',
  'loeschen.ohneperiode.meldung': 'Deactivating needs the period it takes effect from.',
  'loeschen.ohneziel.meldung': '{anzahl} further elements hang on this one — without a target they are left without a place.',
  'loeschen.ohneziel.weg': 'Say where they belong from now on. They then move in the same step.',
  'loeschen.ohnestilllegung.meldung': '{objektart} has no deactivation — here there is only removal.',
  'loeschen.zielistselbst.meldung': 'The target is the element itself. The elements below need a different home.',

  'stammdaten.ohnecode.meldung': 'The field “Code” is empty.',
  'stammdaten.ohnecode.weg':
    'Enter the code, for example “EUR” for a currency or “DE” for a country. The entry can then be created.',
  'strukturanlage.konto.ohnebezeichnung.meldung': 'The new account has no name.',
  'strukturanlage.konto.ohnebezeichnung.weg':
    'Enter the name with the account number in front, for example “0200 Technical equipment”. The account can then be created.',

  /* The sections of the sheet fold open (R515) */
  'loeschen.gefunden.erlaeuterung':
    'Before {code} is deleted: this is where it occurs. The number tells you how much; the details fold open.',
  'loeschen.bewegung.text':
    'Posted and calculated rows stand on this element. Posted ones can be deleted in the journal, calculated ones are recreated by the next run.',
  'loeschen.ziel.erlaeuterung':
    'Nothing is deleted without a target — an element without a home would silently lose its place.',
  'loeschen.zurueck': 'Cancel',
  'loeschen.wirdgeprueft': 'Checking where this element is used …',

  /* The time jump in the version series (R517) */
  'stammdaten.fassung.heute': 'Today',
  'stammdaten.fassung.heute.zusatz': 'the state in force',
  'stammdaten.fassung.danach': 'Later states',
  'stammdaten.fassung.ansehen': 'View the state of {zeit}',
  'stammdaten.blick.marke': 'Past state.',
  'stammdaten.blick.satz': 'This sheet stands as it did on {zeit}.',
  'stammdaten.blick.satz.ohne': 'This sheet stands as it did before its first change recorded here.',
  'stammdaten.blick.erlaeuterung':
    'Maintenance always happens at the current state, so nothing here can be changed. The series on the right stays complete — what came after this version is set apart there and remains clickable.',
  'stammdaten.blick.heute': 'Back to the current state',

  /* What stands differently today (R518) */
  'stammdaten.heute.wert': 'Today: {wert}',
  'stammdaten.blick.anders': 'Different today: {stellen}',
  'stammdaten.blick.gleich': 'Unchanged in substance up to today.',

  /* The latest version is the current state (R522) */
  'stammdaten.fassung.heute.name': 'Current state — last changed on {zeit}',
}
